import { formatDate, formatDateTime } from '../lib/dateFormat';
import { applyPrintExtras, lineDescription } from './docExtras';
import {
  qtyWithUnit, lineUnit, fmtQty, categorySummaryThermalHTML, categorySummaryDotMatrixHTML,
} from './printUnits';
/**
 * PrintEngine v2 — Professional receipt/invoice generator
 * Generates print-ready HTML for thermal (58mm) and full-page (8.5×11) documents.
 * 
 * Document types: order_slip, trust_receipt, purchase_order, branch_transfer,
 *                 expense_voucher, return_slip, statement
 * 
 * Usage: PrintEngine.print({ type, data, format, businessInfo, docCode })
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Default fallback text — overridden per-company via /settings/business-info
const TAX_DISCLAIMER_DEFAULT = 'THIS DOCUMENT IS NOT VALID FOR CLAIMING INPUT TAX. THIS IS NOT AN OFFICIAL RECEIPT, PLEASE ASK FOR RECEIPT UPON PAYMENT.';

/** Get tax disclaimer: use company-configured value, fall back to hardcoded default */
function getTaxDisclaimer(biz) {
  return (biz && biz.tax_disclaimer) ? biz.tax_disclaimer : TAX_DISCLAIMER_DEFAULT;
}

// ═══════════════════════════════════════════════════════════════════════════
//  CONTEXT-SMART DOCUMENT FOOTERS
//  BIR principal docs (Sales/Charge Invoice) vs internal/supplementary docs.
//  Single source of truth so full-page, dot-matrix and 58mm all stay in sync.
// ═══════════════════════════════════════════════════════════════════════════

// First line = bold title; remaining lines = body. Verbatim per business owner.
const INTERNAL_FOOTERS = {
  order_slip: [
    'ORDER SLIP ONLY',
    'For order preparation and stock checking only.',
    'Not an Official Invoice. Not valid for claim of input tax.',
    'Sales Invoice will be issued upon order completion.',
    'For items not available, request a partial refund at the counter.',
  ],
  trust_receipt: [
    'TRUST RECEIPT ONLY',
    'Issued to acknowledge goods received on credit.',
    'Please ask for the corresponding Charge Invoice.',
    'Not an Invoice. Not valid for claim of input tax.',
  ],
  proof_of_payment: [
    'PROOF OF PAYMENT ONLY',
    'Issued as customer copy for payment record.',
    'Not an Official Receipt. Not an Invoice.',
    'Not valid for claim of input tax.',
  ],
  internal: [
    'INTERNAL DOCUMENT ONLY',
    'Classified for internal business use.',
    'Not valid for claim of input tax.',
  ],
  packing_slip: [
    'PACKING SLIP ONLY',
    'For loading, hauling and delivery checking only.',
    'Shows items and quantities — no prices.',
    'Not an Official Invoice. Not valid for claim of input tax.',
  ],
};

/** True once the org has both a TIN and a BIR Permit/ATP — i.e. actually registered. */
function isBirRegistered(biz) {
  return !!((biz.tin || '').trim() && (biz.bir_permit_number || biz.bir_atp_number || '').trim());
}

/** Build the BIR principal-doc footer body lines + temporary-registration banner. */
function birFooterParts(biz, docLabel) {
  const name = biz.business_name_bir || biz.business_name || 'AgriBooks';
  const tin = (biz.tin || '').trim();
  const addr = biz.bir_address || biz.address || '';
  const vat = (biz.vat_type === 'vat_registered') ? 'VAT REGISTERED' : 'NON-VAT REGISTERED';
  const permit = (biz.bir_permit_number || biz.bir_atp_number || '').trim();
  const serial = (biz.bir_machine_serial || biz.machine_serial || '').trim();
  const registered = isBirRegistered(biz);
  const body = [];
  const head = [name, tin ? `TIN: ${tin}` : '', addr].filter(Boolean).join('  ·  ');
  if (head) body.push(head);
  body.push([vat, permit ? `Permit/ATP No.: ${permit}` : '', serial ? `Serial: ${serial}` : ''].filter(Boolean).join('   ·   '));
  body.push(`This ${docLabel} shall be valid for five (5) years from the date of the Permit to Use.`);
  body.push('Issued via AgriBooks POS (BIR-Accredited Software).');
  const banner = registered ? '' : 'TEMPORARY DOCUMENT — NOT YET BIR REGISTERED. This number is a system-generated temporary reference pending BIR registration.';
  return { body, banner };
}

/** Render a footer block in the format-appropriate wrapper. */
function renderFooterBlock({ format, titleLine, bodyLines, banner, isBir }) {
  const lines = (bodyLines || []).filter(Boolean);
  if (format === 'thermal') {
    let h = '<div class="footer">';
    if (banner) h += `<div style="font-weight:700;color:#000;margin-bottom:2px">${banner}</div>`;
    if (titleLine) h += `<div style="font-weight:700;margin-bottom:2px">${titleLine}</div>`;
    h += lines.map(l => `<div style="line-height:1.35">${l}</div>`).join('');
    h += '</div>';
    return h;
  }
  if (format === 'dot_matrix') {
    let h = '<div class="dm-footer">';
    if (banner) h += `<div class="dm-thankyou" style="font-size:11px;color:#000">${banner}</div>`;
    if (titleLine) h += `<div class="dm-thankyou">${titleLine}</div>`;
    h += `<div class="dm-disclaimer">${lines.join('<br>')}</div>`;
    h += '</div>';
    return h;
  }
  // full_page
  let h = '<div class="page-footer">';
  if (banner) h += `<div style="font-size:10px;font-weight:800;color:#b91c1c;margin-bottom:5px;letter-spacing:0.3px;line-height:1.4">${banner}</div>`;
  if (titleLine) h += `<div style="font-size:12px;font-weight:800;color:#1A4D2E;margin-bottom:3px;letter-spacing:0.5px">${titleLine}</div>`;
  h += `<div style="font-size:9px;color:#555;line-height:1.55">${lines.join('<br>')}</div>`;
  h += '</div>';
  return h;
}

/**
 * Context-smart document footer.
 * @param docKind 'sales_invoice' | 'charge_invoice' | 'order_slip' | 'trust_receipt' | 'proof_of_payment' | 'internal'
 * @param opts    { format: 'full_page'|'dot_matrix'|'thermal', isDraft?: boolean }
 */
function buildFooter(docKind, biz, opts = {}) {
  const format = opts.format || 'full_page';
  if (docKind === 'sales_invoice' || docKind === 'charge_invoice') {
    const docLabel = docKind === 'charge_invoice' ? 'Charge Invoice' : 'Sales Invoice';
    const { body, banner } = birFooterParts(biz, docLabel);
    return renderFooterBlock({ format, titleLine: '', bodyLines: body, banner, isBir: true });
  }
  const map = INTERNAL_FOOTERS[docKind] || INTERNAL_FOOTERS.internal;
  const banner = opts.isDraft ? 'DRAFT — not yet a valid transaction.' : '';
  return renderFooterBlock({ format, titleLine: map[0], bodyLines: map.slice(1), banner, isBir: false });
}


/** Get copy color guide (bottom of DM charge agreements): company-configured or default */
function getCopyColorGuide(biz) {
  if (biz && biz.copy_color_guide !== undefined) return biz.copy_color_guide;
  return 'Cash and Check \u2014 White and Pink  |  Credit \u2014 Pink  |  Store Copy \u2014 Yellow';
}

/** Get logo for printing: prefer cached base64 (works offline), fall back to URL.
 * Checks localStorage cache (keyed by org_id) so the logo survives page refreshes
 * and works fully offline after the first login. */
function getLogoSrc(biz) {
  if (!biz) return '';
  // A trustworthy logo source is either an image data URL or an http(s) URL.
  const isValid = (s) => typeof s === 'string' &&
    (/^data:image\/[a-z0-9.+-]+;base64,/i.test(s) || /^https?:\/\//i.test(s));
  // 1. Explicit base64 on the biz object (already fetched/cached)
  if (biz.logo_base64 && /^data:image\//i.test(biz.logo_base64)) return biz.logo_base64;
  // 2. localStorage cache keyed by org_id (written by AuthContext on login)
  try {
    const orgId = biz.organization_id || biz.org_id || '';
    const key = orgId ? `agribooks_logo_${orgId}` : '';
    const cached = key ? localStorage.getItem(key) : null;
    if (cached) {
      if (/^data:image\//i.test(cached)) return cached;
      // Corrupted/legacy cache value → drop it so it stops garbling prints and
      // gets re-cached cleanly on next login.
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
  } catch { /* localStorage unavailable (e.g. incognito strict mode) */ }
  // 3. Last resort: direct URL (requires network — may be a fresh presigned URL)
  return isValid(biz.logo_url) ? biz.logo_url : '';
}

function formatPHP(v) {
  const n = parseFloat(v) || 0;
  return `P${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Per-unit line discount label — every printed doc shows the discount PER UNIT
// (matching /sales-new and the invoice modals' "Disc/Unit" column), never the
// whole-line total. Amount discounts print "P20.00/unit", percent prints "5%".
// Legacy lines that stored only a total discount_amount (no per-unit
// discount_value) are converted back to per-unit (amount / qty). Returns '' when
// there is no discount. The NET line total already reflects the discount, so the
// whole-line effect stays visible via gross → total.
function perUnitDiscText(item) {
  const type = item.discount_type || 'amount';
  const qty = parseFloat(item.quantity || item.qty) || 0;
  const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
  let val = parseFloat(item.discount_value) || 0;
  const amt = parseFloat(item.discount_amount) || 0;
  if (type === 'percent') {
    if (val <= 0 && amt > 0 && qty > 0 && rate > 0) val = Math.round((amt / (qty * rate)) * 10000) / 100;
    return val > 0 ? `${val}%` : '';
  }
  if (val <= 0 && amt > 0 && qty > 0) val = Math.round((amt / qty) * 100) / 100;
  return val > 0 ? `${formatPHP(val)}/unit` : '';
}

// Senior Citizen / PWD discount rows (RA 9994 / RA 10754). `fmt` selects the
// markup style: 'fp' (full-page t-row), 'dm' (dot-matrix tr/td), 'th' (thermal row).
function scpwdRowsHTML(inv, fmt) {
  const sc = inv && inv.scpwd;
  if (!sc || !sc.enabled) return '';
  const pct = sc.discount_percent != null ? sc.discount_percent : 20;
  const add = (label, val) => {
    if (fmt === 'dm') return `<tr><td class="dm-tb-label">${label}</td><td class="dm-tb-val">${val}</td></tr>`;
    if (fmt === 'th') return `<div class="row"><span>${label}</span><span>${val}</span></div>`;
    return `<div class="t-row"><span>${label}</span><span>${val}</span></div>`;
  };
  let out = '';
  if (parseFloat(sc.vat_amount) > 0) out += add('Less: VAT', `-${formatPHP(sc.vat_amount)}`);
  out += add('VAT-Exempt Sales', formatPHP(sc.vat_exempt_sales));
  out += add(`Less: ${pct}% SC/PWD Disc`, `-${formatPHP(sc.discount_amount)}`);
  return out;
}

// Cardholder signature/identity block required on SC/PWD receipts.
function scpwdCardHTML(inv, fmt) {
  const sc = inv && inv.scpwd;
  if (!sc || !sc.enabled) return '';
  const typeLabel = sc.type === 'pwd' ? 'PWD' : 'Senior Citizen';
  const line1 = `${typeLabel}: ${sc.card_name || ''}`;
  const line2 = `${sc.type === 'pwd' ? 'PWD ID' : 'OSCA/SC ID'}: ${sc.card_id || ''}`;
  if (fmt === 'th') {
    return `<div style="border-top:1px dashed #000;margin-top:4px;padding-top:4px;font-size:10px"><div>${line1}</div><div>${line2}</div><div style="margin-top:8px">Signature: ____________</div></div>`;
  }
  if (fmt === 'dm') {
    return `<div style="margin-top:6px;font-size:11px;border-top:1px dotted #000;padding-top:4px"><div>${line1}</div><div>${line2}</div><div style="margin-top:14px">Cardholder Signature: ________________________</div></div>`;
  }
  return `<div style="margin-top:10px;font-size:11px;border:1px solid #999;border-radius:6px;padding:8px"><div style="font-weight:600;color:#1A4D2E;margin-bottom:2px">Senior Citizen / PWD Discount (RA 9994 / RA 10754)</div><div>${line1}</div><div>${line2}</div><div style="margin-top:18px">Cardholder Signature: ________________________</div></div>`;
}


// ── REPRINT banner — ONLY on the official BIR invoice (SI/CI), never on
// internal Order Slips. Copy #1 is the original; copy #2+ is a reprint and
// must be visibly marked so two look-alikes can't both pass as originals.
function reprintBannerHTML(type, copyNumber, format) {
  if (type !== 'official_receipt' || !copyNumber || copyNumber < 2) return '';
  const label = `REPRINTED COPY #${copyNumber}`;
  if (format === 'thermal') {
    return `<div style="text-align:center;border:2px solid #000;padding:3px 2px;margin:0 0 6px;font-weight:800;font-size:13px;letter-spacing:1px">*** ${label} ***</div>`;
  }
  if (format === 'dot_matrix') {
    return `<div style="text-align:center;border:2px solid #000;padding:4px;margin:0 0 8px;font-weight:800;font-size:13px;letter-spacing:2px">${label}</div>`;
  }
  return `<div style="text-align:center;border:2px dashed #b91c1c;color:#b91c1c;padding:8px;margin:0 0 12px;font-weight:800;font-size:15px;letter-spacing:2px;border-radius:6px">${label}<div style="font-size:9px;font-weight:600;letter-spacing:0.3px;margin-top:2px">Not the original — issued for reference only.</div></div>`;
}


function fmtDate(d) {
  if (!d) return '';
  try { return formatDate(d); }
  catch { return d; }
}

function fmtDateTime(d) {
  if (!d) return '';
  try { return formatDateTime(d); }
  catch { return d; }
}

// Shows date + time only when the string has a time component (contains 'T').
// Date-only strings (e.g. "2026-04-06") show date only — avoids UTC-midnight-to-PHT-8AM bug.
function fmtDateMaybeTime(d) {
  if (!d) return '';
  if (typeof d === 'string' && d.includes('T')) return fmtDateTime(d);
  return fmtDate(d);
}

// ── QR Code helper (uses public API for print-friendly inline image) ────────
function qrImgTag(code, size = 100) {
  if (!code) return '';
  const url = `${window.location.origin}/doc/${code}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}&margin=2`;
  return `
    <div class="qr-block">
      <img src="${qrUrl}" alt="QR" width="${size}" height="${size}" />
      <div class="qr-code-text">${code}</div>
      <div class="qr-hint">Scan to view document</div>
    </div>`;
}

// ── Thermal Receipt CSS (58mm / ~384px — use full WebView width; printer still has ~1–2mm dead zone) ──
// KEY: use width:100% not width:384px — on high-DPI Android the CSS viewport is smaller than 384px
// so a fixed 384px body overflows and gets clipped. 100% fills exactly the WebView's CSS viewport.
//
// VIEWPORT CONTRACT (terminal APK): the H10P native path captures thermal HTML as a
// 384-CSS-px-wide bitmap. EVERY thermal document shell must include this meta or a
// high-DPI WebView lays out ~2-3x wide and the downscale blurs the text. Injected in
// ALL THREE assembly sites: generateHtml(), print(), and statementThermal()'s wrapper.
const THERMAL_VIEWPORT_META =
  '<meta name="viewport" content="width=384, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">';

// FONT (thermal clarity, Jul 2026): 'Courier New' does NOT exist on Android — the
// WebView aliases it to Cutive Mono, a hairline typewriter font whose thin strokes
// crumble when the 203dpi head thresholds them to 1-bit (this was the "Courier bold
// ghosts/bleeds" problem). Stack order: Consolas (Windows browser prints — sturdy),
// Roboto Mono / Droid Sans Mono (modern Android), then generic monospace (Android
// default = Droid Sans Mono). All have uniform medium strokes that print dark.
const THERMAL_FONT_STACK = "'Consolas', 'Roboto Mono', 'Droid Sans Mono', monospace";

// ── DESKTOP RASTER SWITCH + GEOMETRY (2026-07-15) ────────────────────────
// Desktop 58mm prints go out as a BITMAP of the same calibrated template the
// H10P prints (thickness parity). Two per-device localStorage controls, same
// pattern as the agribooks_textmode kill switch — no redeploy needed:
//
//   localStorage.setItem('agribooks_desktop_raster', 'off')
//     → INSTANT ROLLBACK to the pre-raster vector popup on this device.
//
//   localStorage.setItem('agribooks_desktop_paper_width_mm', '80')
//     → the desktop printer's roll width. The receipt is laid out at that
//       physical width with the TERMINAL'S FONT SIZES printed at true CSS
//       size (11px ≈ 2.9mm — what the pre-raster vector prints showed).
//       Fonts do NOT scale with the paper; only line width / wrapping /
//       arrangement adapt (owner model, 2026-07-16). Default 80 (this
//       station's printer); clamped 40–100.
//
//   localStorage.setItem('agribooks_desktop_font_delta_px', '3')
//     → optional ±px nudge applied to EVERY font-size in the receipt
//       (desktop raster only — the H10P/native path never sees it).
//       Negative shrinks. Clamped −4…+8, floor 6px.
//       DEFAULT +2 (owner on-paper tuning 2026-07-16): terminal hierarchy
//       at true CSS size read slightly small on the 80mm roll.
function desktopRasterEnabled() {
  try { return localStorage.getItem('agribooks_desktop_raster') !== 'off'; }
  catch { return true; }
}
function desktopPaperWidthMm() {
  try {
    const v = parseFloat(localStorage.getItem('agribooks_desktop_paper_width_mm'));
    if (Number.isFinite(v)) return Math.max(40, Math.min(100, v));
  } catch { /* ignore */ }
  return 80;
}
function desktopFontDeltaPx() {
  try {
    const v = parseFloat(localStorage.getItem('agribooks_desktop_font_delta_px'));
    if (Number.isFinite(v)) return Math.max(-4, Math.min(8, Math.round(v)));
  } catch { /* ignore */ }
  return 2; // owner-tuned baseline (2026-07-16)
}
// Bump every font-size declaration (class rules AND inline styles) by the
// per-device delta. Runs on the assembled doc string so no tier or inline
// style can dodge it; unitless line-heights then scale spacing to match.
function applyDesktopFontDelta(html) {
  const d = desktopFontDeltaPx();
  if (!d) return html;
  return String(html).replace(/font-size:\s*(\d+(?:\.\d+)?)px/gi,
    (m, n) => `font-size:${Math.max(6, parseFloat(n) + d)}px`);
}

const thermalCSS = `
  @page { size: 58mm auto; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box;
      -webkit-font-smoothing: none;
      -moz-osx-font-smoothing: unset;
      text-rendering: optimizeSpeed; }
  html, body { width: 100%; max-width: 100%; overflow-x: hidden; }
  body { font-family: ${THERMAL_FONT_STACK}; font-size: 11px; font-weight: normal; line-height: 1.22; padding: 1px 3px; color: #000; word-wrap: break-word; overflow-wrap: anywhere; }
  .header { text-align: center; margin-bottom: 3px; }
  /* Thermal type HIERARCHY — COMPACT pass (Jul 2026, on-paper feedback: the
     first hierarchy printed 0.8m for a 14-line sale). Sizes/spacing tightened
     ~20-30% for roll economy while keeping the tier structure. Floor 9px:
       Tier A identity:   biz-name 14 bold
       Tier B money/doc:  GRAND TOTAL 15 bold · doc-title 12 bold
       Tier C content:    item names 12 · item/totals rows 11 · meta 10
       Tier D fine print: details/footer/terms/hints 9 */
  .header .biz-name { font-size: 14px; font-weight: bold; text-transform: uppercase; line-height: 1.15; }
  .header .biz-detail { font-size: 9px; font-weight: normal; line-height: 1.2; }
  .doc-title { text-align: center; font-size: 12px; font-weight: bold; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 2px 0; margin: 3px 0; letter-spacing: 0.5px; }
  .meta-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 4px; font-size: 10px; }
  .meta-row > span { min-width: 0; }
  .meta-row > span:last-child { text-align: right; }
  .meta-row .label { color: #000; flex-shrink: 0; }
  .sep { border-top: 1px solid #000; margin: 3px 0; }
  .items-table { width: 100%; table-layout: fixed; font-size: 11px; border-collapse: collapse; }
  .items-table td { padding: 1px 0; vertical-align: top; word-break: break-word; }
  .items-table .item-name { font-weight: normal; font-size: 12px; line-height: 1.25; padding-top: 3px; }
  /* Visually separate one item from the next so wrapped product names
     don't appear to bleed into the row below. The first item row keeps
     no top border so the items table flows cleanly from the heading
     above it. */
  .items-table tr + tr .item-name { padding-top: 3px; border-top: 1px dashed #000; margin-top: 1px; }
  .items-table .item-detail { width: 58%; padding-left: 4px; font-size: 11px; color: #000; }
  /* The line's description. Indented under the name, one step down in size —
     never below 10px, thermal heads smear anything smaller. Solid black: grey
     does not exist on a thermal head, it only comes out patchy. */
  .items-table .item-desc { padding-left: 4px; font-size: 10px; line-height: 1.2; color: #000; }
  /* The document's own note. Boxed so it reads as a remark, not as an item. */
  .note-block { margin-top: 4px; padding: 3px 4px; border: 1px dashed #000; font-size: 10px; line-height: 1.25; }
  .note-block .note-label { font-weight: bold; }
  .items-table .item-total { width: 42%; text-align: right; font-weight: normal; font-size: 11px; white-space: nowrap; }
  .items-table .item-discount { padding-left: 4px; font-size: 11px; color: #000; font-style: italic; }
  .items-table .item-disc-val { text-align: right; font-size: 11px; color: #000; white-space: nowrap; }
  .totals { margin-top: 3px; }
  .totals .row { display: flex; justify-content: space-between; gap: 4px; font-size: 11px; padding: 1px 0; }
  .totals .row > span:last-child { text-align: right; white-space: nowrap; }
  .totals .grand { font-size: 15px; font-weight: bold; border-top: 2px solid #000; padding-top: 3px; margin-top: 2px; }
  .payment-info { margin-top: 3px; font-size: 10px; }
  .trust-terms { margin-top: 4px; font-size: 9px; line-height: 1.2; border-top: 1px solid #000; padding-top: 3px; word-break: break-word; }
  .trust-terms .terms-title { font-weight: bold; font-size: 10px; margin-bottom: 1px; text-align: center; }
  .signature-line { margin-top: 8px; text-align: center; }
  .signature-line .line { border-top: 1px solid #000; width: 70%; margin: 0 auto; }
  .signature-line .sig-label { font-size: 9px; color: #000; margin-top: 1px; }
  .footer { text-align: center; font-size: 9px; color: #000; margin-top: 4px; border-top: 1px solid #000; padding-top: 3px; line-height: 1.2; }
  .qr-block { text-align: center; margin: 4px 0 2px; }
  .qr-block img { display: block; margin: 0 auto; max-width: 100%; height: auto; image-rendering: pixelated; }
  .qr-code-text { font-size: 11px; font-weight: bold; letter-spacing: 1px; margin-top: 1px; word-break: break-all; }
  .qr-hint { font-size: 9px; color: #000; }
  @media print { body { width: 100%; } }
`;

// ── Full Page CSS (Professional Template) ───────────────────────────────────
const fullPageCSS = `
  @page { size: 8.5in 11in; margin: 0.6in 0.7in; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; font-size: 11px; line-height: 1.5; color: #222; }

  /* Header: company left, doc info right */
  .page-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 14px; border-bottom: 3px solid #1A4D2E; margin-bottom: 18px; }
  .page-header .company { }
  .page-header .company .biz-name { font-size: 24px; font-weight: 800; color: #1A4D2E; letter-spacing: -0.5px; line-height: 1.1; }
  .page-header .company .biz-detail { font-size: 10px; color: #666; margin-top: 2px; }
  .page-header .doc-meta { text-align: right; }
  .page-header .doc-meta .doc-type { font-size: 13px; font-weight: 700; color: #1A4D2E; text-transform: uppercase; letter-spacing: 2px; }
  .page-header .doc-meta .doc-number { font-size: 18px; font-weight: 800; color: #222; margin-top: 2px; }
  .page-header .doc-meta .doc-date { font-size: 10px; color: #888; margin-top: 2px; }

  /* Info boxes */
  .info-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
  .info-box { background: #f8faf8; border: 1px solid #e2e8e2; border-radius: 6px; padding: 12px 14px; }
  .info-box .box-label { font-size: 9px; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
  .info-box .box-value { font-size: 14px; font-weight: 700; color: #1A4D2E; }
  .info-box .box-sub { font-size: 10px; color: #666; margin-top: 2px; }

  /* Meta grid */
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; margin-bottom: 18px; font-size: 11px; }
  .meta-grid .m-label { color: #888; font-size: 10px; }
  .meta-grid .m-value { font-weight: 600; color: #333; }

  /* Items table */
  .items-table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  .items-table thead th { background: #1A4D2E; color: #fff; padding: 8px 10px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; text-align: left; }
  .items-table thead th.r { text-align: right; }
  .items-table thead th.c { text-align: center; }
  .items-table tbody td { padding: 7px 10px; font-size: 11px; border-bottom: 1px solid #eee; }
  .items-table tbody tr:nth-child(even) { background: #fafcfa; }
  .items-table tbody td.r { text-align: right; font-family: 'Courier New', monospace; }
  .items-table tbody td.c { text-align: center; }
  .items-table tbody td .item-sub { font-size: 9px; color: #999; font-family: monospace; }

  /* Totals */
  .totals-area { display: flex; justify-content: flex-end; margin-bottom: 20px; }
  .totals-box { width: 260px; border: 1px solid #e2e8e2; border-radius: 6px; overflow: hidden; }
  .totals-box .t-row { display: flex; justify-content: space-between; padding: 6px 12px; font-size: 11px; border-bottom: 1px solid #f0f0f0; }
  .totals-box .t-row:last-child { border-bottom: none; }
  .totals-box .t-grand { background: #1A4D2E; color: #fff; font-weight: 700; font-size: 13px; padding: 8px 12px; }
  .totals-box .t-highlight { background: #e8f5e9; color: #1A4D2E; font-weight: 600; }

  /* Notes */
  .notes-box { font-size: 10px; color: #555; margin-bottom: 16px; padding: 8px 12px; background: #fffde7; border: 1px solid #fff3cd; border-radius: 4px; }

  /* Signatures */
  .sig-row { display: flex; justify-content: space-between; margin-top: 48px; gap: 24px; }
  .sig-block { text-align: center; flex: 1; }
  .sig-block .sig-line { border-bottom: 1px solid #333; margin-bottom: 4px; height: 28px; }
  .sig-block .sig-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }

  /* QR */
  .qr-block { text-align: center; }
  .qr-block img { display: block; margin: 0 auto; }
  .qr-code-text { font-size: 12px; font-weight: 700; letter-spacing: 3px; margin-top: 4px; color: #333; }
  .qr-hint { font-size: 8px; color: #999; }

  /* Footer */
  .page-footer { text-align: center; font-size: 8px; color: #aaa; margin-top: 24px; padding-top: 10px; border-top: 1px solid #eee; }
  .page-footer .thank-you { font-size: 12px; font-weight: 600; color: #1A4D2E; margin-bottom: 4px; }

  /* Payment info */
  .payment-box { font-size: 10px; padding: 8px 12px; background: #f9f9f9; border: 1px solid #eee; border-radius: 4px; margin-bottom: 12px; }

  @media print { body { max-width: none; } }
`;

// ── Dot Matrix CSS (8.5×11 — Epson LX-310 optimised) ────────────────────────
// Typography (May 2026 overhaul): Century Gothic geometric sans-serif with a
// deliberate weight-based hierarchy (bold for titles/values/labels, regular
// for data/body, no italics — Century Gothic obliques render poorly on most
// drivers). Letter-spacing removed throughout: Century Gothic ships with
// natural open tracking, additional CSS spacing distorts it.
//
// REVERT: a single `git checkout HEAD~1 -- frontend/src/lib/PrintEngine.js`
// restores the previous Courier New variant. No template HTML or backend
// changes are involved, so rollback is safe and instant.
const DM_FONT_STACK = `'Century Gothic', 'CenturyGothic', 'AppleGothic', 'URW Gothic', 'Futura', 'Trebuchet MS', 'Avenir Next', sans-serif`;
// Owner decision (Jul 2026): desk/office prints standardize on the dot-matrix
// layout; browser full_page requests are remapped in print(). Single flag so
// the migration is revertable in one line.
const PREFER_DOT_MATRIX_OVER_FULL_PAGE = true;

const dotMatrixCSS = `
  @page {
    size: 8.5in 11in;
    margin: 0.35in 0.6in 0.5in 0.6in;
  }
  /* NOTE: @page margin boxes (@bottom-center page counters) are NOT supported
     by Chrome/Blink and were silently dropped — per-page grounding now lives
     in each template's repeating <tfoot> (doc no + printed-at stamp). */
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: ${DM_FONT_STACK};
    font-size: 11px; line-height: 1.4; color: #000; background: #fff;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
    -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;
  }

  /* ── Page-frame: thead repeats letterhead on every printed page,
        tfoot reserves space for the receipt-# fallback footer. ── */
  .dm-page-frame { width: 100%; border-collapse: collapse; }
  .dm-page-frame > thead { display: table-header-group; }
  .dm-page-frame > tfoot { display: table-footer-group; }
  .dm-page-frame > thead > tr > td,
  .dm-page-frame > tbody > tr > td,
  .dm-page-frame > tfoot > tr > td { padding: 0; border: 0; }

  /* ── Letterhead repeated on every page ── */
  .dm-letterhead {
    width: 100%; border-collapse: collapse;
    padding-bottom: 6px; margin-bottom: 4px;
  }
  .dm-letterhead td { vertical-align: top; padding: 0; }
  .dm-lh-biz { padding-left: 8px; }
  .dm-lh-biz .dm-biz-name-lh {
    font-size: 17px; font-weight: 700; text-transform: uppercase;
    line-height: 1.15; margin-bottom: 2px;
  }
  .dm-lh-biz .dm-biz-detail-lh { font-size: 10.5px; font-weight: 400; line-height: 1.45; }
  .dm-lh-biz .dm-biz-detail-lh .dm-lbl {
    display: inline-block; min-width: 42px; font-weight: 700;
  }
  .dm-lh-title-cell { text-align: right; padding-right: 0; vertical-align: top; }
  .dm-lh-title {
    font-size: 26px; font-weight: 700; text-transform: uppercase;
    line-height: 1.05; margin-bottom: 6px;
  }
  .dm-lh-invbox {
    display: inline-block; border: 1px solid #000; padding: 4px 8px;
    font-size: 12px; line-height: 1.5; text-align: left; min-width: 210px;
  }
  .dm-lh-invbox table { border-collapse: collapse; }
  .dm-lh-invbox td { padding: 1px 6px 1px 0; font-weight: 400; }
  .dm-lh-invbox .dm-inv-val {
    font-size: 14px; font-weight: 700; text-align: right; padding-left: 14px;
  }

  /* ── Billing / Shipping band (first page only) ── */
  .dm-addr-band { width: 100%; border-collapse: collapse; margin: 6px 0 4px 0; }
  .dm-addr-band td { vertical-align: top; padding: 2px 4px; font-size: 10.5px; }
  .dm-addr-band .dm-addr-label { font-weight: 700; width: 95px; text-transform: uppercase; font-size: 10px; }
  /* Customer name — prominent for at-a-glance scanning */
  .dm-addr-band .dm-addr-val { font-weight: 700; font-size: 15px; line-height: 1.2; }

  /* ── Sales-rep / Payment terms grid (first page only) ── */
  .dm-meta-grid { width: 100%; border-collapse: collapse; margin: 4px 0 8px 0; }
  .dm-meta-grid th, .dm-meta-grid td {
    border: 1px solid #000; padding: 3px 7px; font-size: 11px; vertical-align: top;
  }
  .dm-meta-grid th {
    background: #f0f0f0; font-weight: 700; text-align: left;
    font-size: 10px; text-transform: uppercase;
  }
  .dm-meta-grid td { font-weight: 400; }

  /* ── Right-aligned totals box ── */
  .dm-total-box {
    margin: 8px 0 6px auto;
    width: 290px; border-collapse: collapse;
  }
  .dm-total-box td { padding: 3px 10px; font-size: 13px; vertical-align: middle; }
  .dm-total-box td.dm-tb-label { text-align: left; font-weight: 700; }
  .dm-total-box td.dm-tb-val {
    text-align: right; border: 1px solid #000; min-width: 120px; font-weight: 400;
  }
  .dm-total-box tr.dm-tb-grand td.dm-tb-label {
    font-size: 15px; font-weight: 700; text-transform: uppercase;
  }
  .dm-total-box tr.dm-tb-grand td.dm-tb-val { font-size: 15px; font-weight: 700; }

  /* ── Centered AUTHORIZED REPRESENTATIVE signature line ── */
  .dm-auth-sig {
    text-align: center; margin: 14px auto 4px auto;
    page-break-inside: avoid;
  }
  .dm-auth-sig .dm-sig-line-c {
    border-bottom: 1px solid #000; width: 280px; height: 18px; margin: 0 auto;
  }
  .dm-auth-sig .dm-sig-label-c {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    margin-top: 3px;
  }

  /* ── Terms block (charge agreement style) ── */
  .dm-terms-block {
    margin-top: 10px; font-size: 10px; font-weight: 400; line-height: 1.45;
    text-align: center;
    page-break-inside: avoid;
  }
  .dm-terms-block .dm-terms-head {
    font-weight: 700; text-transform: uppercase;
    margin-bottom: 3px; font-size: 11px;
  }
  .dm-terms-block .dm-terms-copy-key {
    font-size: 10px; font-weight: 700;
    margin-top: 4px; text-transform: uppercase;
  }

  /* ── Receipt-# fallback footer ── */
  .dm-foot-receipt {
    text-align: right; font-size: 9.5px; padding: 3px 0;
    border-top: 1px dashed #000;
  }

  /* ── Company header (legacy — order slip uses this) ── */
  .dm-header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 6px; }
  .dm-biz-name {
    font-size: 18px; font-weight: 700; text-transform: uppercase; line-height: 1.15;
  }
  .dm-biz-detail { font-size: 10.5px; font-weight: 400; line-height: 1.45; }

  /* ── Document title banner ── */
  .dm-doc-title {
    text-align: center; font-size: 16px; font-weight: 700;
    text-transform: uppercase;
    border-top: 1px solid #000; border-bottom: 1px solid #000;
    padding: 5px 0; margin: 6px 0;
  }

  /* ── Meta grid (two-column key-value pairs) ── */
  .dm-meta-table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
  .dm-meta-table td { padding: 2px 4px; font-size: 11px; font-weight: 400; vertical-align: top; }
  .dm-meta-table .dm-label { font-weight: 700; white-space: nowrap; width: 1%; padding-right: 8px; text-transform: uppercase; font-size: 10px; }

  /* ── Customer / info box ── */
  .dm-info-box {
    border: 1px solid #000; padding: 6px 10px; margin: 6px 0;
  }
  .dm-info-box .dm-box-label {
    font-size: 9.5px; font-weight: 700; text-transform: uppercase;
    margin-bottom: 3px; border-bottom: 1px solid #000; padding-bottom: 2px;
  }
  .dm-info-box .dm-box-name { font-size: 16px; font-weight: 700; line-height: 1.2; }
  .dm-info-box .dm-box-sub { font-size: 10.5px; font-weight: 400; line-height: 1.4; }

  /* ── Warning banner ── */
  .dm-warning {
    border: 2px solid #000; padding: 6px; margin: 6px 0;
    text-align: center; font-weight: 700; font-size: 11px; text-transform: uppercase;
  }

  /* ── Items table — breathing-room layout (target: 10-15 lines on page 1)
        Hierarchy: header (10.5px bold uppercase) is smaller than data
        (12px regular). This makes the data scannable first — the way a
        cashier or customer reads the receipt. ── */
  .dm-items-table {
    width: 100%; border-collapse: collapse; margin: 6px 0;
    table-layout: fixed;
  }
  .dm-items-table thead tr { border-bottom: 1.5px solid #000; }
  .dm-items-table th {
    border: 1px solid #000; padding: 4px 7px;
    font-size: 10.5px; font-weight: 700; text-transform: uppercase;
    text-align: left; background: #fff; line-height: 1.2;
  }
  .dm-items-table th.r { text-align: right; }
  .dm-items-table th.c { text-align: center; }
  .dm-items-table td {
    border: 1px solid #000; padding: 4px 7px;
    font-size: 12px; font-weight: 400; vertical-align: top;
    word-break: break-word; line-height: 1.35;
  }
  .dm-items-table td.r { text-align: right; }
  .dm-items-table td.c { text-align: center; }
  .dm-items-table td.strong { font-weight: 700; }
  /* Tick-box cell — narrow, just enough for a pen mark */
  .dm-items-table .dm-row-num {
    width: 0.33cm; padding: 2px 0; text-align: center;
  }

  /* ── Totals block (legacy — used by sales receipts) ── */
  .dm-totals { margin-top: 6px; border-top: 1.5px solid #000; padding-top: 6px; }
  .dm-tot-row {
    display: flex; justify-content: flex-end; gap: 0;
    font-size: 13px; padding: 2px 0;
  }
  .dm-tot-row .dm-tot-label {
    min-width: 120px; text-align: right; font-weight: 700; padding-right: 14px;
  }
  .dm-tot-row .dm-tot-val { min-width: 110px; text-align: right; font-weight: 400; }
  .dm-tot-row.dm-grand {
    font-size: 15px; font-weight: 700;
    border-top: 1px solid #000; border-bottom: 1px solid #000;
    padding: 3px 0; margin: 3px 0; text-transform: uppercase;
  }
  .dm-tot-row.dm-grand .dm-tot-val { font-weight: 700; }

  /* ── QR section (legacy big — kept for order slip) ── */
  .dm-qr-section {
    display: flex; align-items: flex-start; gap: 16px;
    margin-top: 10px; border-top: 1px solid #000; padding-top: 8px;
  }
  .dm-qr-section img {
    display: block; width: 90px; height: 90px;
    image-rendering: pixelated; image-rendering: crisp-edges;
    flex-shrink: 0;
  }
  .dm-qr-info { font-size: 10.5px; font-weight: 400; line-height: 1.4; }
  .dm-qr-info .dm-qr-code { font-size: 12px; font-weight: 700; margin-bottom: 3px; }
  .dm-qr-info .dm-qr-scan { font-size: 10.5px; font-weight: 700; margin-bottom: 4px; }
  .dm-qr-info .dm-qr-sub { font-size: 10px; font-weight: 400; }

  /* ── Compact QR strip ── */
  .dm-qr-strip {
    display: flex; align-items: center; gap: 12px;
    margin-top: 8px; padding-top: 6px;
    border-top: 1px dashed #000;
    page-break-inside: avoid;
  }
  .dm-qr-strip img {
    width: 70px; height: 70px;
    image-rendering: pixelated; image-rendering: crisp-edges;
    flex-shrink: 0;
  }
  .dm-qr-strip .dm-qr-strip-info { font-size: 10.5px; font-weight: 400; line-height: 1.4; }
  .dm-qr-strip .dm-qr-code { font-size: 12px; font-weight: 700; }
  .dm-qr-strip .dm-qr-scan { font-size: 10.5px; font-weight: 700; }
  .dm-qr-strip .dm-qr-sub  { font-size: 10px; font-weight: 400; }

  /* ── Signature row ── */
  .dm-sig-row { display: flex; justify-content: space-between; gap: 24px; margin-top: 18px; }
  .dm-sig-block { flex: 1; text-align: center; }
  .dm-sig-line { border-bottom: 1px solid #000; height: 24px; margin-bottom: 3px; }
  .dm-sig-label { font-size: 10px; font-weight: 700; text-transform: uppercase; }

  /* ── Footer ── */
  .dm-footer {
    text-align: center; border-top: 1.5px solid #000;
    margin-top: 10px; padding-top: 8px;
  }
  .dm-footer .dm-thankyou { font-size: 13px; font-weight: 700; margin-bottom: 3px; text-transform: uppercase; }
  .dm-footer .dm-disclaimer { font-size: 9px; font-weight: 400; line-height: 1.45; }

  /* ── Terms / Note boxes ── */
  .dm-box { border: 1px solid #000; padding: 6px 10px; margin: 6px 0; font-size: 10.5px; font-weight: 400; line-height: 1.45; }
  .dm-box strong { font-size: 11px; font-weight: 700; text-transform: uppercase; }

  @media print {
    body { max-width: none; }
    .dm-items-table { page-break-inside: auto; }
    .dm-items-table tr { page-break-inside: avoid; }
  }
`;

// Per-print dot-matrix stamp — RETIRED as CSS: @page margin boxes are not
// supported by Chrome/Blink, so the @bottom-left "Printed:" stamp never
// rendered. The stamp now lives in each DM template's repeating <tfoot>
// (see the tfootHtml blocks). Kept as a no-op so the two assembly sites'
// `${dmExtras}` injection stays harmless.
function dotMatrixStampCSS(_printedAtStr) {
  return '';
}

function formatPrintedAt(d = new Date()) {
  try {
    return d.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return d.toISOString();
  }
}

// ── Build page header ───────────────────────────────────────────────────────
function buildPageHeader(biz, docType, docNumber, date, extraLines = []) {
  let companyHtml = `<div class="biz-name">${biz.business_name || 'AgriBooks'}</div>`;
  if (biz.address) companyHtml += `<div class="biz-detail">${biz.address}</div>`;
  if (biz.phone) companyHtml += `<div class="biz-detail">${biz.phone}</div>`;
  if (biz.tin) companyHtml += `<div class="biz-detail">TIN: ${biz.tin}</div>`;

  let metaHtml = `<div class="doc-type">${docType}</div>`;
  metaHtml += `<div class="doc-number">${docNumber}</div>`;
  // Iter 243.3: ONE date on the receipt — the actual transaction moment.
  // The Z-Report posting date is recoverable via the QR code / Audit Center,
  // so we keep the customer-facing receipt clean (matches BIR expectations
  // for a single OR issuance date).
  metaHtml += `<div class="doc-date">${fmtDate(date)}</div>`;
  for (const line of extraLines) {
    metaHtml += `<div class="doc-date">${line}</div>`;
  }

  return `<div class="page-header"><div class="company">${companyHtml}</div><div class="doc-meta">${metaHtml}</div></div>`;
}

function buildThermalHeader(biz) {
  const parts = [];
  parts.push(`<div class="biz-name">${biz.business_name || 'AgriBooks'}</div>`);
  if (biz.address) parts.push(`<div class="biz-detail">${biz.address}</div>`);
  if (biz.phone) parts.push(`<div class="biz-detail">${biz.phone}</div>`);
  if (biz.tin) parts.push(`<div class="biz-detail">TIN: ${biz.tin}</div>`);
  return `<div class="header">${parts.join('')}</div>`;
}

// ── Thermal item list ───────────────────────────────────────────────────────
function buildItemsThermal(items) {
  let html = '<table class="items-table">';
  for (const item of items) {
    const qty = parseFloat(item.quantity || item.qty) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price || item.transfer_capital) || 0;
    const discAmt = parseFloat(item.discount_amount) || 0;
    const grossLine = qty * rate;
    // Total: prefer stored line_total, else compute from gross − discount
    const total = (item.total !== undefined && item.total !== null && item.total !== '')
      ? parseFloat(item.total)
      : (grossLine - discAmt);
    html += `<tr><td class="item-name" colspan="2">${item.product_name || item.description || ''}</td></tr>`;
    // The line's own description — a second, smaller line under the name.
    // Thermal never printed it unless the product name was missing, so a
    // Farm Service receipt came off the printer saying only "Farm Service:".
    // Skipped when it IS the name (already printed above).
    const itemDesc = item.product_name ? lineDescription(item) : '';
    if (itemDesc) {
      html += `<tr><td class="item-desc" colspan="2">${itemDesc}</td></tr>`;
    }
    // "2 BAGS x P1,890.00" — the unit rides with the quantity (owner
    // 2026-09-29: only the packing slip used to say what the "2" was).
    const qtyText = qtyWithUnit(qty, item);
    if (discAmt > 0) {
      // Show pre-discount line, then the PER-UNIT discount, then the net line —
      // the discount is shown per unit (matching the entry screen), while the
      // net Line Total still reflects the whole-line effect.
      const pu = perUnitDiscText(item);
      html += `<tr><td class="item-detail">${qtyText} x ${formatPHP(rate)}</td><td class="item-total">${formatPHP(grossLine)}</td></tr>`;
      html += `<tr><td class="item-discount">Less Disc.</td><td class="item-disc-val">-${pu}</td></tr>`;
      html += `<tr><td class="item-detail" style="text-align:right">Line Total</td><td class="item-total">${formatPHP(total)}</td></tr>`;
    } else {
      html += `<tr><td class="item-detail">${qtyText} x ${formatPHP(rate)}</td><td class="item-total">${formatPHP(total)}</td></tr>`;
    }
  }
  html += '</table>';
  return html;
}

// ── The document's own note, per format ────────────────────────────────────
// Notes were bolted onto each document one at a time by whoever built it, so
// dot-matrix mostly got them and thermal almost never did (2026-09-23 sweep:
// only the stock request printed a note on thermal). These two renderers are
// the single spelling of "print the note", used by every family.
//
// An operator who unticked the note at the prompt never reaches here — the
// text was already removed from the document by applyPrintExtras().
function noteBlockThermal(doc, label = 'Note') {
  const note = (doc && doc.notes ? String(doc.notes) : '').trim();
  if (!note) return '';
  return `<div class="note-block"><span class="note-label">${label}:</span> ${note}</div>`;
}

function noteBlockDotMatrix(doc, label = 'Note') {
  const note = (doc && doc.notes ? String(doc.notes) : '').trim();
  if (!note) return '';
  return `<div class="dm-warning" style="text-align:left;font-weight:400;">${label}: ${note}</div>`;
}

// Add-on payments (amend collect-now) carry their own tendered/change — the
// invoice-level tendered_amount/change_amount stay frozen at checkout values,
// so each later cash hand-over prints as its own "Add-on Tendered / Change"
// pair instead of silently rewriting the original figures.
function addonTenderPairs(inv) {
  return ((inv && inv.payments) || [])
    .filter(p => p && parseFloat(p.tendered) > 0 && (parseFloat(p.amount) || 0) > 0)
    .map(p => ({ tendered: parseFloat(p.tendered) || 0, change: parseFloat(p.change) || 0 }));
}

// ═══════════════════════════════════════════════════════════════════════════
//  FULL PAGE DOCUMENTS
// ═══════════════════════════════════════════════════════════════════════════

// ── Order Slip (Sales) ──────────────────────────────────────────────────────
function orderSlipFullPage(data, biz, docCode) {
  const inv = data;
  const isDraft = inv.status === 'for_preparation';
  // Iter 252 — Linked Offline Draft Finalization.
  // When this is being printed for an offline draft completion (before sync)
  // OR a synced one (after sync), show both the OFF receipt number AND the
  // canonical draft invoice number on the printed copy so the customer's
  // paper can always be tied back to the official record.
  const offlineDraftPending = inv.kind === 'draft_finalization_offline' && !inv.synced_from_offline;
  const offlineDraftSynced = inv.linked_offline_receipt_number && inv.finalized_from_draft_offline;
  const linkedOff = inv.linked_offline_receipt_number || (offlineDraftPending ? (inv.offline_receipt_number || inv.invoice_number) : '');
  const linkedDraftNum = inv.original_draft_invoice_number || inv.draft_invoice_number || (offlineDraftPending ? inv.draft_invoice_number : '');

  let html = buildPageHeader(biz, 'Order Slip', inv.invoice_number || '', inv.invoice_date || inv.created_at || inv.order_date, [
    inv.cashier_name ? `Cashier: ${inv.cashier_name}` : '',
    inv.release_mode === 'full' ? 'Status: FULLY RELEASED' : inv.release_mode === 'partial' ? 'Status: PARTIAL RELEASE' : '',
  ].filter(Boolean));

  // FOR PREPARATION banner
  if (isDraft) {
    html += `<div style="background:#fef3c7;border:3px solid #f59e0b;border-radius:8px;padding:14px 18px;margin:0 0 18px;text-align:center">
      <div style="font-size:17px;font-weight:900;color:#92400e;letter-spacing:1.5px;margin-bottom:3px">FOR PREPARATION ONLY</div>
      <div style="font-size:11px;font-weight:700;color:#92400e;letter-spacing:0.5px">NOT YET PAID — NOT A FINAL RECEIPT</div>
    </div>`;
  }

  // OFFLINE RECEIPT — pending sync (draft finalized while offline)
  if (offlineDraftPending) {
    html += `<div style="background:#fef9c3;border:3px solid #ca8a04;border-radius:8px;padding:14px 18px;margin:0 0 18px;text-align:center" data-testid="offline-pending-banner">
      <div style="font-size:17px;font-weight:900;color:#854d0e;letter-spacing:1.5px;margin-bottom:3px">OFFLINE RECEIPT — PENDING SYNC</div>
      <div style="font-size:12px;font-weight:700;color:#854d0e;margin-top:6px">Receipt No.: <span style="font-family:monospace">${linkedOff || ''}</span></div>
      <div style="font-size:12px;font-weight:700;color:#854d0e">Linked Draft / Original Invoice: <span style="font-family:monospace">${linkedDraftNum || ''}</span></div>
      <div style="font-size:10px;font-weight:600;color:#854d0e;margin-top:5px">This receipt will be linked to ${linkedDraftNum} once synced. The official invoice number remains ${linkedDraftNum}.</div>
    </div>`;
  }

  // Synced offline draft completion banner
  if (offlineDraftSynced) {
    html += `<div style="background:#dcfce7;border:2px solid #16a34a;border-radius:8px;padding:10px 14px;margin:0 0 16px;text-align:center" data-testid="offline-synced-banner">
      <div style="font-size:13px;font-weight:800;color:#166534">SYNCED — OFFICIAL RECEIPT</div>
      <div style="font-size:11px;color:#166534;margin-top:3px">Invoice No.: <span style="font-family:monospace;font-weight:700">${inv.invoice_number}</span> · Linked Offline Receipt: <span style="font-family:monospace">${linkedOff}</span></div>
    </div>`;
  }

  // Customer info box (only if not walk-in)
  if (inv.customer_name && inv.customer_name !== 'Walk-in') {
    html += `<div class="info-row"><div class="info-box"><div class="box-label">Customer</div><div class="box-value">${inv.customer_name}</div>`;
    if (inv.customer_address) html += `<div class="box-sub">${inv.customer_address}</div>`;
    if (inv.customer_phone) html += `<div class="box-sub">${inv.customer_phone}</div>`;
    html += `</div><div class="info-box"><div class="box-label">Payment</div><div class="box-value">${inv.payment_method || 'Cash'}</div>`;
    if (inv.terms && inv.terms !== 'COD') html += `<div class="box-sub">Terms: ${inv.terms}</div>`;
    if (inv.due_date) html += `<div class="box-sub">Due: ${fmtDate(inv.due_date)}</div>`;
    if (inv.customer_po) html += `<div class="box-sub">Ref PO: ${inv.customer_po}</div>`;
    html += `</div></div>`;
  }

  // Partial release notice
  if (inv.release_mode === 'partial') {
    html += `<div style="background:#fef3c7;border:2px solid #f59e0b;border-radius:8px;padding:12px;margin:16px 0;text-align:center">`;
    html += `<p style="font-size:13px;font-weight:700;color:#92400e;margin:0">⚠ PARTIAL RELEASE — Items must be scanned via QR code for release</p>`;
    html += `<p style="font-size:11px;color:#78350f;margin:4px 0 0">Scan the QR code below to manage item releases.</p>`;
    html += `</div>`;
  }

  // Items table
  const items = inv.items || [];
  html += '<table class="items-table"><thead><tr>';
  const anyDisc = items.some(i => perUnitDiscText(i) !== '');
  html += '<th style="width:5%">#</th><th>Item</th><th class="c" style="width:10%">Qty</th><th class="r" style="width:15%">Price</th>';
  if (anyDisc) html += '<th class="r" style="width:12%">Disc/Unit</th>';
  html += '<th class="r" style="width:15%">Total</th>';
  html += '</tr></thead><tbody>';
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const disc = parseFloat(item.discount_amount) || 0;
    const total = parseFloat(item.total) || (qty * rate - disc);
    const unit = item.unit || item.uom || '';
    html += `<tr><td class="c">${i + 1}</td><td>${item.product_name || ''}</td><td class="c">${qty}${unit ? ' ' + unit : ''}</td><td class="r">${formatPHP(rate)}</td>`;
    if (anyDisc) { const pu = perUnitDiscText(item); html += `<td class="r">${pu ? '-' + pu : '-'}</td>`; }
    html += `<td class="r" style="font-weight:600">${formatPHP(total)}</td></tr>`;
  }
  html += '</tbody></table>';

  // Totals + QR side by side
  html += '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px">';
  html += qrImgTag(docCode, 100);
  html += '<div class="totals-box">';
  html += `<div class="t-row"><span>Subtotal</span><span>${formatPHP(inv.subtotal)}</span></div>`;
  if (inv.overall_discount > 0) html += `<div class="t-row"><span>Discount</span><span>-${formatPHP(inv.overall_discount)}</span></div>`;
  if (inv.freight > 0) html += `<div class="t-row"><span>Freight</span><span>${formatPHP(inv.freight)}</span></div>`;
  html += scpwdRowsHTML(inv, 'fp');
  html += `<div class="t-row t-grand"><span>TOTAL</span><span>${formatPHP(inv.grand_total)}</span></div>`;
  // Tendered / Change — only for cash & split-with-cash. Use stored fields if
  // present, otherwise fall back to legacy compute so old invoices still render.
  const _tendered_a = parseFloat(inv.tendered_amount) || 0;
  const _change_a = inv.change_amount != null ? parseFloat(inv.change_amount) || 0
    : Math.max(0, _tendered_a - (parseFloat(inv.grand_total) || 0));
  const _showCash_a = inv.payment_type !== 'credit' && _tendered_a > 0;
  if (_showCash_a) {
    html += `<div class="t-row"><span>Tendered</span><span>${formatPHP(_tendered_a)}</span></div>`;
    if (_change_a > 0) html += `<div class="t-row"><span>Change</span><span>${formatPHP(_change_a)}</span></div>`;
  } else if (inv.amount_paid > 0) {
    html += `<div class="t-row"><span>Amount Paid</span><span>${formatPHP(inv.amount_paid)}</span></div>`;
  }
  for (const ap of addonTenderPairs(inv)) {
    html += `<div class="t-row"><span>Add-on Tendered</span><span>${formatPHP(ap.tendered)}</span></div>`;
    if (ap.change > 0) html += `<div class="t-row"><span>Add-on Change</span><span>${formatPHP(ap.change)}</span></div>`;
  }
  const balance = (inv.grand_total || 0) - (inv.amount_paid || 0);
  if (balance > 0 && inv.payment_type === 'credit') html += `<div class="t-row" style="color:#c00;font-weight:600"><span>Balance Due</span><span>${formatPHP(balance)}</span></div>`;
  html += '</div></div>';
  html += scpwdCardHTML(inv, 'fp');

  // Acknowledgment + Signature block
  const today = fmtDate(new Date().toISOString());
  html += `<div style="margin-top:32px;padding-top:16px;border-top:1px solid #eee;">`;
  html += `<p style="font-size:10px;color:#444;margin-bottom:28px;line-height:1.5">I acknowledge receipt of the items listed above in good physical condition and complete.</p>`;
  html += `<div class="sig-row">`;
  html += `<div class="sig-block"><div class="sig-line"></div><div class="sig-label">Customer Signature</div></div>`;
  html += `<div class="sig-block"><div class="sig-line"></div><div class="sig-label">Printed Name</div></div>`;
  html += `<div class="sig-block"><div class="sig-line" style="display:flex;align-items:flex-end;justify-content:center;padding-bottom:2px;font-size:11px;color:#333">${today}</div><div class="sig-label">Date</div></div>`;
  html += `</div></div>`;

  // Footer
  if (isDraft) {
    html += `<div style="text-align:center;margin-top:16px"><div style="font-size:12px;font-weight:900;color:#92400e;letter-spacing:1px">FOR PREPARATION ONLY — NOT YET PAID</div></div>`;
  } else {
    html += `<div style="text-align:center;margin-top:16px"><div class="thank-you" style="font-size:12px;font-weight:600;color:#1A4D2E">Thank you for your business!</div></div>`;
  }
  html += buildFooter('order_slip', biz, { format: 'full_page', isDraft });
  return html;
}

// ── Charge Agreement (Credit / Partial Sales) ──────────────────────────────
function trustReceiptFullPage(data, biz, docCode) {
  const inv = data;
  let html = buildPageHeader(biz, 'Charge Agreement', inv.invoice_number || '', inv.invoice_date || inv.created_at || inv.order_date, [
    inv.terms && inv.terms !== 'COD' ? `Terms: ${inv.terms}` : '',
    inv.due_date ? `Due: ${fmtDate(inv.due_date)}` : '',
    inv.release_mode === 'full' ? 'Status: FULLY RELEASED' : inv.release_mode === 'partial' ? 'Status: PARTIAL RELEASE' : '',
  ].filter(Boolean));

  html += `<div class="info-row">`;
  html += `<div class="info-box"><div class="box-label">Customer</div><div class="box-value">${inv.customer_name || ''}</div>`;
  if (inv.customer_address) html += `<div class="box-sub">${inv.customer_address}</div>`;
  if (inv.customer_phone) html += `<div class="box-sub">${inv.customer_phone}</div>`;
  if (inv.customer_po) html += `<div class="box-sub">Ref PO: ${inv.customer_po}</div>`;
  html += `</div>`;
  html += `<div class="info-box"><div class="box-label">Cashier</div><div class="box-value">${inv.cashier_name || ''}</div></div>`;
  html += `</div>`;

  // Partial release notice
  if (inv.release_mode === 'partial') {
    html += `<div style="background:#fef3c7;border:2px solid #f59e0b;border-radius:8px;padding:12px;margin:16px 0;text-align:center">`;
    html += `<p style="font-size:13px;font-weight:700;color:#92400e;margin:0">⚠ PARTIAL RELEASE — Items must be scanned via QR code for release</p>`;
    html += `<p style="font-size:11px;color:#78350f;margin:4px 0 0">Scan the QR code below to manage item releases.</p>`;
    html += `</div>`;
  }

  // Items
  const items = inv.items || [];
  html += '<table class="items-table"><thead><tr>';
  html += '<th style="width:5%">#</th><th>Item</th><th class="c" style="width:10%">Qty</th><th class="r" style="width:15%">Price</th><th class="r" style="width:15%">Total</th>';
  html += '</tr></thead><tbody>';
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const total = parseFloat(item.total) || (qty * rate);
    const unit = item.unit || item.uom || '';
    html += `<tr><td class="c">${i + 1}</td><td>${item.product_name || ''}</td><td class="c">${qty}${unit ? ' ' + unit : ''}</td><td class="r">${formatPHP(rate)}</td><td class="r" style="font-weight:600">${formatPHP(total)}</td></tr>`;
  }
  html += '</tbody></table>';

  // Totals + QR
  html += '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px">';
  html += qrImgTag(docCode, 100);
  html += '<div class="totals-box">';
  html += `<div class="t-row"><span>Subtotal</span><span>${formatPHP(inv.subtotal)}</span></div>`;
  if (inv.overall_discount > 0) html += `<div class="t-row"><span>Discount</span><span>-${formatPHP(inv.overall_discount)}</span></div>`;
  html += `<div class="t-row t-grand"><span>TOTAL</span><span>${formatPHP(inv.grand_total)}</span></div>`;
  if (inv.amount_paid > 0) html += `<div class="t-row"><span>Paid</span><span>${formatPHP(inv.amount_paid)}</span></div>`;
  if (inv.balance > 0) html += `<div class="t-row" style="color:#c00;font-weight:700"><span>Balance Due</span><span>${formatPHP(inv.balance)}</span></div>`;
  html += '</div></div>';

  // Trust terms
  const terms = (biz.trust_receipt_terms || '').replace('{business_name}', biz.business_name || '');
  if (terms) {
    html += `<div style="margin:16px 0;font-size:9px;line-height:1.4;border:1px solid #ddd;padding:10px;border-radius:4px"><div style="font-weight:bold;font-size:10px;margin-bottom:4px;text-transform:uppercase">Terms and Conditions</div><p>${terms}</p></div>`;
  }

  // Disclaimer (context-smart internal footer)
  html += buildFooter('trust_receipt', biz, { format: 'full_page' });

  // Signatures — embed customer signature image if present, or "Manager Bypass" badge
  // Tamper-evident stamp below: signed-at + verification token (8-char HMAC)
  const sigStampFull = (() => {
    const t = data.signature_verification_token || '';
    const sa = data.signature_signed_at || '';
    if (!t && !sa) return '';
    const dt = sa ? new Date(sa) : null;
    const dtStr = dt ? dt.toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : '';
    return `<div style="text-align:center;font-size:8.5px;color:#666;margin-top:4px;letter-spacing:0.3px">Signed ${dtStr}${t ? ` &middot; v.${t}` : ''}</div>`;
  })();
  if (data.signature_url) {
    html += `<div class="sig-row"><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Authorized Representative</div></div><div class="sig-block"><img src="${data.signature_url}" alt="signature" style="max-width:80%;max-height:60px;display:block;margin:0 auto 2px;object-fit:contain"/><div class="sig-line"></div><div class="sig-label">Customer Signature</div>${sigStampFull}</div></div>`;
  } else if (data.bypass_method) {
    html += `<div class="sig-row"><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Authorized Representative</div></div><div class="sig-block"><div style="margin:8px auto;font-size:10px;border:1px solid #555;padding:4px 8px;display:inline-block;color:#555">AUTHORIZED VIA MANAGER PIN</div><div class="sig-label">Customer (bypassed)</div>${sigStampFull}</div></div>`;
  } else {
    html += '<div class="sig-row"><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Authorized Representative</div></div><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Customer Signature &amp; Printed Name</div></div></div>';
  }
  return html;
}

// ── Purchase Order ──────────────────────────────────────────────────────────
function purchaseOrderFullPage(data, biz, docCode) {
  const po = data;
  let html = buildPageHeader(biz, 'Purchase Order', po.po_number || '', po.purchase_date, [
    `Status: ${(po.status || '').toUpperCase()}`,
  ]);

  html += `<div class="info-row">`;
  html += `<div class="info-box"><div class="box-label">Supplier</div><div class="box-value">${po.vendor || ''}</div>`;
  if (po.dr_number) html += `<div class="box-sub">DR #: ${po.dr_number}</div>`;
  html += `</div>`;
  html += `<div class="info-box"><div class="box-label">Payment</div><div class="box-value">${po.po_type === 'cash' ? 'Cash' : po.terms_label || 'Terms'}</div>`;
  html += `<div class="box-sub">${po.payment_status || 'Unpaid'}</div>`;
  if (po.due_date) html += `<div class="box-sub">Due: ${fmtDate(po.due_date)}</div>`;
  html += `</div></div>`;

  if (po.notes) html += `<div class="notes-box"><strong>Notes:</strong> ${po.notes}</div>`;

  // Items
  const items = po.items || [];
  html += '<table class="items-table"><thead><tr>';
  html += '<th style="width:5%">#</th><th>Item</th><th class="c" style="width:10%">Qty</th><th class="r" style="width:15%">Unit Cost</th><th class="r" style="width:15%">Total</th>';
  html += '</tr></thead><tbody>';
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const total = parseFloat(item.total) || (qty * rate);
    const unit = item.unit || item.uom || '';
    html += `<tr><td class="c">${i + 1}</td><td>${item.product_name || item.description || ''}</td><td class="c">${qty}${unit ? ' ' + unit : ''}</td><td class="r">${formatPHP(rate)}</td><td class="r" style="font-weight:600">${formatPHP(total)}</td></tr>`;
  }
  html += '</tbody></table>';

  // Totals + QR
  html += '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px">';
  html += qrImgTag(docCode, 100);
  html += '<div class="totals-box">';
  html += `<div class="t-row"><span>Subtotal</span><span>${formatPHP(po.subtotal || po.line_subtotal)}</span></div>`;
  if (po.overall_discount_amount > 0) html += `<div class="t-row"><span>Discount</span><span>-${formatPHP(po.overall_discount_amount)}</span></div>`;
  if (po.freight > 0) html += `<div class="t-row"><span>Freight</span><span>${formatPHP(po.freight)}</span></div>`;
  if (po.tax_amount > 0) html += `<div class="t-row"><span>VAT (${po.tax_rate}%)</span><span>${formatPHP(po.tax_amount)}</span></div>`;
  html += `<div class="t-row t-grand"><span>Grand Total</span><span>${formatPHP(po.grand_total)}</span></div>`;
  if (po.balance > 0) html += `<div class="t-row" style="color:#c00;font-weight:600"><span>Balance</span><span>${formatPHP(po.balance)}</span></div>`;
  html += '</div></div>';

  // Signatures
  html += '<div class="sig-row"><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Prepared By</div></div><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Received By</div></div></div>';
  html += `<div style="text-align:center;margin-top:16px"><div class="thank-you" style="font-size:12px;font-weight:600;color:#1A4D2E">Thank you!</div></div>`;
  html += buildFooter('internal', biz, { format: 'full_page' });
  return html;
}

// ── Branch Transfer Invoice ─────────────────────────────────────────────────
function branchTransferFullPage(data, biz, docCode) {
  const t = data;
  const invoiceNo = t.invoice_number || t.order_number || '';
  let html = buildPageHeader(biz, 'Branch Transfer', invoiceNo, t.created_at, [
    t.order_number !== invoiceNo ? `Transfer: ${t.order_number}` : '',
    t.request_po_number ? `Request: ${t.request_po_number}` : '',
    `Status: ${(t.status || '').toUpperCase()}`,
  ].filter(Boolean));

  // From / To boxes
  html += `<div class="info-row">`;
  html += `<div class="info-box"><div class="box-label">From (Source Branch)</div><div class="box-value">${t.from_branch_name || ''}</div></div>`;
  html += `<div class="info-box"><div class="box-label">To (Receiving Branch)</div><div class="box-value">${t.to_branch_name || ''}</div></div>`;
  html += `</div>`;

  // ── Variance recap (only shown when an accepted variance exists) ──────
  // Source: BTO is updated post-accept with variance_pin_verified_by_name /
  // accepted_by_name / total_capital_loss / pending_items[] qty_received.
  // Operators previously had to dig into the incident ticket to know the
  // printed paper qty changed — now the document declares it.
  const pendingMap = {};
  (t.pending_items || []).forEach(p => { pendingMap[p.product_id] = p; });
  const hasVariance = (t.shortages?.length || 0) + (t.excesses?.length || 0) > 0
    || Object.keys(pendingMap).length > 0;
  if (hasVariance) {
    const verifier = t.variance_pin_verified_by_name || t.accepted_by_name || '';
    const method = t.variance_pin_verified_method ? ` (${t.variance_pin_verified_method.replace('_', ' ')})` : '';
    const note = t.accept_note ? `<div class="box-sub">Note: ${t.accept_note}</div>` : '';
    html += `<div class="notes-box" style="border-color:#c2410c;background:#fff7ed;color:#7c2d12">`;
    html += `<strong>Variance accepted${verifier ? ' by ' + verifier + method : ''}</strong>`;
    if (t.total_capital_loss > 0) {
      html += ` · Capital loss: ${formatPHP(t.total_capital_loss)}`;
    }
    html += note;
    html += `</div>`;
  }

  // Items table - clean columns
  const items = t.items || [];
  html += '<table class="items-table"><thead><tr>';
  html += '<th style="width:5%">#</th><th>Product</th><th class="c" style="width:14%">Qty</th><th class="r" style="width:14%">Transfer Price</th><th class="r" style="width:14%">Total</th><th class="r" style="width:13%">Retail</th>';
  html += '</tr></thead><tbody>';
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const sentQty = parseFloat(item.qty) || 0;
    // After a variance accept, the *paper* qty should reflect what was
    // actually moved — `pending_items[].qty_received` is the source of
    // truth. Original sent qty is shown alongside as "(was X)".
    const pend = pendingMap[item.product_id];
    const effectiveQty = pend !== undefined
      ? (parseFloat(pend.qty_received) || 0)
      : sentQty;
    const wasLabel = (pend !== undefined && effectiveQty !== sentQty)
      ? `<div class="item-sub" style="color:#c2410c">was ${sentQty} ${item.unit || ''}</div>`
      : '';
    const tc = parseFloat(item.transfer_capital) || 0;
    html += `<tr><td class="c">${i + 1}</td>`;
    html += `<td>${item.product_name || ''}<div class="item-sub">${item.sku || ''} ${item.category ? '· ' + item.category : ''}</div></td>`;
    html += `<td class="c">${effectiveQty} ${item.unit || ''}${wasLabel}</td>`;
    html += `<td class="r">${formatPHP(tc)}</td>`;
    html += `<td class="r" style="font-weight:600">${formatPHP(tc * effectiveQty)}</td>`;
    html += `<td class="r" style="color:#1A4D2E;font-weight:600">${formatPHP(item.branch_retail)}</td>`;
    html += `</tr>`;
  }
  html += '</tbody></table>';

  // Totals + QR — compute from RECEIVED qty when variance has been accepted.
  const lineQty = (i) => {
    const p = pendingMap[i.product_id];
    return p !== undefined ? (parseFloat(p.qty_received) || 0) : (parseFloat(i.qty) || 0);
  };
  const totalTransfer = items.reduce((s, i) => s + (parseFloat(i.transfer_capital) || 0) * lineQty(i), 0);
  const totalRetail = items.reduce((s, i) => s + (parseFloat(i.branch_retail) || 0) * lineQty(i), 0);

  html += '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px">';
  html += qrImgTag(docCode, 100);
  html += '<div class="totals-box">';
  html += `<div class="t-row"><span>Total Items</span><span>${items.length}</span></div>`;
  html += `<div class="t-row"><span>Total Qty</span><span>${items.reduce((s, i) => s + lineQty(i), 0)}</span></div>`;
  html += `<div class="t-row t-grand"><span>Transfer Total</span><span>${formatPHP(totalTransfer)}</span></div>`;
  html += `<div class="t-row t-highlight"><span>Retail Value</span><span>${formatPHP(totalRetail)}</span></div>`;
  html += '</div></div>';

  // Signatures
  html += '<div class="sig-row"><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Prepared By</div></div><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Driver / Released By</div></div><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Received By</div></div></div>';
  html += `<div style="text-align:center;margin-top:16px"><div class="thank-you" style="font-size:12px;font-weight:600;color:#1A4D2E">Thank you!</div></div>`;
  html += buildFooter('internal', biz, { format: 'full_page' });
  return html;
}

// ── Expense Voucher ─────────────────────────────────────────────────────────
function expenseVoucherFullPage(data, biz, docCode) {
  const e = data;
  let html = buildPageHeader(biz, 'Expense Voucher', e.reference_number || e.id?.slice(0, 8) || '', e.date || e.created_at);

  html += '<div class="meta-grid">';
  html += `<div><span class="m-label">Category</span><div class="m-value">${e.category || 'General'}</div></div>`;
  html += `<div><span class="m-label">Payment</span><div class="m-value">${e.payment_method || 'Cash'}</div></div>`;
  html += `<div><span class="m-label">Amount</span><div class="m-value" style="font-size:16px;color:#1A4D2E">${formatPHP(e.amount)}</div></div>`;
  if (e.fund_source) html += `<div><span class="m-label">Source</span><div class="m-value">${e.fund_source}</div></div>`;
  html += '</div>';
  if (e.description) html += `<div class="notes-box"><strong>Description:</strong> ${e.description}</div>`;
  if (e.notes) html += `<div class="notes-box"><strong>Notes:</strong> ${e.notes}</div>`;

  html += '<div style="display:flex;justify-content:flex-end">' + qrImgTag(docCode, 80) + '</div>';
  html += '<div class="sig-row"><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Approved By</div></div><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Received By</div></div></div>';
  return html;
}

// ── Return Slip ─────────────────────────────────────────────────────────────
function returnSlipFullPage(data, biz, docCode) {
  const r = data;
  let html = buildPageHeader(biz, 'Return Slip', r.return_number || r.id?.slice(0, 8) || '', r.created_at, [
    `Original Invoice: ${r.original_invoice_number || ''}`,
  ]);

  if (r.customer_name) {
    html += `<div class="info-row"><div class="info-box"><div class="box-label">Customer</div><div class="box-value">${r.customer_name}</div></div>`;
    html += `<div class="info-box"><div class="box-label">Refund Method</div><div class="box-value">${r.refund_method || 'Cash'}</div>`;
    if (r.reason) html += `<div class="box-sub">Reason: ${r.reason}</div>`;
    html += `</div></div>`;
  }

  const items = r.items || [];
  html += '<table class="items-table"><thead><tr><th style="width:5%">#</th><th>Item</th><th class="c" style="width:10%">Qty</th><th class="r" style="width:15%">Price</th><th class="r" style="width:15%">Total</th></tr></thead><tbody>';
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const total = parseFloat(item.total) || (qty * rate);
    const unit = item.unit || item.uom || '';
    html += `<tr><td class="c">${i + 1}</td><td>${item.product_name || ''}</td><td class="c">${qty}${unit ? ' ' + unit : ''}</td><td class="r">${formatPHP(rate)}</td><td class="r" style="font-weight:600">${formatPHP(total)}</td></tr>`;
  }
  html += '</tbody></table>';

  html += '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px">';
  html += qrImgTag(docCode, 80);
  html += `<div class="totals-box"><div class="t-row t-grand"><span>Total Refund</span><span>${formatPHP(r.refund_amount || r.total_refund || 0)}</span></div></div>`;
  html += '</div>';

  html += '<div class="sig-row"><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Authorized By</div></div><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Customer Signature</div></div></div>';
  return html;
}

// ── Statement of Account ────────────────────────────────────────────────────
function statementFullPage(data, biz, docCode) {
  const s = data;
  let html = buildPageHeader(biz, 'Statement of Account', '', s.statement_date || new Date().toISOString());

  html += `<div class="info-row"><div class="info-box"><div class="box-label">Customer</div><div class="box-value" style="font-size:16px">${s.customer_name || ''}</div>`;
  if (s.customer_phone) html += `<div class="box-sub">${s.customer_phone}</div>`;
  if (s.customer_address) html += `<div class="box-sub">${s.customer_address}</div>`;
  html += `</div><div class="info-box"><div class="box-label">Balance Due</div><div class="box-value" style="font-size:18px;color:#c00">${formatPHP(s.closing_balance || s.balance || 0)}</div></div></div>`;

  if (s.transactions?.length) {
    html += '<table class="items-table"><thead><tr><th>Date</th><th>Reference</th><th>Description</th><th class="r">Debit</th><th class="r">Credit</th><th class="r">Balance</th></tr></thead><tbody>';
    for (const tx of s.transactions) {
      html += `<tr><td>${fmtDate(tx.date)}</td><td>${tx.reference || ''}</td><td>${tx.description || ''}</td><td class="r">${tx.debit > 0 ? formatPHP(tx.debit) : ''}</td><td class="r">${tx.credit > 0 ? formatPHP(tx.credit) : ''}</td><td class="r">${formatPHP(tx.running_balance || 0)}</td></tr>`;
    }
    html += '</tbody></table>';
  }

  html += '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px">';
  html += qrImgTag(docCode, 80);
  html += '<div class="totals-box">';
  if (s.opening_balance !== undefined) html += `<div class="t-row"><span>Opening</span><span>${formatPHP(s.opening_balance)}</span></div>`;
  if (s.total_charges !== undefined) html += `<div class="t-row"><span>Charges</span><span>${formatPHP(s.total_charges)}</span></div>`;
  if (s.total_payments !== undefined) html += `<div class="t-row"><span>Payments</span><span>-${formatPHP(s.total_payments)}</span></div>`;
  html += `<div class="t-row t-grand"><span>Balance Due</span><span>${formatPHP(s.closing_balance || s.balance || 0)}</span></div>`;
  html += '</div></div>';
  return html;
}

// ── Statement of Account — 58mm THERMAL ──────────────────────────────────────
// printType: 'open_invoices' | 'history' | 'payments'
export function statementThermal(data, biz, printType) {
  const { customer, open_invoices = [], open_balance = 0, history = [], payments_only = [], period, period_from, period_to } = data;
  // Use "P" instead of "₱" — the peso sign's thick stroke ghosts on 203dpi
  // thermal heads under Android WebView anti-aliasing. Plain "P" renders cleanly.
  const php = v => `P${(parseFloat(v) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fdt = iso => iso ? new Date(iso).toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';
  const today = new Date().toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' });

  const bizName = (biz?.business_name || biz?.name || 'AgriBooks').toUpperCase();
  const bizAddr = biz?.address || '';
  const custName = customer?.name || '';
  const custPhone = customer?.phone ? `Tel: ${customer.phone}` : '';

  let body = '';

  // ── Open Invoices ──────────────────────────────────────────────
  if (printType === 'open_invoices') {
    body += `<div class="doc-title">OPEN INVOICES</div>`;
    body += `<div class="meta-row"><span class="label">Customer:</span><span>${custName}</span></div>`;
    if (custPhone) body += `<div class="meta-row"><span></span><span>${custPhone}</span></div>`;
    body += `<div class="meta-row"><span class="label">Date:</span><span>${today}</span></div>`;
    body += `<div class="sep"></div>`;
    // OPEN BALANCE: size for emphasis, no bold (bold ghosts on peso amounts)
    body += `<div class="meta-row" style="font-size:14px"><span>OPEN BALANCE</span><span style="color:#c00">${php(open_balance)}</span></div>`;
    body += `<div class="sep"></div>`;

    if (!open_invoices.length) {
      body += `<div style="text-align:center;padding:8px 0;font-size:13px">No open invoices.</div>`;
    } else {
      for (const inv of open_invoices) {
        // Invoice number | Date — the number is the customer's reference for
        // "which invoice is this?" (owner bug 2026-07-28: the printout showed
        // only date/amount/due, unmatchable against actual receipts).
        body += `<div class="meta-row" style="font-size:12px"><span>${inv.invoice_number || inv.bir_invoice_number || '—'}</span><span>${fdt(inv.date || inv.invoice_date)}</span></div>`;
        // Amount | Due Date + Collectable — normal weight, no ghost
        body += `<div class="meta-row" style="font-size:12px"><span style="color:#444">Due: ${fdt(inv.due_date)}</span><span>${php(inv.grand_total)}</span></div>`;
        body += `<div class="meta-row" style="font-size:12px"><span style="color:#444">Collectable</span><span style="color:#c00">${php(inv.balance)}</span></div>`;
        body += `<div class="sep"></div>`;
      }
    }
    body += `<div class="meta-row" style="font-size:12px"><span>${open_invoices.length} invoice(s)</span><span>Due: ${php(open_balance)}</span></div>`;

  // ── Invoice & Payment History ──────────────────────────────────
  } else if (printType === 'history') {
    const periodLabel = period === 'all' ? 'All Time' : period === '7d' ? 'Last 7 Days' : period === '30d' ? 'Last 30 Days' : 'Last 3 Months';
    body += `<div class="doc-title">INVOICE & PAYMENT HISTORY</div>`;
    body += `<div class="meta-row"><span class="label">Customer:</span><span>${custName}</span></div>`;
    if (custPhone) body += `<div class="meta-row"><span></span><span>${custPhone}</span></div>`;
    body += `<div class="meta-row"><span class="label">Period:</span><span>${periodLabel}</span></div>`;
    body += `<div class="meta-row"><span class="label">Date:</span><span>${today}</span></div>`;
    body += `<div class="sep"></div>`;
    body += `<div class="meta-row" style="font-size:14px"><span>OPEN BALANCE</span><span style="color:#c00">${php(open_balance)}</span></div>`;
    body += `<div class="sep"></div>`;

    if (!history.length) {
      body += `<div style="text-align:center;padding:8px 0;font-size:13px">No transactions in this period.</div>`;
    } else {
      for (const row of history) {
        if (row.type === 'invoice') {
          const statusBadge = row.balance > 0 ? `[DUE: ${php(row.balance)}]` : '[PAID]';
          // Invoice number + amount — normal weight avoids ghost on amounts
          body += `<div class="meta-row" style="font-size:12px"><span>${row.invoice_number}</span><span>${php(row.grand_total)}</span></div>`;
          body += `<div class="meta-row" style="font-size:12px"><span>${fdt(row.date)} ${row.invoice_type !== 'Invoice' ? row.invoice_type : ''}</span><span>${statusBadge}</span></div>`;
        } else {
          // Payment row — green for visibility, no bold
          body += `<div class="meta-row" style="font-size:12px;color:#006600"><span>Payment ${row.method}</span><span>-${php(row.amount)}</span></div>`;
          body += `<div class="meta-row" style="font-size:12px"><span>${fdt(row.date)}</span><span>${row.receipt_number || row.reference || ''}</span></div>`;
        }
        body += `<div class="sep"></div>`;
      }
    }

  // ── Payment History ─────────────────────────────────────────────
  } else if (printType === 'payments') {
    const periodLabel = period === 'all' ? 'All Time' : period === '7d' ? 'Last 7 Days' : period === '30d' ? 'Last 30 Days' : 'Last 3 Months';
    body += `<div class="doc-title">PAYMENT HISTORY</div>`;
    body += `<div class="meta-row"><span class="label">Customer:</span><span>${custName}</span></div>`;
    if (custPhone) body += `<div class="meta-row"><span></span><span>${custPhone}</span></div>`;
    body += `<div class="meta-row"><span class="label">Period:</span><span>${periodLabel}</span></div>`;
    body += `<div class="meta-row"><span class="label">Date:</span><span>${today}</span></div>`;
    body += `<div class="sep"></div>`;
    body += `<div class="meta-row" style="font-size:14px"><span>OPEN BALANCE</span><span style="color:#c00">${php(open_balance)}</span></div>`;
    body += `<div class="sep"></div>`;

    if (!payments_only.length) {
      body += `<div style="text-align:center;padding:8px 0;font-size:13px">No payments in this period.</div>`;
    } else {
      let total_paid = 0;
      for (const p of payments_only) {
        total_paid += parseFloat(p.amount) || 0;
        // Amount + method — normal weight, no ghost on "P" amounts
        body += `<div class="meta-row" style="font-size:12px"><span>${php(p.amount)}</span><span>${p.method}</span></div>`;
        body += `<div class="meta-row" style="font-size:12px"><span>${fdt(p.date)}</span><span>${p.invoice_number}</span></div>`;
        if (p.receipt_number || p.reference) body += `<div style="font-size:12px;color:#444;padding-left:4px">Ref: ${p.receipt_number || p.reference}</div>`;
        body += `<div class="sep"></div>`;
      }
      body += `<div class="meta-row" style="font-size:12px"><span>${payments_only.length} payment(s)</span><span>${php(total_paid)}</span></div>`;
    }
  }

  // Footer with signature line
  body += `<div style="margin-top:12px;text-align:center;font-size:12px">Prepared by: _______________</div>`;
  body += `<div style="text-align:center;font-size:11px;margin-top:4px">Customer Signature: ________</div>`;
  body += `<div style="text-align:center;font-size:11px;margin-top:8px">This is not an official receipt.</div>`;

  return `<!DOCTYPE html><html><head><meta charset="utf-8">${THERMAL_VIEWPORT_META}<title>Customer Statement</title>
  <style>${thermalCSS}</style></head><body>
  <div class="header">
    <div class="biz-name">${bizName}</div>
    ${bizAddr ? `<div class="biz-detail">${bizAddr}</div>` : ''}
  </div>
  ${body}
  <script>setTimeout(()=>window.print(),400)<\/script>
  </body></html>`;
}

// ── PROOF OF PAYMENT (supplementary, NON-BIR — replaces Official Receipt) ──
function _popLines(data) {
  const raw = data.lines || data.invoices_covered || data.invoices || [];
  return raw.map(l => ({
    invoice_number: l.invoice_number || '',
    original_amount: l.original_amount != null ? l.original_amount : (l.grand_total || 0),
    balance_before: l.balance_before != null ? l.balance_before : null,
    amount_applied: l.amount_applied != null ? l.amount_applied : (l.applied || 0),
    pending_balance: l.pending_balance != null ? l.pending_balance : (l.balance_after || 0),
  }));
}

// Whole-account AR after the payment (owner 2026-07-16: the PP tells the
// customer what's still pending on their ACCOUNT, beyond the invoices this
// payment touched). Only receipts created after the backend change carry
// `account_balance_after`; old stored receipts return null → block hidden.
function _popAccountBalance(pp) {
  const v = pp.account_balance_after;
  if (v == null || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

// Method-detail lines (owner 2026-08-10: check number, check/PDC date, bank
// and e-wallet references must be traceable from the printed PP). Old stored
// receipts lack these fields and print nothing extra. For checks the check
// number doubles as the reference, so only the Check No. line is shown; a
// future-dated check is tagged (PDC) against the payment date.
function _popMethodLines(pp) {
  const out = [];
  const method = String(pp.method || pp.payment_method || '').toLowerCase();
  const ref = String(pp.reference || '').trim();
  const bank = String(pp.bank_name || '').trim();
  const sender = String(pp.sender || '').trim();
  if (method === 'check') {
    const chk = String(pp.check_number || '').trim() || ref;
    if (chk) out.push({ label: 'Check No.', value: chk });
    if (bank) out.push({ label: 'Bank', value: bank });
    if (pp.check_date) {
      const isPdc = pp.date && String(pp.check_date).slice(0, 10) > String(pp.date).slice(0, 10);
      out.push({ label: 'Check Date', value: fmtDate(pp.check_date) + (isPdc ? ' (PDC)' : '') });
    }
  } else {
    if (ref) out.push({ label: 'Ref No.', value: ref });
    if (bank) out.push({ label: 'Bank', value: bank });
    if (sender) out.push({ label: 'Sender', value: sender });
  }
  // Memo (family hunt 2026-09-01): captured since the field shipped, stored
  // since today — one line here reaches all four PP templates.
  const memo = String(pp.memo || '').trim();
  if (memo) out.push({ label: 'Memo', value: memo });
  return out;
}

// Date-correction line for the printed PP (owner 2026-08-12): a receipt whose
// date was edited must SAY so — deposit date, encoded moment, and the
// correction are all visible, so nobody can silently re-date a payment. Shows
// the latest correction in full; more than one collapses into a count.
function _popCorrectionLine(pp) {
  const edits = Array.isArray(pp.date_edit_history) ? pp.date_edit_history : [];
  if (!edits.length) return '';
  const last = edits[edits.length - 1];
  const by = last.edited_by ? ` by ${last.edited_by}` : '';
  const was = last.from ? ` — was ${fmtDate(last.from)}` : '';
  const more = edits.length > 1 ? ` (${edits.length} corrections)` : '';
  return `Date corrected ${fmtDateTime(last.edited_at)}${by}${was}${more}`;
}

function proofOfPaymentFullPage(data, biz, docCode) {
  const pp = data;
  const lines = _popLines(pp);
  const num = pp.receipt_number || pp.number || '';
  const correction = _popCorrectionLine(pp);

  let html = `<div class="page-header">`;
  html += `<div class="company">`;
  html += `<div class="biz-name">${biz.business_name || 'AgriBooks'}</div>`;
  if (biz.address) html += `<div class="biz-detail">${biz.address}</div>`;
  if (biz.phone) html += `<div class="biz-detail">${biz.phone}</div>`;
  html += `</div>`;
  html += `<div class="doc-meta">`;
  html += `<div class="doc-type">PROOF OF PAYMENT</div>`;
  if (num) html += `<div class="doc-date" style="font-weight:700;color:#1A4D2E">${num}</div>`;
  html += `<div class="doc-date">${fmtDate(pp.payment_date || pp.date || new Date().toISOString())}</div>`;
  if (pp.payment_date && pp.date && pp.payment_date !== pp.date) html += `<div class="doc-date" style="font-size:9px">Posted: ${fmtDate(pp.date)}</div>`;
  // Recorded = created_at, the exact moment the payment was keyed in (owner
  // 2026-08-12: the printed time is what lets a payment be found on CCTV).
  // Distinct from `date`, which is the business payment date and can be
  // backdated. Older receipts without created_at just omit the line.
  if (pp.created_at) html += `<div class="doc-date">Recorded: ${fmtDateTime(pp.created_at)}</div>`;
  if (correction) html += `<div class="doc-date" style="font-size:9px;color:#92400e">${correction}</div>`;
  html += `</div></div>`;

  html += `<div class="info-row"><div class="info-box"><div class="box-label">Received From</div>`;
  html += `<div class="box-value">${pp.customer_name || 'Customer'}</div></div>`;
  html += `<div class="info-box"><div class="box-label">Payment</div>`;
  html += `<div class="box-value">${pp.method || pp.payment_method || 'Cash'}</div>`;
  for (const ml of _popMethodLines(pp)) {
    html += `<div class="box-sub">${ml.label}: ${ml.value}</div>`;
  }
  html += `<div class="box-sub">Received by: ${pp.recorded_by || pp.cashier_name || ''}</div>`;
  html += `</div></div>`;

  if (lines.length > 0) {
    html += `<table class="items-table"><thead><tr>`;
    html += `<th>Receipt #</th><th class="r">Original Amt</th><th class="r">Balance Before</th><th class="r">Applied</th><th class="r">Pending Balance</th>`;
    html += `</tr></thead><tbody>`;
    for (const l of lines) {
      const settled = (l.pending_balance || 0) <= 0;
      html += `<tr><td>${l.invoice_number}</td>`;
      html += `<td class="r">${formatPHP(l.original_amount || 0)}</td>`;
      html += `<td class="r">${l.balance_before != null ? formatPHP(l.balance_before) : '—'}</td>`;
      html += `<td class="r" style="font-weight:700">${formatPHP(l.amount_applied || 0)}</td>`;
      html += `<td class="r">${settled ? '<span style="color:#16a34a;font-weight:700">SETTLED</span>' : formatPHP(l.pending_balance || 0)}</td>`;
      html += `</tr>`;
    }
    html += `</tbody></table>`;
  }

  html += `<div style="display:flex;justify-content:flex-end;margin-top:12px"><div class="totals-box">`;
  if ((pp.total_principal || 0) > 0) html += `<div class="t-row"><span>Total Principal</span><span>${formatPHP(pp.total_principal)}</span></div>`;
  if ((pp.total_interest || 0) > 0) html += `<div class="t-row"><span>Total Interest</span><span>${formatPHP(pp.total_interest)}</span></div>`;
  if ((pp.total_penalty || 0) > 0) html += `<div class="t-row"><span>Total Penalty</span><span>${formatPHP(pp.total_penalty)}</span></div>`;
  html += `<div class="t-row t-grand"><span>TOTAL RECEIVED</span><span>${formatPHP(pp.total_amount || 0)}</span></div>`;
  {
    const acctBal = _popAccountBalance(pp);
    if (acctBal != null) {
      html += acctBal > 0.005
        ? `<div class="t-row" style="border-top:1px solid #ccc;margin-top:4px;padding-top:4px"><span>Account Balance (pending)</span><span style="font-weight:700;color:#b91c1c">${formatPHP(acctBal)}</span></div>`
        : `<div class="t-row" style="border-top:1px solid #ccc;margin-top:4px;padding-top:4px"><span>Account Balance</span><span style="font-weight:700;color:#16a34a">SETTLED</span></div>`;
    }
  }
  html += `</div></div>`;
  {
    const acctBal = _popAccountBalance(pp);
    if (acctBal != null && acctBal > 0.005) {
      html += `<div style="text-align:right;font-size:10px;color:#555;margin-top:2px">This amount is still pending on your account after this payment.</div>`;
    }
  }

  html += `<div style="margin-top:32px;padding-top:16px;border-top:1px solid #eee;"><div class="sig-row">`;
  html += `<div class="sig-block"><div class="sig-line"></div><div class="sig-label">Received By</div></div>`;
  html += `<div class="sig-block"><div class="sig-line"></div><div class="sig-label">Customer Signature</div></div>`;
  html += `</div></div>`;

  if (docCode) html += `<div style="text-align:center;margin-top:12px">${qrImgTag(docCode, 96)}</div>`;
  html += buildFooter('proof_of_payment', biz, { format: 'full_page' });
  return html;
}

function proofOfPaymentThermal(data, biz, docCode) {
  const pp = data;
  const lines = _popLines(pp);
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">PROOF OF PAYMENT</div>`;
  if (pp.receipt_number) html += `<div class="meta-row"><span class="label">Receipt #:</span><span>${pp.receipt_number}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDate(pp.payment_date || pp.date || new Date().toISOString())}</span></div>`;
  if (pp.payment_date && pp.date && pp.payment_date !== pp.date) html += `<div class="meta-row"><span class="label">Posted:</span><span>${fmtDate(pp.date)}</span></div>`;
  if (pp.created_at) html += `<div class="meta-row"><span class="label">Recorded:</span><span>${fmtDateTime(pp.created_at)}</span></div>`;
  {
    const correction = _popCorrectionLine(pp);
    if (correction) html += `<div style="font-size:9px">${correction}</div>`;
  }
  html += `<div class="meta-row"><span class="label">From:</span><span>${pp.customer_name || 'Customer'}</span></div>`;
  html += `<div class="meta-row"><span class="label">Payment:</span><span>${pp.method || pp.payment_method || 'Cash'}</span></div>`;
  for (const ml of _popMethodLines(pp)) {
    html += `<div class="meta-row"><span class="label">${ml.label}:</span><span>${ml.value}</span></div>`;
  }
  html += `<div class="meta-row"><span class="label">Received by:</span><span>${pp.recorded_by || pp.cashier_name || ''}</span></div>`;
  html += '<div class="sep"></div>';
  for (const l of lines) {
    const settled = (l.pending_balance || 0) <= 0;
    html += `<div style="font-weight:700">${l.invoice_number}</div>`;
    html += `<div class="meta-row"><span class="label">Applied:</span><span>${formatPHP(l.amount_applied || 0)}</span></div>`;
    html += `<div class="meta-row"><span class="label">Pending:</span><span>${settled ? 'SETTLED' : formatPHP(l.pending_balance || 0)}</span></div>`;
  }
  html += '<div class="sep"></div><div class="totals">';
  if ((pp.total_interest || 0) > 0) html += `<div class="row"><span>Interest</span><span>${formatPHP(pp.total_interest)}</span></div>`;
  if ((pp.total_penalty || 0) > 0) html += `<div class="row"><span>Penalty</span><span>${formatPHP(pp.total_penalty)}</span></div>`;
  html += `<div class="row grand"><span>TOTAL RECEIVED</span><span>${formatPHP(pp.total_amount || 0)}</span></div>`;
  html += '</div>';
  {
    const acctBal = _popAccountBalance(pp);
    if (acctBal != null) {
      html += '<div class="sep"></div>';
      if (acctBal > 0.005) {
        html += `<div class="totals"><div class="row" style="font-weight:bold"><span>ACCOUNT BALANCE</span><span>${formatPHP(acctBal)}</span></div></div>`;
        html += `<div style="font-size:10px;text-align:center">This amount is still pending on your account after this payment.</div>`;
      } else {
        html += `<div style="font-weight:bold;text-align:center">ACCOUNT FULLY SETTLED</div>`;
        html += `<div style="font-size:10px;text-align:center">No pending balance on your account. Thank you!</div>`;
      }
    }
  }
  if (docCode) html += qrImgTag(docCode, 120);
  html += buildFooter('proof_of_payment', biz, { format: 'thermal' });
  return html;
}

function proofOfPaymentDotMatrix(data, biz, docCode) {
  const pp = data;
  const lines = _popLines(pp);

  // Standard dm-letterhead (was the legacy centered header — visually
  // inconsistent with every other DM document).
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.email)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Email</span>${biz.email}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);
  const letterhead = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
        </td>
        <td class="dm-lh-title-cell" style="width:240px;">
          <div class="dm-lh-title">PROOF OF PAYMENT</div>
          <div class="dm-lh-invbox">
            <table>
              ${pp.receipt_number ? `<tr><td>Receipt #</td><td class="dm-inv-val">${pp.receipt_number}</td></tr>` : ''}
              <tr><td>Date</td><td class="dm-inv-val">${fmtDate(pp.payment_date || pp.date || new Date().toISOString())}</td></tr>
              ${pp.payment_date && pp.date && pp.payment_date !== pp.date ? `<tr><td>Posted</td><td class="dm-inv-val">${fmtDate(pp.date)}</td></tr>` : ''}
              ${pp.created_at ? `<tr><td>Recorded</td><td class="dm-inv-val">${fmtDateTime(pp.created_at)}</td></tr>` : ''}
            </table>
          </div>
          ${_popCorrectionLine(pp) ? `<div style="font-size:9px;margin-top:3px">${_popCorrectionLine(pp)}</div>` : ''}
        </td>
      </tr>
    </table>
  `;
  const tfootHtml = `
    <div class="dm-foot-receipt">Receipt No: ${pp.receipt_number || ''} &nbsp;·&nbsp; Printed: ${formatPrintedAt()}</div>
  `;

  let body = `<table class="dm-meta-grid">
    <tr>
      <th style="width:40%;">Received From</th>
      <th style="width:30%;">Payment Method</th>
      <th>Received By</th>
    </tr>
    <tr>
      <td>${pp.customer_name || 'Customer'}</td>
      <td>${pp.method || pp.payment_method || 'Cash'}${_popMethodLines(pp).map(ml => `<br/><span style="font-size:11px">${ml.label}: ${ml.value}</span>`).join('')}</td>
      <td>${pp.recorded_by || ''}</td>
    </tr>
  </table>`;
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th>Receipt #</th><th class="r">Original</th><th class="r">Bal. Before</th><th class="r">Applied</th><th class="r">Pending</th>`;
  body += `</tr></thead><tbody>`;
  for (const l of lines) {
    const settled = (l.pending_balance || 0) <= 0;
    body += `<tr><td>${l.invoice_number}</td>`;
    body += `<td class="r">${formatPHP(l.original_amount || 0)}</td>`;
    body += `<td class="r">${l.balance_before != null ? formatPHP(l.balance_before) : '-'}</td>`;
    body += `<td class="r">${formatPHP(l.amount_applied || 0)}</td>`;
    body += `<td class="r">${settled ? 'SETTLED' : formatPHP(l.pending_balance || 0)}</td></tr>`;
  }
  body += `</tbody></table>`;
  // Principal / interest / penalty breakdown (parity with FP + thermal).
  body += `<table class="dm-total-box">`;
  if ((pp.total_principal || 0) > 0) body += `<tr><td class="dm-tb-label">Total Principal</td><td class="dm-tb-val">${formatPHP(pp.total_principal)}</td></tr>`;
  if ((pp.total_interest || 0) > 0) body += `<tr><td class="dm-tb-label">Total Interest</td><td class="dm-tb-val">${formatPHP(pp.total_interest)}</td></tr>`;
  if ((pp.total_penalty || 0) > 0) body += `<tr><td class="dm-tb-label">Total Penalty</td><td class="dm-tb-val">${formatPHP(pp.total_penalty)}</td></tr>`;
  body += `<tr class="dm-tb-grand"><td class="dm-tb-label">TOTAL RECEIVED</td><td class="dm-tb-val">${formatPHP(pp.total_amount || 0)}</td></tr>`;
  {
    const acctBal = _popAccountBalance(pp);
    if (acctBal != null) {
      body += acctBal > 0.005
        ? `<tr><td class="dm-tb-label">Account Balance (pending)</td><td class="dm-tb-val">${formatPHP(acctBal)}</td></tr>`
        : `<tr><td class="dm-tb-label">Account Balance</td><td class="dm-tb-val">SETTLED</td></tr>`;
    }
  }
  body += `</table>`;
  {
    const acctBal = _popAccountBalance(pp);
    if (acctBal != null && acctBal > 0.005) {
      body += `<div style="text-align:right;font-size:11px;margin-top:2px">This amount is still pending on your account after this payment.</div>`;
    }
  }
  body += `<div class="dm-sig-row" style="margin-top:24px;">`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Received By</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Customer Signature</div></div>`;
  body += `</div>`;
  body += qrImgTagDM(docCode, pp);
  body += buildFooter('proof_of_payment', biz, { format: 'dot_matrix' });
  return `<table class="dm-page-frame"><thead><tr><td>${letterhead}</td></tr></thead><tfoot><tr><td>${tfootHtml}</td></tr></tfoot><tbody><tr><td>${body}</td></tr></tbody></table>`;
}



// ── Terminal Proof of Payment — 58mm thermal ────────────────────────────────
// Uses the same thermal CSS classes as all other 58mm documents.
// Designed for credit AR collections: PP-MB-XXXXXX header, customer info,
// per-invoice breakdown, remaining balance.
function proofOfPaymentTerminal(data, biz) {
  const pp = data;
  const lines = (pp.lines || []).filter(l => parseFloat(l.amount_applied || 0) > 0);
  const totalRemaining = lines.reduce((s, l) => s + (parseFloat(l.pending_balance) || 0), 0);
  const hasDiscount = (parseFloat(pp.total_discount) || 0) > 0;

  const fmtD = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' });
    } catch { return iso?.slice(0, 10) || '—'; }
  };

  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">PROOF OF PAYMENT</div>`;

  // ── Transaction header ──
  html += `<div class="meta-row"><span class="label">Transaction No:</span><span>${pp.receipt_number || '—'}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtD(pp.payment_date || pp.date)}</span></div>`;
  if (pp.payment_date && pp.date && pp.payment_date !== pp.date) html += `<div class="meta-row"><span class="label">Posted:</span><span>${fmtD(pp.date)}</span></div>`;
  if (pp.created_at) html += `<div class="meta-row"><span class="label">Recorded:</span><span>${fmtDateTime(pp.created_at)}</span></div>`;
  {
    const correction = _popCorrectionLine(pp);
    if (correction) html += `<div style="font-size:9px">${correction}</div>`;
  }
  html += `<div class="meta-row"><span class="label">Customer:</span><span>${pp.customer_name || '—'}</span></div>`;
  html += `<div class="meta-row"><span class="label">Method:</span><span>${pp.method || 'Cash'}</span></div>`;
  for (const ml of _popMethodLines(pp)) {
    html += `<div class="meta-row"><span class="label">${ml.label}:</span><span>${ml.value}</span></div>`;
  }
  if (pp.recorded_by) html += `<div class="meta-row"><span class="label">Received by:</span><span>${pp.recorded_by}</span></div>`;

  html += '<div class="sep"></div>';

  // ── Per-invoice lines ──
  for (const l of lines) {
    const settled = (parseFloat(l.pending_balance) || 0) <= 0.005;
    html += `<div style="font-weight:bold;font-size:13px;margin-bottom:2px">${l.invoice_number || '—'}</div>`;
    if (l.invoice_date) html += `<div class="meta-row"><span class="label">Invoice Date:</span><span>${fmtD(l.invoice_date)}</span></div>`;
    if (l.due_date)     html += `<div class="meta-row"><span class="label">Due Date:</span><span>${fmtD(l.due_date)}</span></div>`;
    html += `<div class="meta-row"><span class="label">Original Amt:</span><span>${formatPHP(l.original_amount || 0)}</span></div>`;
    html += `<div class="meta-row"><span class="label">Balance Before:</span><span>${formatPHP(l.balance_before || 0)}</span></div>`;
    if ((parseFloat(l.discount) || 0) > 0) {
      html += `<div class="meta-row"><span class="label">Discount:</span><span>-${formatPHP(l.discount)}</span></div>`;
    }
    html += `<div class="meta-row"><span class="label">Amount Paid:</span><span>${formatPHP(l.amount_applied || 0)}</span></div>`;
    html += `<div class="meta-row" style="${settled ? 'color:#16a34a' : 'color:#b91c1c'}">`;
    html += `<span class="label">Remaining:</span><span>${settled ? 'SETTLED' : formatPHP(l.pending_balance)}</span></div>`;
    html += '<div style="border-top:1px dashed #000;margin:5px 0"></div>';
  }

  // ── Totals ──
  html += '<div class="totals">';
  if ((parseFloat(pp.total_interest) || 0) > 0) {
    html += `<div class="row"><span>Interest Paid</span><span>${formatPHP(pp.total_interest)}</span></div>`;
  }
  if ((parseFloat(pp.total_penalty) || 0) > 0) {
    html += `<div class="row"><span>Penalty Paid</span><span>${formatPHP(pp.total_penalty)}</span></div>`;
  }
  if (hasDiscount) {
    html += `<div class="row"><span>Total Discount</span><span>-${formatPHP(pp.total_discount)}</span></div>`;
  }
  html += `<div class="row grand"><span>TOTAL RECEIVED</span><span>${formatPHP(pp.total_amount || 0)}</span></div>`;
  // Prefer the WHOLE-ACCOUNT balance stamped on new receipts; fall back to
  // the covered-invoices sum for receipts that predate account_balance_after.
  const acctBal = _popAccountBalance(pp);
  const shownRemaining = acctBal != null ? acctBal : totalRemaining;
  if (shownRemaining > 0.005) {
    html += `<div class="row" style="color:#b91c1c"><span>${acctBal != null ? 'ACCOUNT BALANCE' : 'REMAINING BAL'}</span><span>${formatPHP(shownRemaining)}</span></div>`;
    if (acctBal != null) html += `<div style="font-size:10px;text-align:center">This amount is still pending on your account after this payment.</div>`;
  } else if (lines.length > 0) {
    html += `<div class="row" style="color:#16a34a"><span>ACCOUNT SETTLED</span><span></span></div>`;
  }
  html += '</div>';

  html += buildFooter('proof_of_payment', biz, { format: 'thermal' });
  return html;
}


// ── BIR-Accredited Document: Sales Invoice / Charge Invoice (Goods Seller) ──
function officialReceiptFullPage(data, biz, docCode) {
  const inv = data;
  // Goods seller (RR 10-2019):
  //   Paid sale        → SALES INVOICE (SI)
  //   Credit/Utang     → CHARGE INVOICE (CI)
  //   Payment receipt  → OFFICIAL RECEIPT (supplementary, issued on payment collection)
  const docLabel = inv.bir_document_label || (
    (inv.payment_type === 'credit' || inv.payment_type === 'partial')
      ? 'CHARGE INVOICE'
      : 'SALES INVOICE'
  );
  const birNumber = inv.bir_invoice_number || '';
  const isVat = inv.bir_vat_type === 'vat_registered';

  let html = `<div class="page-header">`;
  html += `<div class="company">`;
  html += `<div class="biz-name">${biz.business_name_bir || biz.business_name || 'AgriBooks'}</div>`;
  if (biz.address) html += `<div class="biz-detail">${biz.address}</div>`;
  if (biz.phone) html += `<div class="biz-detail">${biz.phone}</div>`;
  const tin = biz.tin || inv.bir_tin || '';
  if (tin) html += `<div class="biz-detail"><strong>TIN:</strong> ${tin}</div>`;
  const msn = biz.bir_machine_serial || inv.bir_machine_serial || '';
  if (msn) html += `<div class="biz-detail" style="font-size:9px">MSN: ${msn}</div>`;
  const atp = biz.bir_atp_number || inv.bir_atp_number || '';
  if (atp) html += `<div class="biz-detail" style="font-size:9px">ATP: ${atp}</div>`;
  const permit = biz.bir_permit_number || inv.bir_permit_number || '';
  if (permit) html += `<div class="biz-detail" style="font-size:9px">Permit No.: ${permit}</div>`;
  html += `</div>`;
  html += `<div class="doc-meta">`;
  html += `<div class="doc-type">${docLabel}</div>`;
  html += `<div class="doc-number">${birNumber}</div>`;
  html += `<div class="doc-date">${fmtDate(inv.bir_issued_at || inv.invoice_date || inv.created_at)}</div>`;
  if (inv.invoice_number) html += `<div class="doc-date" style="font-size:9px">Ref: ${inv.invoice_number}</div>`;
  if (inv.cashier_name) html += `<div class="doc-date">Cashier: ${inv.cashier_name}</div>`;
  html += `</div></div>`;

  if (inv.customer_name) {
    html += `<div class="info-row"><div class="info-box"><div class="box-label">Customer</div><div class="box-value">${inv.customer_name}</div>`;
    if (inv.customer_address) html += `<div class="box-sub">${inv.customer_address}</div>`;
    if (inv.customer_phone) html += `<div class="box-sub">${inv.customer_phone}</div>`;
    if (inv.bir_customer_tin) html += `<div class="box-sub">TIN: ${inv.bir_customer_tin}</div>`;
    html += `</div><div class="info-box"><div class="box-label">Payment</div><div class="box-value">${inv.payment_method || 'Cash'}</div>`;
    if (inv.terms && inv.terms !== 'COD') html += `<div class="box-sub">Terms: ${inv.terms}</div>`;
    if (inv.due_date) html += `<div class="box-sub">Due: ${fmtDate(inv.due_date)}</div>`;
    if (inv.customer_po) html += `<div class="box-sub">Ref PO: ${inv.customer_po}</div>`;
    html += `</div></div>`;
  }

  const items = inv.items || [];
  html += '<table class="items-table"><thead><tr><th style="width:5%">#</th><th>Item</th><th class="c" style="width:10%">Qty</th><th class="r" style="width:15%">Price</th><th class="r" style="width:15%">Total</th></tr></thead><tbody>';
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const disc = parseFloat(item.discount_amount) || 0;
    const total = parseFloat(item.total) || (qty * rate - disc);
    const unit = item.unit || item.uom || '';
    const vatClass = item.vat_class || 'vatable';
    html += `<tr><td class="c">${i + 1}</td><td>${item.product_name || ''}`;
    if (vatClass === 'vat_exempt') html += ` <span style="font-size:9px;color:#888">(VAT-Exempt)</span>`;
    if (vatClass === 'zero_rated') html += ` <span style="font-size:9px;color:#888">(Zero-Rated)</span>`;
    html += `</td><td class="c">${qty}${unit ? ' ' + unit : ''}</td><td class="r">${formatPHP(rate)}</td><td class="r" style="font-weight:600">${formatPHP(total)}</td></tr>`;
  }
  html += '</tbody></table>';

  html += '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px">';
  html += qrImgTag(docCode, 80);
  html += '<div>';
  html += '<div class="totals-box">';
  html += `<div class="t-row"><span>Subtotal</span><span>${formatPHP(inv.subtotal || inv.grand_total)}</span></div>`;
  if (parseFloat(inv.overall_discount) > 0) html += `<div class="t-row"><span>Discount</span><span>-${formatPHP(inv.overall_discount)}</span></div>`;
  html += scpwdRowsHTML(inv, 'fp');
  html += `<div class="t-row t-grand"><span>TOTAL</span><span>${formatPHP(inv.grand_total)}</span></div>`;
  const _t = parseFloat(inv.tendered_amount) || 0;
  const _ch = inv.change_amount != null ? parseFloat(inv.change_amount) || 0 : Math.max(0, _t - (parseFloat(inv.grand_total) || 0));
  if (_t > 0 && inv.payment_type !== 'credit') {
    html += `<div class="t-row"><span>Tendered</span><span>${formatPHP(_t)}</span></div>`;
    if (_ch > 0) html += `<div class="t-row"><span>Change</span><span>${formatPHP(_ch)}</span></div>`;
  }
  for (const ap of addonTenderPairs(inv)) {
    html += `<div class="t-row"><span>Add-on Tendered</span><span>${formatPHP(ap.tendered)}</span></div>`;
    if (ap.change > 0) html += `<div class="t-row"><span>Add-on Change</span><span>${formatPHP(ap.change)}</span></div>`;
  }
  const _bal = (parseFloat(inv.grand_total) || 0) - (parseFloat(inv.amount_paid) || 0);
  if (_bal > 0 && inv.payment_type === 'credit') html += `<div class="t-row" style="color:#c00;font-weight:600"><span>Balance Due</span><span>${formatPHP(_bal)}</span></div>`;
  html += '</div>';

  if (isVat) {
    const vatable = parseFloat(inv.vatable_sales) || 0;
    const vatAmt  = parseFloat(inv.vat_amount) || 0;
    const exempt  = parseFloat(inv.vat_exempt_sales) || 0;
    const zeroR   = parseFloat(inv.zero_rated_sales) || 0;
    const vatRate = parseFloat(inv.vat_rate) || 12;
    html += `<div style="margin-top:8px;border:1px solid #e2e8e2;border-radius:4px;padding:8px 12px;font-size:10px;">`;
    html += `<div style="font-size:9px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">VAT Breakdown</div>`;
    html += `<div style="display:flex;justify-content:space-between;padding:2px 0"><span>Vatable Sales</span><span>${formatPHP(vatable)}</span></div>`;
    html += `<div style="display:flex;justify-content:space-between;padding:2px 0"><span>VAT (${vatRate}%)</span><span>${formatPHP(vatAmt)}</span></div>`;
    if (exempt > 0) html += `<div style="display:flex;justify-content:space-between;padding:2px 0"><span>VAT-Exempt</span><span>${formatPHP(exempt)}</span></div>`;
    if (zeroR > 0) html += `<div style="display:flex;justify-content:space-between;padding:2px 0"><span>Zero-Rated</span><span>${formatPHP(zeroR)}</span></div>`;
    html += `</div>`;
  }
  html += '</div></div>';
  html += scpwdCardHTML(inv, 'fp');

  html += '<div style="margin-top:32px;padding-top:16px;border-top:1px solid #eee;">';
  html += '<div class="sig-row"><div class="sig-block"><div class="sig-line"></div><div class="sig-label">Authorized Representative</div></div>';
  html += '<div class="sig-block"><div class="sig-line"></div><div class="sig-label">Customer Signature &amp; Printed Name</div></div></div>';
  html += '</div>';

  html += `<div style="text-align:center;margin-top:16px"><div class="thank-you" style="font-size:12px;font-weight:600;color:#1A4D2E">Thank you for your business!</div>`;
  html += `<div style="font-size:11px;font-weight:800;color:#1A4D2E;letter-spacing:0.5px;margin-top:4px">THIS IS YOUR ${docLabel}</div></div>`;
  const _ciKind = (inv.payment_type === 'credit' || inv.payment_type === 'partial') ? 'charge_invoice' : 'sales_invoice';
  html += buildFooter(_ciKind, biz, { format: 'full_page' });
  return html;
}



// ═══════════════════════════════════════════════════════════════════════════
//  DOT MATRIX DOCUMENTS (8.5×11 — Epson LX-310)
// ═══════════════════════════════════════════════════════════════════════════

// ── Dot Matrix: shared company header ──────────────────────────────────────
function buildDotMatrixHeader(biz) {
  let html = `<div class="dm-header">`;
  html += `<div class="dm-biz-name">${biz.business_name || 'AgriBooks'}</div>`;
  if (biz.address) html += `<div class="dm-biz-detail">${biz.address}</div>`;
  if (biz.phone) html += `<div class="dm-biz-detail">Tel: ${biz.phone}</div>`;
  if (biz.tin) html += `<div class="dm-biz-detail">TIN: ${biz.tin}</div>`;
  html += `</div>`;
  return html;
}

// ── Dot Matrix: QR block (120×120, high contrast for LX-310) ───────────────
function qrImgTagDM(code, inv) {
  if (!code) return '';
  const url = `${window.location.origin}/doc/${code}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(url)}&margin=4&color=000000&bgcolor=ffffff`;
  return `
    <div class="dm-qr-section">
      <img src="${qrUrl}" alt="QR Code" width="120" height="120" />
      <div class="dm-qr-info">
        <div class="dm-qr-code">${code}</div>
        <div class="dm-qr-scan">Scan to view document</div>
        ${inv.invoice_number ? `<div class="dm-qr-sub">Transaction No: ${inv.invoice_number}</div>` : ''}
        ${inv.invoice_date || inv.created_at || inv.order_date ? `<div class="dm-qr-sub">Date: ${fmtDate(inv.invoice_date || inv.created_at || inv.order_date)}</div>` : ''}
        ${inv.customer_name ? `<div class="dm-qr-sub">Customer: ${inv.customer_name}</div>` : ''}
      </div>
    </div>`;
}

// ── Shared DM document shell (letterhead + repeating tfoot + page-frame) ────
// Used by the four templates added for the full_page→dot_matrix migration;
// the older DM templates inline the same structure.
function dmShell({ biz, docTitle, invRows, footLine, body }) {
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.email)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Email</span>${biz.email}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);
  const letterhead = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
        </td>
        <td class="dm-lh-title-cell" style="width:240px;">
          <div class="dm-lh-title">${docTitle}</div>
          <div class="dm-lh-invbox"><table>${invRows}</table></div>
        </td>
      </tr>
    </table>
  `;
  const tfootHtml = `
    <div class="dm-foot-receipt">${footLine} &nbsp;·&nbsp; Printed: ${formatPrintedAt()}</div>
  `;
  return `
    <table class="dm-page-frame">
      <thead><tr><td>${letterhead}</td></tr></thead>
      <tfoot><tr><td>${tfootHtml}</td></tr></tfoot>
      <tbody><tr><td>${body}</td></tr></tbody>
    </table>
  `;
}

// ── Purchase Order — Dot Matrix ─────────────────────────────────────────────
// Previously routed to orderSlipDotMatrix, whose sale-shaped reads produced a
// blank Doc #, blank customer, and mislabeled meta for POs.
function purchaseOrderDotMatrix(data, biz, docCode) {
  const po = data;
  let body = `<table class="dm-addr-band">
    <tr>
      <td class="dm-addr-label">Supplier</td>
      <td class="dm-addr-val">${po.vendor || ''}${po.dr_number ? `<br/>DR #: ${po.dr_number}` : ''}</td>
      <td class="dm-addr-label" style="width:120px;">Payment</td>
      <td class="dm-addr-val">${po.po_type === 'cash' ? 'Cash' : (po.terms_label || 'Terms')}<br/>${po.payment_status || 'Unpaid'}${po.due_date ? `<br/>Due: ${fmtDate(po.due_date)}` : ''}</td>
    </tr>
  </table>`;
  if (po.notes) body += `<div class="dm-warning" style="text-align:left;font-weight:400;">Notes: ${po.notes}</div>`;

  const items = po.items || [];
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th class="c" style="width:0.33cm;padding:1px 0;"></th>`;
  body += `<th style="width:50%;">Item</th>`;
  body += `<th class="c" style="width:12%;">Qty</th>`;
  body += `<th class="r" style="width:18%;">Unit Cost</th>`;
  body += `<th class="r" style="width:18%;">Total</th>`;
  body += `</tr></thead><tbody>`;
  for (const item of items) {
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const total = parseFloat(item.total) || (qty * rate);
    const unit = item.unit || item.uom || '';
    body += `<tr><td class="dm-row-num"></td><td class="strong">${item.product_name || item.description || ''}</td><td class="c">${qty}${unit ? ' ' + unit : ''}</td><td class="r">${formatPHP(rate)}</td><td class="r strong">${formatPHP(total)}</td></tr>`;
  }
  body += `</tbody></table>`;

  body += `<table class="dm-total-box">`;
  body += `<tr><td class="dm-tb-label">Sub-Total</td><td class="dm-tb-val">${formatPHP(po.subtotal || po.line_subtotal)}</td></tr>`;
  if (parseFloat(po.overall_discount_amount) > 0) body += `<tr><td class="dm-tb-label">Discount</td><td class="dm-tb-val">- ${formatPHP(po.overall_discount_amount)}</td></tr>`;
  if (parseFloat(po.freight) > 0) body += `<tr><td class="dm-tb-label">Freight</td><td class="dm-tb-val">${formatPHP(po.freight)}</td></tr>`;
  if (parseFloat(po.tax_amount) > 0) body += `<tr><td class="dm-tb-label">VAT (${po.tax_rate}%)</td><td class="dm-tb-val">${formatPHP(po.tax_amount)}</td></tr>`;
  body += `<tr class="dm-tb-grand"><td class="dm-tb-label">GRAND TOTAL</td><td class="dm-tb-val">${formatPHP(po.grand_total)}</td></tr>`;
  if (parseFloat(po.balance) > 0) body += `<tr class="dm-tb-grand"><td class="dm-tb-label">BALANCE</td><td class="dm-tb-val">${formatPHP(po.balance)}</td></tr>`;
  body += `</table>`;

  body += `<div class="dm-sig-row" style="margin-top:24px;">`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Prepared By</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Received By</div></div>`;
  body += `</div>`;
  body += qrImgTagDM(docCode, { invoice_number: po.po_number, created_at: po.purchase_date, customer_name: po.vendor });
  body += buildFooter('internal', biz, { format: 'dot_matrix' });

  return dmShell({
    biz,
    docTitle: 'PURCHASE ORDER',
    invRows: `<tr><td>PO #</td><td class="dm-inv-val">${po.po_number || ''}</td></tr>
              <tr><td>Date</td><td class="dm-inv-val">${fmtDate(po.purchase_date)}</td></tr>
              ${po.status ? `<tr><td>Status</td><td class="dm-inv-val">${String(po.status).toUpperCase()}</td></tr>` : ''}`,
    footLine: `PO No: ${po.po_number || ''}`,
    body,
  });
}

// ── Expense Voucher — Dot Matrix ────────────────────────────────────────────
function expenseVoucherDotMatrix(data, biz, docCode) {
  const e = data;
  const refNo = e.reference_number || (e.id ? e.id.slice(0, 8) : '');
  let body = `<table class="dm-meta-grid">
    <tr>
      <th style="width:25%;">Category</th>
      <th style="width:25%;">Payment</th>
      <th style="width:25%;">Fund Source</th>
      <th>Amount</th>
    </tr>
    <tr>
      <td>${e.category || 'General'}</td>
      <td>${e.payment_method || 'Cash'}</td>
      <td>${e.fund_source || ''}</td>
      <td class="strong">${formatPHP(e.amount)}</td>
    </tr>
  </table>`;
  if (e.description) body += `<div class="dm-warning" style="text-align:left;font-weight:400;">Description: ${e.description}</div>`;
  if (e.notes) body += `<div class="dm-warning" style="text-align:left;font-weight:400;">Notes: ${e.notes}</div>`;
  body += `<table class="dm-total-box"><tr class="dm-tb-grand"><td class="dm-tb-label">AMOUNT</td><td class="dm-tb-val">${formatPHP(e.amount)}</td></tr></table>`;
  body += `<div class="dm-sig-row" style="margin-top:32px;">`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Approved By</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Received By</div></div>`;
  body += `</div>`;
  body += qrImgTagDM(docCode, { invoice_number: refNo, created_at: e.date || e.created_at });
  body += buildFooter('internal', biz, { format: 'dot_matrix' });

  return dmShell({
    biz,
    docTitle: 'EXPENSE VOUCHER',
    invRows: `<tr><td>Ref #</td><td class="dm-inv-val">${refNo}</td></tr>
              <tr><td>Date</td><td class="dm-inv-val">${fmtDate(e.date || e.created_at)}</td></tr>`,
    footLine: `Voucher No: ${refNo}`,
    body,
  });
}

// ── Expense Voucher — 58mm Thermal ──────────────────────────────────────────
// There was NO thermal expense voucher: the switch fell through to the
// full-page builder, so an expense sent to a 58mm printer came out as full-page
// markup wrapped in thermal CSS. Locally it never bit (the expense window asks
// for full_page, which print() swaps to dot matrix) but generateHtml() does no
// swap, so a remote/OrbitPrint job to a thermal printer hit it.
//
// The NOTE leads this document. On prod, 757 of 1,247 expenses carry a note
// and only 111 carry a description — on Miscellaneous (380 of 396) and Farm
// Expense (290 of 325) the note IS the expense ("load", "snack", "ca treaser").
// The category alone tells nobody what the money was for.
function expenseVoucherThermal(data, biz, docCode) {
  const e = data;
  const refNo = e.reference_number || (e.id ? e.id.slice(0, 8) : '');
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">EXPENSE VOUCHER</div>`;
  html += `<div class="meta-row"><span class="label">Ref No:</span><span>${refNo}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDate(e.date || e.created_at)}</span></div>`;
  html += `<div class="meta-row"><span class="label">Category:</span><span>${e.category || 'General'}</span></div>`;
  if (e.vendor) html += `<div class="meta-row"><span class="label">Paid To:</span><span>${e.vendor}</span></div>`;
  html += `<div class="meta-row"><span class="label">Payment:</span><span>${e.payment_method || 'Cash'}</span></div>`;
  if (e.fund_source) html += `<div class="meta-row"><span class="label">Fund:</span><span>${e.fund_source}</span></div>`;
  if (e.created_by_name) html += `<div class="meta-row"><span class="label">By:</span><span>${e.created_by_name}</span></div>`;
  html += '<div class="sep"></div>';
  if (e.description) {
    html += `<div class="note-block"><span class="note-label">Description:</span> ${e.description}</div>`;
  }
  html += noteBlockThermal(e, 'Notes');
  html += '<div class="totals">';
  html += `<div class="row grand"><span>AMOUNT</span><span>${formatPHP(e.amount)}</span></div>`;
  html += '</div>';
  html += '<div class="signature-line"><div style="margin-top:8px"></div><div class="line"></div><div class="sig-label">Approved By</div></div>';
  html += '<div class="signature-line"><div style="margin-top:8px"></div><div class="line"></div><div class="sig-label">Received By</div></div>';
  if (docCode) html += qrImgTag(docCode, 120);
  html += buildFooter('internal', biz, { format: 'thermal' });
  return html;
}

// ── Cross-Branch Settlement — Dot Matrix (BT Batch 2 P2) ────────────────────
function settlementDotMatrix(data, biz, docCode) {
  const s = data;
  const refNo = s.st_number || (s.id ? s.id.slice(0, 8) : '');
  let body = `<table class="dm-meta-grid">
    <tr>
      <th style="width:28%;">Paying Branch</th>
      <th style="width:28%;">Receiving Branch</th>
      <th style="width:22%;">Mode</th>
      <th>Total Settled</th>
    </tr>
    <tr>
      <td>${s.collecting_branch_name || ''}</td>
      <td>${s.credit_branch_name || ''}</td>
      <td>${s.mode === 'documented' ? 'Bank Transfer (documented)' : 'Internal'}</td>
      <td class="strong">${formatPHP(s.total_amount)}</td>
    </tr>
  </table>`;
  if (s.bank_reference) body += `<div class="dm-warning" style="text-align:left;font-weight:400;">Bank Transaction Ref: <b>${s.bank_reference}</b></div>`;
  if (s.notes) body += `<div class="dm-warning" style="text-align:left;font-weight:400;">Notes: ${s.notes}</div>`;

  const lines = s.lines || [];
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th style="width:22%;">CBP #</th>`;
  body += `<th style="width:38%;">Customer</th>`;
  body += `<th class="c" style="width:20%;">Collected</th>`;
  body += `<th class="r" style="width:20%;">Amount</th>`;
  body += `</tr></thead><tbody>`;
  for (const ln of lines) {
    body += `<tr><td class="strong">${ln.payment_number || ''}</td><td>${ln.customer_name || ''}</td><td class="c">${ln.finalized_at || ''}</td><td class="r strong">${formatPHP(ln.amount)}</td></tr>`;
  }
  body += `</tbody></table>`;
  body += `<table class="dm-total-box"><tr class="dm-tb-grand"><td class="dm-tb-label">TOTAL SETTLED (${lines.length} payment${lines.length !== 1 ? 's' : ''})</td><td class="dm-tb-val">${formatPHP(s.total_amount)}</td></tr></table>`;
  body += `<div class="dm-sig-row" style="margin-top:32px;">`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Settled By${s.settled_by_name ? ` — ${s.settled_by_name}` : ''}</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Received By (${s.credit_branch_name || 'Receiving Branch'})</div></div>`;
  body += `</div>`;
  body += `<div class="dm-warning" style="text-align:left;font-weight:400;">Enter this reference in the internal settlement form together with the bank transaction number and receipt photo.</div>`;
  body += qrImgTagDM(docCode, { invoice_number: refNo, created_at: s.created_at });
  body += buildFooter('internal', biz, { format: 'dot_matrix' });

  return dmShell({
    biz,
    docTitle: 'CROSS-BRANCH SETTLEMENT',
    invRows: `<tr><td>ST #</td><td class="dm-inv-val">${refNo}</td></tr>
              <tr><td>Date</td><td class="dm-inv-val">${fmtDate(s.created_at)}</td></tr>`,
    footLine: `Settlement No: ${refNo}`,
    body,
  });
}

// ── Return Slip — Dot Matrix ────────────────────────────────────────────────
function returnSlipDotMatrix(data, biz, docCode) {
  const r = data;
  const refNo = r.return_number || (r.id ? r.id.slice(0, 8) : '');
  // Always render the customer band — walk-in returns print "Walk-in".
  let body = `<table class="dm-addr-band">
    <tr>
      <td class="dm-addr-label">Customer</td>
      <td class="dm-addr-val">${r.customer_name || 'Walk-in'}</td>
      <td class="dm-addr-label" style="width:120px;">Refund Method</td>
      <td class="dm-addr-val">${r.refund_method || 'Cash'}${r.reason ? `<br/>Reason: ${r.reason}` : ''}</td>
    </tr>
  </table>`;

  const items = r.items || [];
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th class="c" style="width:0.33cm;padding:1px 0;"></th>`;
  body += `<th style="width:50%;">Item</th>`;
  body += `<th class="c" style="width:12%;">Qty</th>`;
  body += `<th class="r" style="width:18%;">Price</th>`;
  body += `<th class="r" style="width:18%;">Total</th>`;
  body += `</tr></thead><tbody>`;
  for (const item of items) {
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const total = parseFloat(item.total) || (qty * rate);
    const unit = item.unit || item.uom || '';
    body += `<tr><td class="dm-row-num"></td><td class="strong">${item.product_name || ''}</td><td class="c">${qty}${unit ? ' ' + unit : ''}</td><td class="r">${formatPHP(rate)}</td><td class="r strong">${formatPHP(total)}</td></tr>`;
  }
  body += `</tbody></table>`;

  body += `<table class="dm-total-box"><tr class="dm-tb-grand"><td class="dm-tb-label">TOTAL REFUND</td><td class="dm-tb-val">${formatPHP(r.refund_amount || r.total_refund || 0)}</td></tr></table>`;
  body += `<div class="dm-sig-row" style="margin-top:24px;">`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Authorized By</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Customer Signature</div></div>`;
  body += `</div>`;
  body += noteBlockDotMatrix(r);
  body += qrImgTagDM(docCode, { invoice_number: refNo, created_at: r.created_at, customer_name: r.customer_name });
  body += buildFooter('internal', biz, { format: 'dot_matrix' });

  return dmShell({
    biz,
    docTitle: 'RETURN SLIP',
    invRows: `<tr><td>Ref #</td><td class="dm-inv-val">${refNo}</td></tr>
              <tr><td>Date</td><td class="dm-inv-val">${fmtDate(r.created_at)}</td></tr>
              ${r.original_invoice_number ? `<tr><td>Orig Inv</td><td class="dm-inv-val">${r.original_invoice_number}</td></tr>` : ''}`,
    footLine: `Return No: ${refNo}`,
    body,
  });
}

// ── Statement of Account — Dot Matrix ───────────────────────────────────────
function statementDotMatrix(data, biz, docCode) {
  const s = data;
  const balanceDue = s.closing_balance || s.balance || 0;
  let body = `<table class="dm-addr-band">
    <tr>
      <td class="dm-addr-label">Customer</td>
      <td class="dm-addr-val">${s.customer_name || ''}${s.customer_phone ? `<br/>Tel: ${s.customer_phone}` : ''}${s.customer_address ? `<br/>${s.customer_address}` : ''}</td>
      <td class="dm-addr-label" style="width:120px;">Balance Due</td>
      <td class="dm-addr-val" style="font-size:16px;">${formatPHP(balanceDue)}</td>
    </tr>
  </table>`;

  if (s.transactions?.length) {
    body += `<table class="dm-items-table"><thead><tr>`;
    body += `<th style="width:14%;">Date</th><th style="width:18%;">Reference</th><th>Description</th>`;
    body += `<th class="r" style="width:14%;">Debit</th><th class="r" style="width:14%;">Credit</th><th class="r" style="width:15%;">Balance</th>`;
    body += `</tr></thead><tbody>`;
    for (const tx of s.transactions) {
      body += `<tr><td>${fmtDate(tx.date)}</td><td>${tx.reference || ''}</td><td>${tx.description || ''}</td>`;
      body += `<td class="r">${tx.debit > 0 ? formatPHP(tx.debit) : ''}</td>`;
      body += `<td class="r">${tx.credit > 0 ? formatPHP(tx.credit) : ''}</td>`;
      body += `<td class="r strong">${formatPHP(tx.running_balance || 0)}</td></tr>`;
    }
    body += `</tbody></table>`;
  }

  body += `<table class="dm-total-box">`;
  if (s.opening_balance !== undefined) body += `<tr><td class="dm-tb-label">Opening</td><td class="dm-tb-val">${formatPHP(s.opening_balance)}</td></tr>`;
  if (s.total_charges !== undefined) body += `<tr><td class="dm-tb-label">Charges</td><td class="dm-tb-val">${formatPHP(s.total_charges)}</td></tr>`;
  if (s.total_payments !== undefined) body += `<tr><td class="dm-tb-label">Payments</td><td class="dm-tb-val">- ${formatPHP(s.total_payments)}</td></tr>`;
  body += `<tr class="dm-tb-grand"><td class="dm-tb-label">BALANCE DUE</td><td class="dm-tb-val">${formatPHP(balanceDue)}</td></tr>`;
  body += `</table>`;
  body += qrImgTagDM(docCode, { customer_name: s.customer_name, created_at: s.statement_date });
  body += buildFooter('internal', biz, { format: 'dot_matrix' });

  return dmShell({
    biz,
    docTitle: 'STATEMENT OF ACCOUNT',
    invRows: `<tr><td>Date</td><td class="dm-inv-val">${fmtDate(s.statement_date || new Date().toISOString())}</td></tr>`,
    footLine: `Statement — ${s.customer_name || ''}`,
    body,
  });
}

// ── Order Slip — Dot Matrix ─────────────────────────────────────────────────
function orderSlipDotMatrix(data, biz, docCode, opts = {}) {
  const inv = data;
  const isDraft = inv.status === 'for_preparation';
  const docTitle = opts.docTitle || (isDraft ? 'PREPARATION COPY' : 'ORDER SLIP');

  // ── 1. Repeated letterhead (sits in <thead>) ──────────────────────────────
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.email)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Email</span>${biz.email}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);

  const letterhead = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
        </td>
        <td class="dm-lh-title-cell" style="width:240px;">
          <div class="dm-lh-title">${docTitle}</div>
          <div class="dm-lh-invbox">
            <table>
              ${opts.birNumber ? `<tr><td>BIR #</td><td class="dm-inv-val">${opts.birNumber}</td></tr>` : ''}
              <tr><td>Doc #</td><td class="dm-inv-val">${inv.invoice_number || ''}</td></tr>
              <tr><td>Date</td><td class="dm-inv-val">${fmtDate(inv.invoice_date || inv.created_at || inv.order_date)}</td></tr>
            </table>
          </div>
        </td>
      </tr>
    </table>
  `;

  // ── 2. Footer (sits in <tfoot>, repeats on every printed page) ────────────
  // Chrome ignores @page margin-box counters, so the printed-at stamp lives
  // HERE (table-footer-group repeats per page) instead of @bottom-left.
  const tfootHtml = `
    <div class="dm-foot-receipt">Doc No: ${inv.invoice_number || ''} &nbsp;·&nbsp; Printed: ${formatPrintedAt()}</div>
  `;

  // ── 3. Body content ────────────────────────────────────────────────────────
  let body = '';

  if (isDraft) {
    body += `<div class="dm-warning" style="font-size:13px;padding:10px 8px;margin-bottom:10px;">
      *** FOR PREPARATION ONLY — NOT YET PAID ***<br/>*** NOT A FINAL RECEIPT ***
    </div>`;
  }

  // Billing / Delivery address band — ALWAYS rendered. Walk-in sales used to
  // drop the whole band (different-looking document, no name/address/phone);
  // now they print "Walk-in" with an over-the-counter delivery note so every
  // sale has the same structure.
  {
    const custName = inv.customer_name || 'Walk-in';
    const isWalkIn = custName === 'Walk-in';
    const shipName = inv.shipping_name || custName;
    const shipAddr = inv.shipping_address || inv.customer_address || '';
    body += `<table class="dm-addr-band">
      <tr>
        <td class="dm-addr-label">Prepared For</td>
        <td class="dm-addr-val">${custName}${inv.customer_address ? `<br/>${inv.customer_address}` : ''}${inv.customer_phone ? `<br/>Tel: ${inv.customer_phone}` : ''}</td>
        <td class="dm-addr-label" style="width:120px;">Deliver To</td>
        <td class="dm-addr-val">${isWalkIn && !shipAddr ? 'Over the counter' : `${shipName}${shipAddr ? `<br/>${shipAddr}` : ''}`}</td>
      </tr>
    </table>`;
  }

  // Sales Rep / Payment Method grid (+ Ref PO when the sale carries one)
  body += `<table class="dm-meta-grid">
    <tr>
      <th style="width:${inv.customer_po ? '34' : '50'}%;">Sales Rep</th>
      <th>Payment Method</th>
      ${inv.customer_po ? '<th style="width:28%;">Ref PO</th>' : ''}
    </tr>
    <tr>
      <td>${inv.cashier_name || ''}</td>
      <td>${inv.payment_method || (isDraft ? 'Pending' : 'Cash')}</td>
      ${inv.customer_po ? `<td>${inv.customer_po}</td>` : ''}
    </tr>
  </table>`;

  if (inv.release_mode === 'full') {
    body += `<div class="dm-warning" style="margin-top:0">** FULLY RELEASED **</div>`;
  } else if (inv.release_mode === 'partial') {
    body += `<div class="dm-warning">** PARTIAL RELEASE — SCAN QR CODE BELOW TO RELEASE ITEMS **</div>`;
  }

  // Items table — Trust Agreement style with Description column.
  // Discount column = the PER-UNIT discount (matching the entry screen and the
  // invoice modals' "Disc/Unit"); the net Sub-Total still reflects the whole
  // line. lineDiscOf() (total) is kept only for the net-total fallback math.
  const items = inv.items || [];
  const lineDiscOf = (i) => {
    const amt = parseFloat(i.discount_amount) || 0;
    if (amt > 0) return amt;
    const val = parseFloat(i.discount_value) || 0;
    if (val <= 0) return 0;
    const q = parseFloat(i.quantity) || 0;
    const r = parseFloat(i.rate || i.unit_price || i.price) || 0;
    return i.discount_type === 'percent' ? (q * r * val) / 100 : val * q;
  };
  const hasDiscount = items.some(i => lineDiscOf(i) > 0);
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th class="c" style="width:0.33cm;padding:1px 0;"></th>`;
  body += `<th style="width:${hasDiscount ? '31' : '39'}%;">Item</th>`;
  body += `<th style="width:${hasDiscount ? '17' : '21'}%;">Description</th>`;
  body += `<th class="c" style="width:10%;">Qty</th>`;
  body += `<th class="r" style="width:14%;">Unit Price</th>`;
  if (hasDiscount) {
    body += `<th class="r" style="width:12%;">Disc/Unit</th>`;
  }
  body += `<th class="r" style="width:16%;">Sub-Total</th>`;
  body += `</tr></thead><tbody>`;

  for (const item of items) {
    const qty   = parseFloat(item.quantity) || 0;
    const rate  = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const disc  = lineDiscOf(item);
    const total = (item.total !== undefined && item.total !== null && item.total !== '')
      ? parseFloat(item.total)
      : (qty * rate - disc);
    const unit  = item.unit || item.uom || '';
    // A column headed "Description" must not print the CATEGORY. The old
    // fallback made a blank description read as "Fertilizers" — a control
    // that lies. Empty is honest; lineDescription() also drops a
    // description that merely repeats the product name.
    const desc  = lineDescription(item);
    const pu    = perUnitDiscText(item);
    body += `<tr>`;
    body += `<td class="dm-row-num"></td>`;
    body += `<td class="strong">${item.product_name || ''}</td>`;
    body += `<td>${desc}</td>`;
    body += `<td class="c">${qty}${unit ? ' ' + unit : ''}</td>`;
    body += `<td class="r">${formatPHP(rate)}</td>`;
    if (hasDiscount) {
      body += `<td class="r">${pu ? '- ' + pu : '—'}</td>`;
    }
    body += `<td class="r strong">${formatPHP(total)}</td>`;
    body += `</tr>`;
  }
  body += `</tbody></table>`;

  // Right-aligned totals box (Trust Agreement style)
  body += `<table class="dm-total-box">`;
  body += `<tr><td class="dm-tb-label">Sub-Total</td><td class="dm-tb-val">${formatPHP(inv.subtotal)}</td></tr>`;
  if (parseFloat(inv.overall_discount) > 0) {
    body += `<tr><td class="dm-tb-label">Discount</td><td class="dm-tb-val">- ${formatPHP(inv.overall_discount)}</td></tr>`;
  }
  if (parseFloat(inv.freight) > 0) {
    body += `<tr><td class="dm-tb-label">Freight</td><td class="dm-tb-val">${formatPHP(inv.freight)}</td></tr>`;
  }
  body += scpwdRowsHTML(inv, 'dm');
  body += `<tr class="dm-tb-grand"><td class="dm-tb-label">GRAND TOTAL</td><td class="dm-tb-val">${formatPHP(inv.grand_total)}</td></tr>`;
  if (!isDraft) {
    const _tendered = parseFloat(inv.tendered_amount) || 0;
    const _change = inv.change_amount != null ? parseFloat(inv.change_amount) || 0
      : Math.max(0, _tendered - (parseFloat(inv.grand_total) || 0));
    if (_tendered > 0 && inv.payment_type !== 'credit') {
      body += `<tr><td class="dm-tb-label">Tendered</td><td class="dm-tb-val">${formatPHP(_tendered)}</td></tr>`;
      if (_change > 0) {
        body += `<tr><td class="dm-tb-label">Change</td><td class="dm-tb-val">${formatPHP(_change)}</td></tr>`;
      }
    }
    for (const ap of addonTenderPairs(inv)) {
      body += `<tr><td class="dm-tb-label">Add-on Tendered</td><td class="dm-tb-val">${formatPHP(ap.tendered)}</td></tr>`;
      if (ap.change > 0) body += `<tr><td class="dm-tb-label">Add-on Change</td><td class="dm-tb-val">${formatPHP(ap.change)}</td></tr>`;
    }
    if (inv.payment_type === 'credit' || parseFloat(inv.balance) > 0) {
      // Never fall back to grand_total — a partially-paid credit sale with a
      // missing balance field used to print the FULL total as balance due.
      const _balanceDue = inv.balance != null
        ? parseFloat(inv.balance) || 0
        : Math.max(0, (parseFloat(inv.grand_total) || 0) - (parseFloat(inv.amount_paid) || 0));
      body += `<tr class="dm-tb-grand"><td class="dm-tb-label">BALANCE DUE</td><td class="dm-tb-val">${formatPHP(_balanceDue)}</td></tr>`;
    }
  }
  body += `</table>`;
  body += scpwdCardHTML(inv, 'dm');

  // QR Code
  body += qrImgTagDM(docCode, inv);

  // Acknowledgment + signature (Trust Agreement style)
  const today = fmtDate(new Date().toISOString());
  body += noteBlockDotMatrix(data);
  body += `<div style="border-top:1px solid #000;margin-top:20px;padding-top:14px;">`;
  if (isDraft) {
    body += `<p style="font-size:11px;margin-bottom:20px;line-height:1.5;">This is a preparation copy. Items listed are to be prepared for the customer. Payment and final receipt will be issued upon completion.</p>`;
  } else {
    body += `<p style="font-size:11px;margin-bottom:24px;line-height:1.5;">I acknowledge receipt of the items listed above in good physical condition and complete.</p>`;
  }
  body += `<div class="dm-sig-row">`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Customer Signature</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Printed Name</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line" style="display:flex;align-items:flex-end;justify-content:center;padding-bottom:2px;font-size:12px">${today}</div><div class="dm-sig-label">Date</div></div>`;
  body += `</div></div>`;

  // Footer
  if (isDraft) {
    body += `<div class="dm-footer"><div class="dm-thankyou" style="font-size:13px;">FOR PREPARATION ONLY — NOT A FINAL RECEIPT</div></div>`;
  } else {
    body += `<div class="dm-footer"><div class="dm-thankyou">Thank you for your business!</div></div>`;
  }
  body += buildFooter(opts.footerKind || 'order_slip', biz, { format: 'dot_matrix', isDraft });

  // ── 4. Compose page-frame table ───────────────────────────────────────────
  return `
    <table class="dm-page-frame">
      <thead><tr><td>${letterhead}</td></tr></thead>
      <tfoot><tr><td>${tfootHtml}</td></tr></tfoot>
      <tbody><tr><td>${body}</td></tr></tbody>
    </table>
  `;
}

// ── Packing Slip — Dot Matrix ───────────────────────────────────────────────
// Price-free trucker/delivery copy (owner ask 2026-08-30): truckers haul
// several customers' orders at once and must never see anyone's pricing.
// Items + quantities only — no unit price, no discounts, no totals box, no
// payment block, and deliberately NO QR: the /doc/{code} digital receipt
// shows prices, so a QR here would leak exactly what this document hides.
function packingSlipDotMatrix(data, biz) {
  const inv = data;

  // Letterhead (same skeleton as orderSlipDotMatrix, title = PACKING SLIP)
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.email)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Email</span>${biz.email}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);

  const letterhead = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
        </td>
        <td class="dm-lh-title-cell" style="width:240px;">
          <div class="dm-lh-title">PACKING SLIP</div>
          <div class="dm-lh-invbox">
            <table>
              <tr><td>Doc #</td><td class="dm-inv-val">${inv.invoice_number || ''}</td></tr>
              <tr><td>Date</td><td class="dm-inv-val">${fmtDate(inv.invoice_date || inv.created_at || inv.order_date)}</td></tr>
            </table>
          </div>
        </td>
      </tr>
    </table>
  `;

  const tfootHtml = `
    <div class="dm-foot-receipt">Doc No: ${inv.invoice_number || ''} &nbsp;·&nbsp; Packing copy &nbsp;·&nbsp; Printed: ${formatPrintedAt()}</div>
  `;

  let body = '';

  // Prepared For / Deliver To — the trucker must know whose load is whose;
  // identity stays, money goes.
  {
    const custName = inv.customer_name || 'Walk-in';
    const isWalkIn = custName === 'Walk-in';
    const shipName = inv.shipping_name || custName;
    const shipAddr = inv.shipping_address || inv.customer_address || '';
    body += `<table class="dm-addr-band">
      <tr>
        <td class="dm-addr-label">Prepared For</td>
        <td class="dm-addr-val">${custName}${inv.customer_address ? `<br/>${inv.customer_address}` : ''}${inv.customer_phone ? `<br/>Tel: ${inv.customer_phone}` : ''}</td>
        <td class="dm-addr-label" style="width:120px;">Deliver To</td>
        <td class="dm-addr-val">${isWalkIn && !shipAddr ? 'Over the counter' : `${shipName}${shipAddr ? `<br/>${shipAddr}` : ''}`}</td>
      </tr>
    </table>`;
  }

  // Prepared By / Hauler (blank write-in) / Ref doc
  body += `<table class="dm-meta-grid">
    <tr>
      <th style="width:34%;">Prepared By</th>
      <th>Hauler / Trucker</th>
      <th style="width:28%;">Ref Order Slip</th>
    </tr>
    <tr>
      <td>${inv.cashier_name || ''}</td>
      <td>&nbsp;</td>
      <td>${inv.invoice_number || ''}</td>
    </tr>
  </table>`;

  // Items — no price columns; "Loaded" checkbox per line for the trucker.
  const items = inv.items || [];
  const checkbox = `<span style="display:inline-block;width:12px;height:12px;border:1px solid #000;"></span>`;
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th class="c" style="width:0.33cm;padding:1px 0;">#</th>`;
  body += `<th class="c" style="width:12%;">Qty</th>`;
  body += `<th class="c" style="width:12%;">Unit</th>`;
  body += `<th style="width:34%;">Item</th>`;
  body += `<th>Description / Remarks</th>`;
  body += `<th class="c" style="width:12%;">Loaded</th>`;
  body += `</tr></thead><tbody>`;

  items.forEach((item, idx) => {
    const qty = parseFloat(item.quantity) || 0;
    const unit = lineUnit(item);
    body += `<tr>`;
    body += `<td class="dm-row-num">${idx + 1}</td>`;
    body += `<td class="c strong">${fmtQty(qty)}</td>`;
    body += `<td class="c">${unit}</td>`;
    body += `<td class="strong">${item.product_name || ''}</td>`;
    body += `<td>${lineDescription(item)}</td>`;
    body += `<td class="c">${checkbox}</td>`;
    body += `</tr>`;
  });
  body += `</tbody></table>`;

  // Per-category unit summary (replaces the money totals box, and the old
  // "TOTAL PIECES" that added bags and liters into one number — 2026-09-29).
  body += categorySummaryDotMatrixHTML(items);

  body += `<div class="dm-warning" style="margin-top:10px;">*** PACKING COPY — NO PRICES ***<br/><span style="font-weight:normal;">Not valid as a receipt or invoice. For loading, hauling and delivery checking only.</span></div>`;

  // Three-way signature band: counter → truck → customer
  body += `<div style="border-top:1px solid #000;margin-top:20px;padding-top:14px;">`;
  body += `<div class="dm-sig-row">`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Prepared By</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Loaded By (Trucker)</div></div>`;
  body += `<div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Received By (Customer)</div></div>`;
  body += `</div></div>`;

  body += buildFooter('packing_slip', biz, { format: 'dot_matrix' });

  return `
    <table class="dm-page-frame">
      <thead><tr><td>${letterhead}</td></tr></thead>
      <tfoot><tr><td>${tfootHtml}</td></tr></tfoot>
      <tbody><tr><td>${body}</td></tr></tbody>
    </table>
  `;
}

// ── Charge Agreement (Credit / Partial / Cash) — Dot Matrix ───────────────
// Layout matches the Trust Agreement reference: letterhead with logo, company
// info, doc title and invoice/date box repeats on every printed page; items
// table flows across pages; totals box + signature + terms render after the
// last item; receipt number + "Page X of Y" appear in the footer of every page.
function trustReceiptDotMatrix(data, biz, docCode) {
  const inv = data;
  const docTitle = (inv.payment_type === 'cash' || inv.payment_type === 'digital' || inv.payment_type === 'split')
    ? 'SALES INVOICE'
    : 'TRUST AGREEMENT';

  // ── 1. Repeated letterhead (sits in <thead>) ──────────────────────────────
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.email)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Email</span>${biz.email}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);

  const letterhead = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
        </td>
        <td class="dm-lh-title-cell" style="width:240px;">
          <div class="dm-lh-title">${docTitle}</div>
          <div class="dm-lh-invbox">
            <table>
              <tr><td>Invoice #</td><td class="dm-inv-val">${inv.invoice_number || ''}</td></tr>
              <tr><td>Date</td><td class="dm-inv-val">${fmtDate(inv.invoice_date || inv.created_at || inv.order_date)}</td></tr>
            </table>
          </div>
        </td>
      </tr>
    </table>
  `;

  // ── 2. Footer fallback (sits in <tfoot>, repeats on every printed page) ───
  //   Chrome ignores @page margin-box counters, so the printed-at stamp lives
  //   here instead of the (silently dropped) @bottom-left margin box.
  const tfootHtml = `
    <div class="dm-foot-receipt">Receipt No: ${inv.invoice_number || ''} &nbsp;·&nbsp; Printed: ${formatPrintedAt()}</div>
  `;

  // ── 3. Body content (first-page-only billing + meta grid + items + totals) ─
  let body = '';

  // First-page-only: Billing / Shipping address band — ALWAYS rendered
  // (walk-in used to drop the whole band = structurally different document;
  // now prints "Walk-in"). Phone was missing here vs order slip / full page.
  {
    const custName = inv.customer_name || 'Walk-in';
    const isWalkIn = custName === 'Walk-in';
    const shipName = inv.shipping_name || custName;
    const shipAddr = inv.shipping_address || inv.customer_address || '';
    body += `<table class="dm-addr-band">
      <tr>
        <td class="dm-addr-label">Billing Address</td>
        <td class="dm-addr-val">${custName}${inv.customer_address ? `<br/>${inv.customer_address}` : ''}${inv.customer_phone ? `<br/>Tel: ${inv.customer_phone}` : ''}</td>
        <td class="dm-addr-label" style="width:120px;">Shipping Address</td>
        <td class="dm-addr-val">${isWalkIn && !shipAddr ? 'Over the counter' : `${shipName}${shipAddr ? `<br/>${shipAddr}` : ''}`}</td>
      </tr>
    </table>`;
  }

  // First-page-only: Sales Rep / Payment Terms grid
  // Show both the term label (e.g. "Net 30") AND the computed due date so the
  // customer can see exactly when payment is expected — not just the policy name.
  const paymentTermsCell = (() => {
    const termLabel = inv.terms && inv.terms !== 'COD' ? inv.terms : '';
    const duePart   = inv.due_date ? `Due: ${fmtDate(inv.due_date)}` : '';
    if (termLabel && duePart) return `${termLabel} &nbsp;·&nbsp; ${duePart}`;
    return termLabel || duePart || 'Due on receipt';
  })();
  body += `<table class="dm-meta-grid">
    <tr>
      <th style="width:${inv.customer_po ? '34' : '50'}%;">Sales Rep</th>
      <th>Payment Terms</th>
      ${inv.customer_po ? '<th style="width:28%;">Ref PO</th>' : ''}
    </tr>
    <tr>
      <td>${inv.cashier_name || ''}</td>
      <td>${paymentTermsCell}</td>
      ${inv.customer_po ? `<td>${inv.customer_po}</td>` : ''}
    </tr>
  </table>`;

  if (inv.release_mode === 'partial') {
    body += `<div class="dm-warning">** PARTIAL RELEASE — SCAN QR CODE BELOW TO RELEASE ITEMS **</div>`;
  }

  // Items table — column structure mirrors the reference. Discount column =
  // the LINE discount amount with a % note for percent discounts (was an
  // ambiguous per-unit figure that also missed discount_value-only lines).
  const items = inv.items || [];
  const lineDiscOf = (i) => {
    const amt = parseFloat(i.discount_amount) || 0;
    if (amt > 0) return amt;
    const val = parseFloat(i.discount_value) || 0;
    if (val <= 0) return 0;
    const q = parseFloat(i.quantity) || 0;
    const r = parseFloat(i.rate || i.unit_price || i.price) || 0;
    return i.discount_type === 'percent' ? (q * r * val) / 100 : val * q;
  };
  const hasDiscount = items.some(i => lineDiscOf(i) > 0);
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th class="c" style="width:0.33cm;padding:1px 0;"></th>`;
  // Item gets 65% of the (Item+Description) combined width, since the
  // Description column is often empty. Splits:
  //   No discount: 39% / 21% (item/desc) + 10/14/16 (qty/price/total) = 100%
  //   With disc  : 31% / 17%             + 10/14/12/16              = 100%
  body += `<th style="width:${hasDiscount ? '31' : '39'}%;">Item</th>`;
  body += `<th style="width:${hasDiscount ? '17' : '21'}%;">Description</th>`;
  body += `<th class="c" style="width:10%;">Qty</th>`;
  body += `<th class="r" style="width:14%;">Unit Price</th>`;
  if (hasDiscount) {
    body += `<th class="r" style="width:12%;">Disc/Unit</th>`;
  }
  body += `<th class="r" style="width:16%;">Sub-Total</th>`;
  body += `</tr></thead><tbody>`;

  for (const item of items) {
    const qty   = parseFloat(item.quantity) || 0;
    const rate  = parseFloat(item.rate || item.unit_price || item.price) || 0;
    const disc  = lineDiscOf(item);
    const total = (item.total !== undefined && item.total !== null && item.total !== '')
      ? parseFloat(item.total)
      : (qty * rate - disc);
    const unit  = item.unit || item.uom || '';
    // A column headed "Description" must not print the CATEGORY. The old
    // fallback made a blank description read as "Fertilizers" — a control
    // that lies. Empty is honest; lineDescription() also drops a
    // description that merely repeats the product name.
    const desc  = lineDescription(item);
    const pu    = perUnitDiscText(item);
    body += `<tr>`;
    body += `<td class="dm-row-num"></td>`;
    body += `<td class="strong">${item.product_name || ''}</td>`;
    body += `<td>${desc}</td>`;
    body += `<td class="c">${qty}${unit ? ' ' + unit : ''}</td>`;
    body += `<td class="r">${formatPHP(rate)}</td>`;
    if (hasDiscount) {
      body += `<td class="r">${pu ? '- ' + pu : '—'}</td>`;
    }
    body += `<td class="r strong">${formatPHP(total)}</td>`;
    body += `</tr>`;
  }
  body += `</tbody></table>`;

  // Right-aligned totals box
  body += `<table class="dm-total-box">`;
  body += `<tr><td class="dm-tb-label">Sub-Total</td><td class="dm-tb-val">${formatPHP(inv.subtotal)}</td></tr>`;
  if (parseFloat(inv.overall_discount) > 0) {
    body += `<tr><td class="dm-tb-label">Discount</td><td class="dm-tb-val">- ${formatPHP(inv.overall_discount)}</td></tr>`;
  }
  if (parseFloat(inv.freight) > 0) {
    body += `<tr><td class="dm-tb-label">Freight</td><td class="dm-tb-val">${formatPHP(inv.freight)}</td></tr>`;
  }
  // SC/PWD statutory breakdown (RA 9994 / RA 10754) — was only on the DM
  // order slip; charge agreements dropped it entirely.
  body += scpwdRowsHTML(inv, 'dm');
  body += `<tr class="dm-tb-grand"><td class="dm-tb-label">Total</td><td class="dm-tb-val">${formatPHP(inv.grand_total)}</td></tr>`;
  // Tendered / Change for dot-matrix totals-box variant.
  const _tendered_c = parseFloat(inv.tendered_amount) || 0;
  const _change_c = inv.change_amount != null ? parseFloat(inv.change_amount) || 0
    : Math.max(0, _tendered_c - (parseFloat(inv.grand_total) || 0));
  if (_tendered_c > 0 && inv.payment_type !== 'credit') {
    body += `<tr><td class="dm-tb-label">Tendered</td><td class="dm-tb-val">${formatPHP(_tendered_c)}</td></tr>`;
    if (_change_c > 0) {
      body += `<tr><td class="dm-tb-label">Change</td><td class="dm-tb-val">${formatPHP(_change_c)}</td></tr>`;
    }
  } else if (parseFloat(inv.amount_paid) > 0) {
    body += `<tr><td class="dm-tb-label">Amount Paid</td><td class="dm-tb-val">${formatPHP(inv.amount_paid)}</td></tr>`;
  }
  for (const ap of addonTenderPairs(inv)) {
    body += `<tr><td class="dm-tb-label">Add-on Tendered</td><td class="dm-tb-val">${formatPHP(ap.tendered)}</td></tr>`;
    if (ap.change > 0) body += `<tr><td class="dm-tb-label">Add-on Change</td><td class="dm-tb-val">${formatPHP(ap.change)}</td></tr>`;
  }
  if (parseFloat(inv.balance) > 0) {
    body += `<tr class="dm-tb-grand"><td class="dm-tb-label">Balance Due</td><td class="dm-tb-val">${formatPHP(inv.balance)}</td></tr>`;
  }
  body += `</table>`;
  // SC/PWD cardholder identity block (statutory) — parity with the order slip.
  body += scpwdCardHTML(inv, 'dm');
  body += noteBlockDotMatrix(inv);

  // AUTHORIZED REPRESENTATIVE — centered signature line
  body += `<div class="dm-auth-sig">
    <div class="dm-sig-line-c"></div>
    <div class="dm-sig-label-c">Authorized Representative</div>
  </div>`;

  // Customer signature (if captured) — sits below the auth signature
  const sigStampDM = (() => {
    const t  = data.signature_verification_token || '';
    const sa = data.signature_signed_at || '';
    if (!t && !sa) return '';
    const dt    = sa ? new Date(sa) : null;
    const dtStr = dt ? dt.toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : '';
    return `<div style="font-size:9px;text-align:center;margin-top:3px;">Signed ${dtStr}${t ? ` - v.${t}` : ''}</div>`;
  })();
  if (data.signature_url) {
    body += `<div class="dm-auth-sig" style="margin-top:16px;">
      <img src="${data.signature_url}" alt="signature" style="max-width:300px;max-height:60px;display:block;margin:0 auto 2px;object-fit:contain"/>
      <div class="dm-sig-line-c"></div>
      <div class="dm-sig-label-c">Customer Signature</div>
      ${sigStampDM}
    </div>`;
  } else if (data.bypass_method) {
    body += `<div class="dm-auth-sig" style="margin-top:16px;">
      <div style="margin:8px auto;font-size:10px;border:1px solid #000;padding:4px 8px;display:inline-block;">AUTHORIZED VIA MANAGER PIN</div>
      <div class="dm-sig-label-c">Customer (Bypassed)</div>
      ${sigStampDM}
    </div>`;
  }

  // Terms & Conditions (charge agreement boilerplate)
  const termsTxt = (biz.trust_receipt_terms || '').replace('{business_name}', biz.business_name || 'this business');
  if (termsTxt) {
    body += `<div class="dm-terms-block">
      <div class="dm-terms-head">Terms and Condition</div>
      <div>${termsTxt}</div>
      <div class="dm-terms-copy-key">${getCopyColorGuide(biz)}</div>
    </div>`;
  }
  body += buildFooter('trust_receipt', biz, { format: 'dot_matrix' });

  // Compact QR strip — consistent placement at the bottom-left,
  // below the Terms block. Same data as the legacy `qrImgTagDM` but
  // ~40% smaller footprint so the page stays dense.
  if (docCode) {
    const url = `${window.location.origin}/doc/${docCode}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(url)}&margin=2&color=000000&bgcolor=ffffff`;
    body += `
      <div class="dm-qr-strip">
        <img src="${qrUrl}" alt="QR Code" width="70" height="70" />
        <div class="dm-qr-strip-info">
          <div class="dm-qr-code">${docCode}</div>
          <div class="dm-qr-scan">Scan to view document</div>
          ${inv.invoice_number ? `<div class="dm-qr-sub">Transaction No: ${inv.invoice_number}</div>` : ''}
          ${inv.invoice_date || inv.created_at || inv.order_date ? `<div class="dm-qr-sub">Date: ${fmtDate(inv.invoice_date || inv.created_at || inv.order_date)}</div>` : ''}
          ${inv.customer_name ? `<div class="dm-qr-sub">Customer: ${inv.customer_name}</div>` : ''}
        </div>
      </div>`;
  }

  // ── 4. Compose the page-frame table (thead+tbody+tfoot) ───────────────────
  return `
    <table class="dm-page-frame">
      <thead><tr><td>${letterhead}</td></tr></thead>
      <tfoot><tr><td>${tfootHtml}</td></tr></tfoot>
      <tbody><tr><td>${body}</td></tr></tbody>
    </table>
  `;
}

// ═══════════════════════════════════════════════════════════════════════════
//  THERMAL DOCUMENTS (keep existing for 58mm printers)
// ═══════════════════════════════════════════════════════════════════════════

function orderSlipThermal(data, biz, docCode) {
  const inv = data;
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">ORDER SLIP</div>`;
  html += `<div class="meta-row"><span class="label">No:</span><span>${inv.invoice_number || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDateMaybeTime(inv.invoice_date || inv.created_at || inv.order_date)}</span></div>`;
  if (inv.customer_name && inv.customer_name !== 'Walk-in') html += `<div class="meta-row"><span class="label">Customer:</span><span>${inv.customer_name}</span></div>`;
  if (inv.customer_po) html += `<div class="meta-row"><span class="label">Ref PO:</span><span>${inv.customer_po}</span></div>`;
  html += `<div class="meta-row"><span class="label">Cashier:</span><span>${inv.cashier_name || ''}</span></div>`;
  // Stock release status
  if (inv.release_mode === 'full') {
    html += `<div class="meta-row"><span class="label">Status:</span><span style="font-weight:bold;color:#000">FULLY RELEASED</span></div>`;
  } else if (inv.release_mode === 'partial') {
    html += `<div class="meta-row" style="background:#fef3c7;padding:4px 2px;margin:2px 0;border:1px solid #f59e0b"><span style="font-size:10px;font-weight:bold;color:#92400e">PARTIAL RELEASE - SCAN QR CODE TO RELEASE ITEMS</span></div>`;
  }
  html += '<div class="sep"></div>';
  html += buildItemsThermal(inv.items || []);
  html += '<div class="sep"></div>';
  html += '<div class="totals">';
  html += `<div class="row"><span>Subtotal</span><span>${formatPHP(inv.subtotal)}</span></div>`;
  if (inv.overall_discount > 0) html += `<div class="row"><span>Discount</span><span>-${formatPHP(inv.overall_discount)}</span></div>`;
  html += scpwdRowsHTML(inv, 'th');
  html += `<div class="row grand"><span>TOTAL</span><span>${formatPHP(inv.grand_total)}</span></div>`;
  html += '</div>';
  html += scpwdCardHTML(inv, 'th');
  html += '<div class="payment-info">';
  html += `<div class="meta-row"><span class="label">Payment:</span><span>${inv.payment_method || 'Cash'}</span></div>`;
  // Tendered / Change for thermal roll receipt.
  const _tendered_d = parseFloat(inv.tendered_amount) || 0;
  const _change_d = inv.change_amount != null ? parseFloat(inv.change_amount) || 0
    : Math.max(0, _tendered_d - (parseFloat(inv.grand_total) || 0));
  if (_tendered_d > 0 && inv.payment_type !== 'credit') {
    html += `<div class="meta-row"><span class="label">Tendered:</span><span>${formatPHP(_tendered_d)}</span></div>`;
    if (_change_d > 0) html += `<div class="meta-row"><span class="label">Change:</span><span>${formatPHP(_change_d)}</span></div>`;
  } else if (inv.amount_paid > 0 && inv.payment_type !== 'credit') {
    html += `<div class="meta-row"><span class="label">Paid:</span><span>${formatPHP(inv.amount_paid)}</span></div>`;
  }
  for (const ap of addonTenderPairs(inv)) {
    html += `<div class="meta-row"><span class="label">Add-on Tendered:</span><span>${formatPHP(ap.tendered)}</span></div>`;
    if (ap.change > 0) html += `<div class="meta-row"><span class="label">Add-on Change:</span><span>${formatPHP(ap.change)}</span></div>`;
  }
  html += '</div>';
  // Per-category unit summary (owner 2026-09-29): after the money, before the QR.
  html += categorySummaryThermalHTML(inv.items);
  html += noteBlockThermal(inv);
  if (docCode) html += qrImgTag(docCode, 120);
  html += buildFooter('order_slip', biz, { format: 'thermal', isDraft: inv.status === 'for_preparation' });
  return html;
}

// ── Packing Slip — 58mm thermal ─────────────────────────────────────────────
// Price-free trucker/delivery copy. Keeps the receipt's two-row item skeleton
// (name line, then qty line) with the price/line-total side left BLANK — that
// empty strip is deliberate: staff tally counts and write per-item notes there
// (owner ask 2026-08-30). No QR (the /doc page shows prices).
function packingSlipThermal(data, biz) {
  const inv = data;
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">PACKING SLIP</div>`;
  html += `<div class="meta-row"><span class="label">No:</span><span>${inv.invoice_number || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDateMaybeTime(inv.invoice_date || inv.created_at || inv.order_date)}</span></div>`;
  html += `<div class="meta-row"><span class="label">Customer:</span><span>${inv.customer_name || 'Walk-in'}</span></div>`;
  {
    const shipAddr = inv.shipping_address || inv.customer_address || '';
    const shipName = inv.shipping_name || '';
    if (shipAddr || shipName) {
      html += `<div class="meta-row"><span class="label">Deliver To:</span><span>${[shipName, shipAddr].filter(Boolean).join(' — ')}</span></div>`;
    }
  }
  html += `<div class="meta-row"><span class="label">Prepared By:</span><span>${inv.cashier_name || ''}</span></div>`;
  html += '<div class="sep"></div>';

  // Items — two-row skeleton like buildItemsThermal, prices omitted. The empty
  // right cell keeps the row's width/height so every item has a write-in strip.
  const items = inv.items || [];
  html += '<table class="items-table">';
  for (const item of items) {
    const qty = parseFloat(item.quantity || item.qty) || 0;
    html += `<tr><td class="item-name" colspan="2">${item.product_name || item.description || ''}</td></tr>`;
    html += `<tr><td class="item-detail" style="font-weight:bold">${qtyWithUnit(qty, item)}</td><td class="item-total">&nbsp;</td></tr>`;
  }
  html += '</table>';

  // Per-category unit summary replaces "TOTAL PIECES", which added bags,
  // liters and bottles into one meaningless number (owner 2026-09-29).
  html += categorySummaryThermalHTML(items);

  html += `<div style="border:1px solid #000;margin-top:6px;padding:4px 5px;text-align:center;font-weight:bold;font-size:10px;">** PACKING COPY — NO PRICES **<br/><span style="font-weight:normal;font-size:9px;">Not a receipt or invoice</span></div>`;

  html += `<div class="signature-line"><div class="line"></div><div class="sig-label">Received by (signature over printed name)</div></div>`;

  html += buildFooter('packing_slip', biz, { format: 'thermal' });
  return html;
}

function trustReceiptThermal(data, biz, docCode) {
  const inv = data;
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">CHARGE AGREEMENT</div>`;
  html += `<div class="meta-row"><span class="label">No:</span><span>${inv.invoice_number || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDateMaybeTime(inv.invoice_date || inv.created_at || inv.order_date)}</span></div>`;
  html += `<div class="meta-row"><span class="label">Customer:</span><span>${inv.customer_name || ''}</span></div>`;
  if (inv.customer_po) html += `<div class="meta-row"><span class="label">Ref PO:</span><span>${inv.customer_po}</span></div>`;
  if (inv.due_date) html += `<div class="meta-row"><span class="label">Due:</span><span>${fmtDate(inv.due_date)}</span></div>`;
  // Stock release status
  if (inv.release_mode === 'full') {
    html += `<div class="meta-row"><span class="label">Status:</span><span style="font-weight:bold;color:#000">FULLY RELEASED</span></div>`;
  } else if (inv.release_mode === 'partial') {
    html += `<div class="meta-row" style="background:#fef3c7;padding:4px 2px;margin:2px 0;border:1px solid #f59e0b"><span style="font-size:10px;font-weight:bold;color:#92400e">PARTIAL RELEASE - SCAN QR CODE TO RELEASE ITEMS</span></div>`;
  }
  html += '<div class="sep"></div>';
  html += buildItemsThermal(inv.items || []);
  html += '<div class="sep"></div>';
  html += '<div class="totals">';
  html += `<div class="row"><span>Subtotal</span><span>${formatPHP(inv.subtotal)}</span></div>`;
  html += `<div class="row grand"><span>TOTAL</span><span>${formatPHP(inv.grand_total)}</span></div>`;
  if (inv.amount_paid > 0) html += `<div class="row"><span>Paid</span><span>${formatPHP(inv.amount_paid)}</span></div>`;
  if (inv.balance > 0) html += `<div class="row" style="font-weight:bold"><span>BALANCE</span><span>${formatPHP(inv.balance)}</span></div>`;
  html += '</div>';
  // Per-category unit summary — after the money, before the terms + signature.
  html += categorySummaryThermalHTML(inv.items);
  const terms = (biz.trust_receipt_terms || '').replace('{business_name}', biz.business_name || '');
  if (terms) html += `<div class="trust-terms"><div class="terms-title">TERMS</div>${terms}</div>`;
  // Signature block — render embedded signature image if present
  // Tamper-evident stamp below: signed-at + verification token
  const sigStampThermal = (() => {
    const t = inv.signature_verification_token || '';
    const sa = inv.signature_signed_at || '';
    if (!t && !sa) return '';
    const dt = sa ? new Date(sa) : null;
    const dtStr = dt ? dt.toISOString().slice(0, 16).replace('T', ' ') + 'Z' : '';
    return `<div style="text-align:center;font-size:8px;color:#000;margin-top:2px">Signed ${dtStr}${t ? ` &middot; v.${t}` : ''}</div>`;
  })();
  if (inv.signature_url) {
    html += `<div class="signature-line"><img src="${inv.signature_url}" alt="signature" style="max-width:60%;max-height:50px;margin:6px auto 0;display:block;object-fit:contain"/><div class="line"></div><div class="sig-label">Customer Signature</div>${sigStampThermal}</div>`;
  } else if (inv.bypass_method) {
    html += `<div class="signature-line"><div style="margin-top:6px;font-size:9px;color:#000;border:1px solid #000;padding:3px 6px;display:inline-block">AUTHORIZED VIA MANAGER PIN</div>${sigStampThermal}</div>`;
  } else {
    html += '<div class="signature-line"><div style="margin-top:8px"></div><div class="line"></div><div class="sig-label">Customer Signature &amp; Printed Name</div></div>';
  }
  html += noteBlockThermal(data);
  if (docCode) html += qrImgTag(docCode, 120);
  html += buildFooter('trust_receipt', biz, { format: 'thermal' });
  return html;
}

function returnSlipThermal(data, biz, docCode) {
  const r = data;
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">RETURN SLIP</div>`;
  html += `<div class="meta-row"><span class="label">Ref:</span><span>${r.return_number || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDateTime(r.created_at)}</span></div>`;
  html += `<div class="meta-row"><span class="label">Orig:</span><span>${r.original_invoice_number || ''}</span></div>`;
  html += '<div class="sep"></div>';
  html += buildItemsThermal(r.items || []);
  html += '<div class="sep"></div>';
  html += '<div class="totals">';
  html += `<div class="row grand"><span>REFUND</span><span>${formatPHP(r.refund_amount || r.total_refund || 0)}</span></div>`;
  html += '</div>';
  html += noteBlockThermal(r);
  if (docCode) html += qrImgTag(docCode, 120);
  html += `<div class="footer">${biz.receipt_footer || ''}</div>`;
  return html;
}


function purchaseOrderThermal(data, biz, docCode) {
  const po = data;
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">PURCHASE ORDER</div>`;
  html += `<div class="meta-row"><span class="label">PO #:</span><span>${po.po_number || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDateTime(po.purchase_date || po.created_at)}</span></div>`;
  html += `<div class="meta-row"><span class="label">Supplier:</span><span>${po.vendor || ''}</span></div>`;
  if (po.dr_number) html += `<div class="meta-row"><span class="label">DR #:</span><span>${po.dr_number}</span></div>`;
  html += `<div class="meta-row"><span class="label">Status:</span><span>${(po.status || '').toUpperCase()}</span></div>`;
  html += '<div class="sep"></div>';
  html += buildItemsThermal(po.items || []);
  html += '<div class="sep"></div>';
  html += '<div class="totals">';
  html += `<div class="row"><span>Subtotal</span><span>${formatPHP(po.subtotal || po.line_subtotal)}</span></div>`;
  if (po.overall_discount_amount > 0) html += `<div class="row"><span>Discount</span><span>-${formatPHP(po.overall_discount_amount)}</span></div>`;
  if (po.freight > 0) html += `<div class="row"><span>Freight</span><span>${formatPHP(po.freight)}</span></div>`;
  html += `<div class="row grand"><span>TOTAL</span><span>${formatPHP(po.grand_total)}</span></div>`;
  if (po.balance > 0) html += `<div class="row" style="font-weight:bold"><span>BALANCE</span><span>${formatPHP(po.balance)}</span></div>`;
  html += '</div>';
  html += noteBlockThermal(po);
  if (docCode) html += qrImgTag(docCode, 120);
  html += buildFooter('internal', biz, { format: 'thermal' });
  return html;
}

// ── Branch Transfer — Dot Matrix (content-aware, NOT a sales / order slip) ──
// Mirrors the trustReceiptDotMatrix layout (repeated letterhead via <thead>,
// receipt-# fallback in <tfoot>, dense items table) but the doc title, address
// band, items table headings, totals box and footer are all reworded so the
// printed copy clearly identifies as an INTERNAL BRANCH TRANSFER and never a
// customer-facing order slip / sales invoice.
function branchTransferDotMatrix(data, biz, docCode) {
  const t = data;
  const docTitle = 'BRANCH TRANSFER';
  const transferNo = t.order_number || t.id?.slice(0, 8) || '';
  const invoiceNo  = t.invoice_number || '';
  const requestNo  = t.request_po_number || '';
  const dateStr    = fmtDate(t.created_at || t.transfer_date || t.date);
  const statusStr  = (t.status || '').toUpperCase();
  const items      = t.items || [];

  // ── 1. Letterhead (repeated via <thead> on every printed page) ───────────
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.email)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Email</span>${biz.email}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);

  const letterhead = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
          <div class="dm-biz-detail-lh" style="margin-top:2px;font-style:italic;">Internal Document — Stock Movement Between Branches</div>
        </td>
        <td class="dm-lh-title-cell" style="width:260px;">
          <div class="dm-lh-title">${docTitle}</div>
          <div class="dm-lh-invbox">
            <table>
              <tr><td>Transfer #</td><td class="dm-inv-val">${transferNo}</td></tr>
              ${invoiceNo ? `<tr><td>Invoice #</td><td class="dm-inv-val">${invoiceNo}</td></tr>` : ''}
              ${requestNo ? `<tr><td>Request #</td><td class="dm-inv-val">${requestNo}</td></tr>` : ''}
              <tr><td>Date</td><td class="dm-inv-val">${dateStr}</td></tr>
              ${statusStr ? `<tr><td>Status</td><td class="dm-inv-val">${statusStr}</td></tr>` : ''}
            </table>
          </div>
        </td>
      </tr>
    </table>
  `;

  // ── 2. Footer fallback (in <tfoot>, prints on every page) ────────────────
  const tfootHtml = `
    <div class="dm-foot-receipt">Transfer No: ${transferNo}${invoiceNo ? `  ·  Invoice No: ${invoiceNo}` : ''}  ·  INTERNAL — NOT FOR CUSTOMER  ·  Printed: ${formatPrintedAt()}</div>
  `;

  // ── 3. Body content ──────────────────────────────────────────────────────
  let body = '';

  // First-page-only: From Branch / To Branch band (replaces Billing/Shipping)
  body += `<table class="dm-addr-band">
    <tr>
      <td class="dm-addr-label">From Branch</td>
      <td class="dm-addr-val">${t.from_branch_name || ''}</td>
      <td class="dm-addr-label" style="width:120px;">To Branch</td>
      <td class="dm-addr-val">${t.to_branch_name || ''}</td>
    </tr>
  </table>`;

  // First-page-only: Prepared By / Driver / Mode grid
  body += `<table class="dm-meta-grid">
    <tr>
      <th style="width:33%;">Prepared By</th>
      <th style="width:33%;">Driver / Released By</th>
      <th>Transport Mode</th>
    </tr>
    <tr>
      <td>${t.prepared_by_name || t.created_by_name || ''}</td>
      <td>${t.driver_name || t.released_by_name || ''}</td>
      <td>${t.transport_mode || t.vehicle || '—'}</td>
    </tr>
  </table>`;

  if (t.notes) {
    body += `<div class="dm-box"><strong>Notes:</strong> ${t.notes}</div>`;
  }

  // ── Variance recap (only when an accepted variance exists) ────────────
  const pendingMap = {};
  (t.pending_items || []).forEach(p => { pendingMap[p.product_id] = p; });
  const hasVariance = (t.shortages?.length || 0) + (t.excesses?.length || 0) > 0
    || Object.keys(pendingMap).length > 0;
  if (hasVariance) {
    const verifier = t.variance_pin_verified_by_name || t.accepted_by_name || '';
    const method = t.variance_pin_verified_method ? ` (${t.variance_pin_verified_method.replace('_', ' ')})` : '';
    body += `<div class="dm-box" style="border-color:#c2410c">`;
    body += `<strong>VARIANCE ACCEPTED${verifier ? ' BY ' + verifier.toUpperCase() + method.toUpperCase() : ''}</strong>`;
    if (t.total_capital_loss > 0) {
      body += `  ·  Capital loss: ${formatPHP(t.total_capital_loss)}`;
    }
    if (t.accept_note) body += `<div style="margin-top:2px">Note: ${t.accept_note}</div>`;
    body += `</div>`;
  }

  // Items table — branch-transfer columns (no DISC, no UNIT PRICE; instead
  // we show Transfer Cost / Line Cost / Retail Value at destination).
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th class="c" style="width:0.33cm;padding:1px 0;"></th>`;
  body += `<th>PRODUCT DESCRIPTION</th>`;
  body += `<th class="c" style="width:13%">QTY</th>`;
  body += `<th class="r" style="width:15%">TRANSFER COST</th>`;
  body += `<th class="r" style="width:15%">LINE COST</th>`;
  body += `<th class="r" style="width:13%">RETAIL @ DEST</th>`;
  body += `</tr></thead><tbody>`;
  for (const item of items) {
    const sentQty = parseFloat(item.qty) || 0;
    const pend    = pendingMap[item.product_id];
    const qty     = pend !== undefined ? (parseFloat(pend.qty_received) || 0) : sentQty;
    const wasLbl  = (pend !== undefined && qty !== sentQty)
      ? `<div style="font-size:9px;color:#7c2d12">was ${sentQty} ${item.unit || ''}</div>`
      : '';
    const tc     = parseFloat(item.transfer_capital) || 0;
    const retail = parseFloat(item.branch_retail) || 0;
    const unit   = item.unit || item.uom || '';
    body += `<tr>`;
    body += `<td class="dm-row-num"></td>`;
    body += `<td>${item.product_name || ''}${item.sku ? `<div style="font-size:9px">SKU: ${item.sku}${item.category ? ` · ${item.category}` : ''}</div>` : ''}</td>`;
    body += `<td class="c">${qty}${unit ? ' ' + unit : ''}${wasLbl}</td>`;
    body += `<td class="r">${formatPHP(tc)}</td>`;
    body += `<td class="r strong">${formatPHP(tc * qty)}</td>`;
    body += `<td class="r">${retail > 0 ? formatPHP(retail) : '—'}</td>`;
    body += `</tr>`;
  }
  body += `</tbody></table>`;

  // Totals — content-specific: no Grand Total / Balance Due / Amount Paid.
  // A branch transfer doesn't take money from a customer — it moves stock.
  const lineQty = (i) => {
    const p = pendingMap[i.product_id];
    return p !== undefined ? (parseFloat(p.qty_received) || 0) : (parseFloat(i.qty) || 0);
  };
  const totalTransfer = items.reduce((s, i) => s + (parseFloat(i.transfer_capital) || 0) * lineQty(i), 0);
  const totalRetail   = items.reduce((s, i) => s + (parseFloat(i.branch_retail) || 0) * lineQty(i), 0);

  // "Total Qty" (bags + liters + bottles in one number) is gone — the
  // per-category unit summary below says what is actually on the truck.
  body += `<table class="dm-total-box">`;
  body += `<tr><td class="dm-tb-label">Total Lines:</td><td class="dm-tb-val">${items.length}</td></tr>`;
  body += `<tr class="dm-tb-grand"><td class="dm-tb-label">Transfer Cost Total:</td><td class="dm-tb-val">${formatPHP(totalTransfer)}</td></tr>`;
  if (totalRetail > 0) {
    body += `<tr><td class="dm-tb-label">Retail Value @ Dest:</td><td class="dm-tb-val">${formatPHP(totalRetail)}</td></tr>`;
  }
  body += `</table>`;
  body += categorySummaryDotMatrixHTML(items, { qtyOf: lineQty, showCount: false });

  // QR strip
  if (docCode) {
    const url   = `${window.location.origin}/doc/${docCode}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(url)}&margin=4&color=000000&bgcolor=ffffff`;
    body += `
      <div class="dm-qr-strip">
        <img src="${qrUrl}" alt="QR" width="120" height="120" />
        <div class="dm-qr-strip-info">
          <div class="dm-qr-code">${docCode}</div>
          <div class="dm-qr-scan">Scan to verify this transfer</div>
          <div class="dm-qr-sub">Transfer No: ${transferNo}</div>
          ${invoiceNo ? `<div class="dm-qr-sub">Invoice No: ${invoiceNo}</div>` : ''}
        </div>
      </div>`;
  }

  // Three-signature row — Released / Driver / Received (no "customer")
  body += `<div class="dm-sig-row">
    <div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Released By (Source Branch)</div></div>
    <div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Driver / Courier</div></div>
    <div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Received By (Destination Branch)</div></div>
  </div>`;

  // Terms / disclosure — branch-transfer-specific. NO tax disclaimer here
  // because this is not a sales document.
  body += `<div class="dm-terms-block">
    <div class="dm-terms-head">INTERNAL BRANCH TRANSFER</div>
    <div>Stock listed above is moved between company branches at transfer cost.</div>
    <div>This document is NOT a sales invoice and is NOT for customer release.</div>
    <div>Any variance must be noted and signed by the receiving branch before posting.</div>
  </div>`;
  body += buildFooter('internal', biz, { format: 'dot_matrix' });

  // ── 4. Wrap in dm-page-frame so thead/tfoot repeat on every printed page ─
  return `
    <table class="dm-page-frame">
      <thead><tr><td>${letterhead}</td></tr></thead>
      <tfoot><tr><td>${tfootHtml}</td></tr></tfoot>
      <tbody><tr><td>${body}</td></tr></tbody>
    </table>
  `;
}


function branchTransferThermal(data, biz, docCode) {
  const t = data;
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">BRANCH TRANSFER</div>`;
  html += `<div class="meta-row"><span class="label">No:</span><span>${t.order_number || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDateTime(t.created_at)}</span></div>`;
  html += `<div class="meta-row"><span class="label">From:</span><span>${t.from_branch_name || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">To:</span><span>${t.to_branch_name || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Status:</span><span>${(t.status || '').toUpperCase()}</span></div>`;
  // ── Variance recap row ────────────────────────────────────────────────
  const pendingMap = {};
  (t.pending_items || []).forEach(p => { pendingMap[p.product_id] = p; });
  const hasVariance = (t.shortages?.length || 0) + (t.excesses?.length || 0) > 0
    || Object.keys(pendingMap).length > 0;
  if (hasVariance) {
    const verifier = t.variance_pin_verified_by_name || t.accepted_by_name || '';
    html += `<div class="sep"></div>`;
    html += `<div class="meta-row"><span class="label">Variance:</span><span>ACCEPTED${verifier ? ' BY ' + verifier : ''}</span></div>`;
    if (t.total_capital_loss > 0) {
      html += `<div class="meta-row"><span class="label">Cap Loss:</span><span>${formatPHP(t.total_capital_loss)}</span></div>`;
    }
  }
  html += '<div class="sep"></div>';
  const items = t.items || [];
  let itemHtml = '<table class="items-table">';
  for (const item of items) {
    const sentQty = parseFloat(item.qty) || 0;
    const pend    = pendingMap[item.product_id];
    const qty     = pend !== undefined ? (parseFloat(pend.qty_received) || 0) : sentQty;
    const tc      = parseFloat(item.transfer_capital) || 0;
    itemHtml += `<tr><td class="item-name" colspan="2">${item.product_name || ''}</td></tr>`;
    itemHtml += `<tr><td class="item-detail">${qtyWithUnit(qty, item)} x ${formatPHP(tc)}</td><td class="item-total">${formatPHP(tc * qty)}</td></tr>`;
    // A received count that differs from what was sent gets its own small line
    // — with the unit on the quantity, "12 BAGS (was 15 BAGS) x P1,890.00"
    // no longer fits a 58mm roll.
    if (pend !== undefined && qty !== sentQty) {
      itemHtml += `<tr><td class="item-desc" colspan="2">(sent ${qtyWithUnit(sentQty, item)})</td></tr>`;
    }
  }
  itemHtml += '</table>';
  html += itemHtml;
  html += '<div class="sep"></div>';
  const lineQty = (i) => {
    const p = pendingMap[i.product_id];
    return p !== undefined ? (parseFloat(p.qty_received) || 0) : (parseFloat(i.qty) || 0);
  };
  const totalTransfer = items.reduce((s, i) => s + (parseFloat(i.transfer_capital) || 0) * lineQty(i), 0);
  html += '<div class="totals">';
  html += `<div class="row"><span>Items</span><span>${items.length}</span></div>`;
  html += `<div class="row grand"><span>TOTAL</span><span>${formatPHP(totalTransfer)}</span></div>`;
  html += '</div>';
  // Per-category unit summary in the quantities actually on the truck
  // (received count where it differs). "Items" above already counts lines.
  html += categorySummaryThermalHTML(items, { qtyOf: lineQty, showCount: false });
  html += noteBlockThermal(t);
  if (docCode) html += qrImgTag(docCode, 120);
  html += buildFooter('internal', biz, { format: 'thermal' });
  return html;
}


// ── Stock Request (new /stock-requests flow) ────────────────────────────────
// Price-free by design: a stock request asks for QUANTITIES; costs appear on
// the child BTO/PO documents born from triage, never here.
const SR_STATUS_LABELS = {
  draft: 'DRAFT', sent: 'AWAITING TRIAGE', fulfillment_generated: 'IN FULFILLMENT',
  // Owner 2026-09-03: one label for both stories — the request's permanent status.
  completed: 'COMPLETED', completed_with_variance: 'PARTIALLY FULFILLED',
  cancelled: 'CANCELLED',
};
// Headline for the printout: an In-Fulfillment request with declined lines is
// already PARTIALLY FULFILLED — print it that way, with the count.
function srStatusLabel(r) {
  const items = r.items || [];
  if (r.status === 'fulfillment_generated') {
    const n = items.filter(i => i.status === 'unfulfilled' || i.fulfillment_type === 'unfulfilled').length;
    if (n > 0) return `PARTIALLY FULFILLED · ${items.length - n} OF ${items.length} LINES`;
  }
  return SR_STATUS_LABELS[r.status] || (r.status || '').toUpperCase();
}
const SR_LINE_LABELS = {
  assigned: 'Branch Transfer', noted_for_supplier: 'Supplier PO',
  unfulfilled: 'Unfulfilled', received: 'Received', variance: 'Variance',
};

function stockRequestDotMatrix(data, biz, docCode) {
  const r = data;
  const requestNo = r.request_number || r.id?.slice(0, 8) || '';
  const dateStr   = fmtDate(r.created_at);
  const statusStr = srStatusLabel(r);
  const items     = r.items || [];
  const triaged   = !!r.triaged_at;

  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.email)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Email</span>${biz.email}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);

  const letterhead = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
          <div class="dm-biz-detail-lh" style="margin-top:2px;font-style:italic;">Internal Document — Stock Requested From Another Branch</div>
        </td>
        <td class="dm-lh-title-cell" style="width:260px;">
          <div class="dm-lh-title">STOCK REQUEST</div>
          <div class="dm-lh-invbox">
            <table>
              <tr><td>Request #</td><td class="dm-inv-val">${requestNo}</td></tr>
              <tr><td>Date</td><td class="dm-inv-val">${dateStr}</td></tr>
              ${statusStr ? `<tr><td>Status</td><td class="dm-inv-val">${statusStr}</td></tr>` : ''}
            </table>
          </div>
        </td>
      </tr>
    </table>
  `;

  const tfootHtml = `
    <div class="dm-foot-receipt">Request No: ${requestNo}  ·  INTERNAL — NOT FOR CUSTOMER  ·  Printed: ${formatPrintedAt()}</div>
  `;

  let body = '';

  // Requesting / Supplying branch band
  body += `<table class="dm-addr-band">
    <tr>
      <td class="dm-addr-label">Requested By</td>
      <td class="dm-addr-val">${r.requesting_branch_name || ''}</td>
      <td class="dm-addr-label" style="width:120px;">Requested From</td>
      <td class="dm-addr-val">${r.supplying_branch_name || ''}</td>
    </tr>
  </table>`;

  // Prepared By / Triaged By grid
  body += `<table class="dm-meta-grid">
    <tr>
      <th style="width:34%;">Prepared By</th>
      <th style="width:33%;">Sent</th>
      <th>Sorted By (Supplying Branch)</th>
    </tr>
    <tr>
      <td>${r.created_by_name || ''}</td>
      <td>${r.sent_at ? fmtDate(r.sent_at) : '—'}</td>
      <td>${triaged ? `${r.triaged_by || ''} · ${fmtDate(r.triaged_at)}` : '—'}</td>
    </tr>
  </table>`;

  if (r.notes) {
    body += `<div class="dm-box"><strong>Notes:</strong> ${r.notes}</div>`;
  }

  // Items — no price columns; outcome column appears once triaged.
  body += `<table class="dm-items-table"><thead><tr>`;
  body += `<th class="c" style="width:0.33cm;padding:1px 0;">#</th>`;
  body += `<th class="c" style="width:12%;">Qty</th>`;
  body += `<th class="c" style="width:12%;">Unit</th>`;
  body += `<th>Item</th>`;
  body += triaged ? `<th class="c" style="width:20%;">Fulfill Via</th>`
                  : `<th class="c" style="width:20%;">Prepared</th>`;
  body += `</tr></thead><tbody>`;

  const checkbox = `<span style="display:inline-block;width:12px;height:12px;border:1px solid #000;"></span>`;
  items.forEach((item, idx) => {
    // stock_requests store `qty` (see _validate_items); `quantity` kept as fallback
    const qty = parseFloat(item.qty ?? item.quantity) || 0;
    const unit = lineUnit(item);
    body += `<tr>`;
    body += `<td class="dm-row-num">${idx + 1}</td>`;
    body += `<td class="c strong">${fmtQty(qty)}</td>`;
    body += `<td class="c">${unit}</td>`;
    body += `<td class="strong">${item.product_name || ''}${item.sku ? `<div style="font-size:9px;font-weight:normal;">SKU: ${item.sku}</div>` : ''}</td>`;
    body += triaged ? `<td class="c">${SR_LINE_LABELS[item.status] || '—'}</td>`
                    : `<td class="c">${checkbox}</td>`;
    body += `</tr>`;
  });
  body += `</tbody></table>`;

  // Per-category unit summary — replaces the money totals box and the old
  // "TOTAL QTY REQUESTED" that added bags and liters together (2026-09-29).
  body += categorySummaryDotMatrixHTML(items, { qtyOf: (i) => i.qty ?? i.quantity });

  body += `<div class="dm-warning" style="margin-top:10px;">*** STOCK REQUEST — NO PRICES ***<br/><span style="font-weight:normal;">Quantities requested between company branches. Costs appear on the transfer or purchase order created from this request.</span></div>`;

  // QR strip (only when a doc code exists)
  if (docCode) {
    const url   = `${window.location.origin}/doc/${docCode}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(url)}&margin=4&color=000000&bgcolor=ffffff`;
    body += `
      <div class="dm-qr-strip">
        <img src="${qrUrl}" alt="QR" width="120" height="120" />
        <div class="dm-qr-strip-info">
          <div class="dm-qr-code">${docCode}</div>
          <div class="dm-qr-scan">Scan to verify this request</div>
          <div class="dm-qr-sub">Request No: ${requestNo}</div>
        </div>
      </div>`;
  }

  // Signature band — requesting side prepares, supplying side sorts/releases
  body += `<div class="dm-sig-row">
    <div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Prepared By (Requesting Branch)</div></div>
    <div class="dm-sig-block"><div class="dm-sig-line"></div><div class="dm-sig-label">Approved / Sorted By (Supplying Branch)</div></div>
  </div>`;

  body += `<div class="dm-terms-block">
    <div class="dm-terms-head">INTERNAL STOCK REQUEST</div>
    <div>This document asks another company branch to supply the stock listed above.</div>
    <div>It is NOT a sales invoice, NOT a purchase order, and NOT for customer release.</div>
    <div>Fulfillment happens through the Branch Transfer or Purchase Order created from it.</div>
  </div>`;
  body += buildFooter('internal', biz, { format: 'dot_matrix' });

  return `
    <table class="dm-page-frame">
      <thead><tr><td>${letterhead}</td></tr></thead>
      <tfoot><tr><td>${tfootHtml}</td></tr></tfoot>
      <tbody><tr><td>${body}</td></tr></tbody>
    </table>
  `;
}


function stockRequestThermal(data, biz, docCode) {
  const r = data;
  const triaged = !!r.triaged_at;
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">STOCK REQUEST</div>`;
  html += `<div class="meta-row"><span class="label">No:</span><span>${r.request_number || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDateTime(r.created_at)}</span></div>`;
  html += `<div class="meta-row"><span class="label">From:</span><span>${r.requesting_branch_name || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">To:</span><span>${r.supplying_branch_name || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Status:</span><span>${srStatusLabel(r)}</span></div>`;
  if (triaged) {
    html += `<div class="meta-row"><span class="label">Sorted:</span><span>${r.triaged_by || ''} ${fmtDate(r.triaged_at)}</span></div>`;
  }
  html += '<div class="sep"></div>';
  const items = r.items || [];
  let itemHtml = '<table class="items-table">';
  for (const item of items) {
    const qty = parseFloat(item.qty ?? item.quantity) || 0;
    itemHtml += `<tr><td class="item-name" colspan="2">${item.product_name || ''}</td></tr>`;
    itemHtml += `<tr><td class="item-detail">${qtyWithUnit(qty, item)}</td><td class="item-total">${triaged ? (SR_LINE_LABELS[item.status] || '—') : ''}</td></tr>`;
  }
  itemHtml += '</table>';
  html += itemHtml;
  html += '<div class="sep"></div>';
  html += '<div class="totals">';
  html += `<div class="row"><span>Lines</span><span>${items.length}</span></div>`;
  html += '</div>';
  // Per-category unit summary replaces "TOTAL QTY", which added bags, liters
  // and bottles into one number (owner 2026-09-29).
  html += categorySummaryThermalHTML(items, { qtyOf: (i) => i.qty ?? i.quantity, showCount: false });
  if (r.notes) html += `<div class="meta-row" style="margin-top:4px;"><span class="label">Notes:</span><span>${r.notes}</span></div>`;
  if (docCode) html += qrImgTag(docCode, 120);
  html += buildFooter('internal', biz, { format: 'thermal' });
  return html;
}


// ── Capital Change Report — Dot Matrix ──────────────────────────────────────
// Shared bits (labels, period line, month axis) — the thermal variant and the
// station snapshot path reuse these so every printout reads the same window.
const CCR_RANGE_LABELS = {
  '1d': 'Last 1 day', '7d': 'Last 7 days', '30d': 'Last 30 days',
  '60d': 'Last 60 days', '90d': 'Quarter (90d)', '180d': 'Last 6 months',
  '365d': 'Last 12 months', 'all': 'All time', 'custom': 'Custom range',
};

function ccrFmtDay(v, withYear = true) {
  if (!v) return '';
  try {
    const d = new Date(v.length === 10 ? v + 'T00:00:00' : v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', withYear
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

// "Sep 1 – Sep 30, 2026" — the year once when both ends share it.
function ccrSpan(fromV, toV) {
  const a = ccrFmtDay(fromV, false), b = ccrFmtDay(toV, true);
  if (!a && !b) return '';
  if (!a) return `up to ${b}`;
  if (!b) return `from ${ccrFmtDay(fromV, true)}`;
  const sameYear = new Date(fromV).getFullYear() === new Date(toV).getFullYear();
  return `${sameYear ? a : ccrFmtDay(fromV, true)} – ${b}`;
}

// { label: 'Last 30 days', dates: 'Aug 4 – Sep 3, 2026' } — dates empty for All time
// with no lower bound. Custom prints the typed inclusive days.
function ccrPeriod(data) {
  const rng = data.range || '';
  const label = CCR_RANGE_LABELS[rng] || rng;
  let dates = '';
  if (rng === 'custom') dates = ccrSpan(data.date_from || data.from, data.date_to || data.to);
  else if (rng !== 'all') dates = ccrSpan(data.from, data.to);
  return { label, dates };
}

function ccrMonthLabel(yyyymm) {
  if (!yyyymm || yyyymm.length < 7) return yyyymm || '';
  const [y, m] = yyyymm.split('-');
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1)
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// Every month any product changed in, oldest first — the print's column axis
// mirrors the screen's (one global axis across categories).
function ccrMonths(categories) {
  const set = new Set();
  categories.forEach(c => (c.products || []).forEach(p =>
    Object.keys(p.by_month || {}).forEach(m => set.add(m))));
  return Array.from(set).sort();
}

// The day a change actually happened: DR day when the receipt has one.
function ccrChangeDay(ch) {
  const d = ch.date || ch.changed_at;
  return `${ch.date_is_dr ? 'DR ' : ''}${ccrFmtDay(d, false)}`;
}

function ccrPct(v) {
  return (v !== null && v !== undefined) ? `${v > 0 ? '+' : ''}${v}%` : '';
}

function ccrMethodTag(ch) {
  if (ch.method === 'moving_average') {
    const lp = (ch.lp > 0 && Math.abs(ch.lp - ch.new_capital) > 0.005) ? ` LP ${formatPHP(ch.lp)}` : '';
    return `MA${lp}`;
  }
  if (ch.method === 'last_purchase') {
    const ma = (ch.ma > 0 && Math.abs(ch.ma - ch.new_capital) > 0.005) ? ` MA ${formatPHP(ch.ma)}` : '';
    return `LP${ma}`;
  }
  return '';
}

function capitalChangeReportDotMatrix(data, biz) {
  const period = ccrPeriod(data);
  const filters = data.filters || {};
  const summary = data.summary || {};
  const categories = data.categories || [];
  const monthView = data.view_mode === 'month';
  const months = monthView ? ccrMonths(categories) : [];

  // Letterhead
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.email)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Email</span>${biz.email}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);

  let html = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
        </td>
        <td class="dm-lh-title-cell" style="width:260px;">
          <div class="dm-lh-title" style="font-size:20px;">CAPITAL CHANGE<br/>REPORT</div>
          <div class="dm-lh-invbox">
            <table>
              <tr><td>Period</td><td class="dm-inv-val">${period.label}${period.dates ? `<br/><span style="font-weight:400;font-size:11px;">${period.dates}</span>` : ''}</td></tr>
              <tr><td>View</td><td class="dm-inv-val">${monthView ? 'By month' : 'Sequence'}</td></tr>
              <tr><td>Printed</td><td class="dm-inv-val">${formatPrintedAt()}</td></tr>
            </table>
          </div>
        </td>
      </tr>
    </table>`;

  // Filters + Summary band
  const filterParts = [];
  if (filters.source_type && filters.source_type !== 'all') filterParts.push(`Source: ${filters.source_type.replace('_', ' ')}`);
  if (filters.category) filterParts.push(`Category: ${filters.category}`);
  if (filters.branch_name || filters.branch_id) filterParts.push(`Branch: ${filters.branch_name || filters.branch_id}`);
  if (filters.search) filterParts.push(`Search: "${filters.search}"`);

  html += `<table class="dm-meta-grid">
    <tr>
      <th>Products Changed</th>
      <th>Total Changes</th>
      <th>Increases</th>
      <th>Decreases</th>
      ${filterParts.length ? '<th>Filters</th>' : ''}
    </tr>
    <tr>
      <td>${summary.products_affected || 0}</td>
      <td>${summary.total_changes || 0}</td>
      <td>${summary.increases || 0}</td>
      <td>${summary.decreases || 0}</td>
      ${filterParts.length ? `<td>${filterParts.join(' / ')}</td>` : ''}
    </tr>
  </table>`;

  const productCell = (p) =>
    `<td style="font-size:13px;">${p.name}${p.sku ? ` <span style="font-size:10px;font-weight:400;">(${p.sku})</span>` : ''}${p.unit ? ` <span style="font-size:10px;font-weight:400;">/ ${p.unit}</span>` : ''}</td>`;
  const startMa = (p) => (p.start_ma != null ? p.start_ma : p.first_capital);
  const curMa   = (p) => (p.current_ma != null ? p.current_ma : p.current_capital);
  const startCell = (p, cls = 'r') =>
    `<td class="${cls}">${formatPHP(startMa(p))}${(p.start_lp != null && Math.abs(p.start_lp - startMa(p)) > 0.01) ? `<br><span style="font-size:10px;font-weight:400;">LP ${formatPHP(p.start_lp)}</span>` : ''}</td>`;
  const currentCell = (p) =>
    `<td class="r strong">${formatPHP(curMa(p))}${(p.current_lp != null && Math.abs(p.current_lp - curMa(p)) > 0.01) ? `<br><span style="font-size:10px;font-weight:400;">LP ${formatPHP(p.current_lp)}</span>` : ''}</td>`;
  const dirStyle = (p) => (p.net_delta > 0 || p.net_delta < 0) ? 'font-weight:700;' : '';
  const netStr = (p) => `${p.net_delta > 0 ? '+' : ''}${formatPHP(p.net_delta)}`;
  const arrowOf = (v) => v > 0 ? '&#9650;' : v < 0 ? '&#9660;' : '&#8212;';

  // Per-category tables
  for (const cat of categories) {
    html += `<div style="margin-top:12px;page-break-inside:avoid;">`;
    html += `<div style="font-size:13px;font-weight:700;text-transform:uppercase;border-bottom:2px solid #000;padding-bottom:3px;margin-bottom:2px;">${cat.category} <span style="font-weight:400;font-size:11px;">(${cat.count} product${cat.count !== 1 ? 's' : ''})</span></div>`;

    if (monthView) {
      // ── Month grid: START | one column per month | CURRENT | NET ──────────
      // Many months squeeze the cells; the font steps down past 6 columns so a
      // year still fits on the sheet.
      const dense = months.length > 6;
      const cellFont = dense ? '9px' : '10px';
      const nameW = dense ? 20 : 24;
      const fixedW = dense ? 9 : 11;
      const monthW = Math.max(6, Math.floor((100 - nameW - fixedW * 3 - 3) / Math.max(1, months.length)));

      html += `<table class="dm-items-table">`;
      html += `<thead><tr>`;
      html += `<th style="width:3%">#</th>`;
      html += `<th style="width:${nameW}%">PRODUCT</th>`;
      html += `<th class="r" style="width:${fixedW}%">START</th>`;
      for (const m of months) html += `<th class="c" style="width:${monthW}%">${ccrMonthLabel(m).toUpperCase()}</th>`;
      html += `<th class="r" style="width:${fixedW}%">CURRENT</th>`;
      html += `<th class="r" style="width:${fixedW}%">NET</th>`;
      html += `</tr></thead><tbody>`;

      for (let i = 0; i < cat.products.length; i++) {
        const p = cat.products[i];
        html += `<tr>`;
        html += `<td class="c">${i + 1}</td>`;
        html += productCell(p);
        html += startCell(p);
        for (const m of months) {
          const cell = (p.by_month && p.by_month[m]) || [];
          if (!cell.length) { html += `<td class="c" style="font-size:${cellFont};">&#8212;</td>`; continue; }
          const lines = cell.map(ch => {
            const pct = ccrPct(ch.delta_pct);
            const tag = ccrMethodTag(ch);
            return `<div style="white-space:nowrap;">${arrowOf(ch.delta)} <b>${formatPHP(ch.new_capital)}</b>${pct ? ` <span style="font-size:${dense ? '8px' : '9px'};">${pct}</span>` : ''}</div>`
              + `<div style="font-size:${dense ? '8px' : '9px'};">${ccrChangeDay(ch)}${tag ? ` · ${tag}` : ''}</div>`;
          });
          html += `<td style="font-size:${cellFont};padding:3px 4px;">${lines.join('')}</td>`;
        }
        html += currentCell(p);
        html += `<td class="r" style="${dirStyle(p)}">${netStr(p)}${(p.net_delta_pct !== null && p.net_delta_pct !== undefined) ? `<br><span style="font-size:10px;font-weight:400;">${ccrPct(p.net_delta_pct)}</span>` : ''}</td>`;
        html += `</tr>`;
      }
      html += `</tbody></table></div>`;
      continue;
    }

    // ── Sequence: OLD → NEW with each change event under the product ──────
    html += `<table class="dm-items-table">`;
    html += `<thead><tr>`;
    html += `<th style="width:4%">#</th>`;
    html += `<th>PRODUCT</th>`;
    html += `<th class="r" style="width:15%">OLD (MA/LP)</th>`;
    html += `<th class="r" style="width:15%">NEW (MA/LP)</th>`;
    html += `<th class="c" style="width:8%">DIR</th>`;
    html += `<th class="r" style="width:14%">CHANGE</th>`;
    html += `<th class="r" style="width:9%">%</th>`;
    html += `</tr></thead><tbody>`;

    for (let i = 0; i < cat.products.length; i++) {
      const p = cat.products[i];
      const pctStr = (p.net_delta_pct !== null && p.net_delta_pct !== undefined) ? ccrPct(p.net_delta_pct) : '-';

      html += `<tr>`;
      html += `<td class="c">${i + 1}</td>`;
      html += productCell(p);
      html += startCell(p);
      html += currentCell(p);
      html += `<td class="c" style="${dirStyle(p)}">${arrowOf(p.net_delta)}</td>`;
      html += `<td class="r" style="${dirStyle(p)}">${netStr(p)}</td>`;
      html += `<td class="r">${pctStr}</td>`;
      html += `</tr>`;

      // Detail rows: show each individual change event
      if (p.changes && p.changes.length > 1) {
        for (const ch of p.changes) {
          const chArrow = ch.direction === 'up' ? '&#9650;' : ch.direction === 'down' ? '&#9660;' : '';
          const chRef = [ch.source_ref, ch.vendor].filter(Boolean).join(' / ');
          const tag = ccrMethodTag(ch);
          html += `<tr style="font-size:10px;">`;
          html += `<td></td>`;
          html += `<td style="padding-left:20px;font-weight:400;font-size:10px;">${ccrChangeDay(ch)}${chRef ? ' &middot; ' + chRef : ''}${ch.branch_name ? ' &middot; ' + ch.branch_name : ''}${tag ? ' &middot; ' + tag : ''}</td>`;
          html += `<td class="r" style="font-size:10px;">${formatPHP(ch.old_capital)}</td>`;
          html += `<td class="r" style="font-size:10px;">${formatPHP(ch.new_capital)}</td>`;
          html += `<td class="c" style="font-size:10px;">${chArrow}</td>`;
          html += `<td class="r" style="font-size:10px;">${ch.delta > 0 ? '+' : ''}${formatPHP(ch.delta)}</td>`;
          html += `<td class="r" style="font-size:10px;">${ccrPct(ch.delta_pct)}</td>`;
          html += `</tr>`;
        }
      }
    }
    html += `</tbody></table></div>`;
  }

  // Footer
  html += `<div class="dm-footer" style="margin-top:16px;">`;
  html += `<div class="dm-disclaimer">This report lists only products with actual cost price changes within the selected period${period.dates ? ` (${period.dates})` : ''}. Generated by AgriBooks.</div>`;
  html += `</div>`;

  return html;
}

// ── Capital Price Strip — Thermal ───────────────────────────────────────────
// The counter companion of the report (owner 2026-09-03): ONE category per
// strip, one line per product with its CURRENT MA and LP and the receipt date
// that set it inside the period. A product with no receipt in the period is
// stamped "as of <newest receipt>" so the strip is a complete price list.
function capitalPriceStripThermal(data, biz) {
  const period = ccrPeriod(data);
  const filters = data.filters || {};
  const items = data.items || [];

  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">CAPITAL PRICE LIST</div>`;
  html += `<div class="meta-row"><span class="label">Category:</span><span><b>${data.category || ''}</b></span></div>`;
  html += `<div class="meta-row"><span class="label">Period:</span><span>${period.label}${period.dates ? `<br>${period.dates}` : ''}</span></div>`;
  if (filters.branch_name) html += `<div class="meta-row"><span class="label">Branch:</span><span>${filters.branch_name}</span></div>`;
  if (filters.source_type && filters.source_type !== 'all') html += `<div class="meta-row"><span class="label">Source:</span><span>${filters.source_type.replace('_', ' ')}</span></div>`;
  html += `<div class="meta-row"><span class="label">Printed:</span><span>${formatPrintedAt()}</span></div>`;
  html += `<div class="meta-row"><span class="label">Lists:</span><span>${data.only_changed === false ? 'whole category' : 'changed in period'}</span></div>`;
  html += '<div class="sep"></div>';
  html += `<div class="meta-row"><span class="label">MA / LP</span><span>Receipt</span></div>`;
  html += '<table class="items-table">';
  for (const it of items) {
    const lc = it.last_change;
    let stamp;
    if (!lc) stamp = 'no receipt';
    else if (lc.in_range) stamp = ccrChangeDay(lc);
    else stamp = `as of ${ccrFmtDay(lc.date, true)}`;   // older receipts announce their year
    const sub = [it.sku, it.unit].filter(Boolean).join(' · ');
    html += `<tr><td class="item-name" colspan="2">${it.name}${sub ? `<div style="font-size:9px;line-height:1.1;">${sub}</div>` : ''}</td></tr>`;
    html += `<tr><td class="item-detail"><b>MA ${formatPHP(it.current_ma)}</b><br>LP ${formatPHP(it.current_lp)}</td><td class="item-total">${stamp}</td></tr>`;
  }
  html += '</table>';
  html += '<div class="sep"></div>';
  html += '<div class="totals">';
  html += `<div class="row"><span>Products</span><span>${items.length}</span></div>`;
  html += `<div class="row"><span>With a receipt in period</span><span>${data.in_range_count || 0}</span></div>`;
  html += '</div>';
  html += `<div style="font-size:9px;margin-top:3px;">${data.only_changed === false ? '"as of" = no receipt in the period; date of the newest receipt on record. ' : 'Only products with a receipt in the period. '}Capital cost only, not selling prices.</div>`;
  html += buildFooter('internal', biz, { format: 'thermal' });
  return html;
}

// Per-PO Price Change Report — shows, for each product whose capital moved in
// a received PO, the old→new cost plus the previous price points (last 3, dated)
// so the operator can see the trend at a glance.
function poPriceChangeReportDotMatrix(data, biz) {
  const products = data.products || [];
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);

  let html = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
        </td>
        <td class="dm-lh-title-cell" style="width:260px;">
          <div class="dm-lh-title" style="font-size:18px;">PRICE CHANGE<br/>REPORT</div>
          <div class="dm-lh-invbox">
            <table>
              <tr><td>PO #</td><td class="dm-inv-val">${data.po_number || '-'}</td></tr>
              ${data.vendor ? `<tr><td>Vendor</td><td class="dm-inv-val">${data.vendor}</td></tr>` : ''}
              <tr><td>Printed</td><td class="dm-inv-val">${formatPrintedAt()}</td></tr>
            </table>
          </div>
        </td>
      </tr>
    </table>`;

  html += `<div style="font-size:12px;margin:8px 0 4px;font-weight:600;">${products.length} product${products.length !== 1 ? 's' : ''} changed capital cost in this Purchase Order.</div>`;

  html += `<table class="dm-items-table"><thead><tr>`;
  html += `<th style="width:4%">#</th>`;
  html += `<th>PRODUCT</th>`;
  html += `<th class="r" style="width:13%">OLD COST</th>`;
  html += `<th class="r" style="width:16%">NEW COST<br/><span style="font-size:9px;font-weight:600;color:#222;">MA · PO</span></th>`;
  html += `<th class="r" style="width:11%">CHANGE</th>`;
  html += `<th style="width:34%">PREVIOUS PRICES (recent → older) <span style="font-size:9px;font-weight:600;color:#222;">MA · PO</span></th>`;
  html += `</tr></thead><tbody>`;

  // Render the "MA · PO" cell. When the recorded moving-average matches the
  // PO unit price (e.g. last-purchase costing method, or PO ≥ existing cost)
  // we show one value to avoid noise. When they differ — i.e. the costing
  // method picked the moving-average over a cheaper PO — we show BOTH so the
  // operator can see the underlying movement (the original ask).
  const renderMAandPO = (ma, po, strong = false) => {
    const maVal = Number(ma) || 0;
    const poVal = Number(po) || 0;
    const same = Math.abs(maVal - poVal) < 0.005;
    if (same || !poVal) {
      return `<span ${strong ? 'style="font-weight:700;"' : ''}>${formatPHP(maVal)}</span>`;
    }
    return `
      <div ${strong ? 'style="font-weight:700;"' : ''}>${formatPHP(maVal)}
        <span style="font-size:9px;font-weight:600;color:#222;">MA</span>
      </div>
      <div ${strong ? 'style="font-weight:700;"' : ''}>${formatPHP(poVal)}
        <span style="font-size:9px;font-weight:600;color:#222;">PO</span>
      </div>`;
  };

  for (let i = 0; i < products.length; i++) {
    const p = products[i];
    const arrow = p.delta > 0 ? '&#9650;' : p.delta < 0 ? '&#9660;' : '&#8212;';
    const changeStr = `${p.delta > 0 ? '+' : ''}${formatPHP(p.delta)}`;
    // Build the dated history string (last 3 POs, recent → older). Each entry
    // now exposes BOTH the moving-average new_capital and the actual PO unit
    // price so the trend reflects the real PO price even when costing picked MA.
    const hist = (p.history || []).map(h => {
      const d = h.changed_at ? new Date(h.changed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      const ref = h.source_ref ? ` <span style="color:#666;">(${h.source_ref})</span>` : '';
      const maVal = Number(h.new_capital) || 0;
      const poVal = Number(h.po_unit_price) || 0;
      const same = Math.abs(maVal - poVal) < 0.005 || !poVal;
      const priceCell = same
        ? `<b>${formatPHP(maVal)}</b>`
        : `<b>${formatPHP(maVal)}</b><span style="font-size:9px;font-weight:600;color:#222;">MA</span> &middot; <b>${formatPHP(poVal)}</b><span style="font-size:9px;font-weight:600;color:#222;">PO</span>`;
      return `${d}${ref}: ${priceCell}`;
    });
    const histStr = hist.length ? hist.join('  &rarr;  ') : '<span style="color:#888;">No prior PO history</span>';

    html += `<tr style="page-break-inside:avoid;">`;
    html += `<td class="c">${i + 1}</td>`;
    html += `<td style="font-size:13px;">${p.name}${p.sku ? ` <span style="font-size:10px;font-weight:400;">(${p.sku})</span>` : ''}${p.unit ? ` <span style="font-size:10px;font-weight:400;">/ ${p.unit}</span>` : ''}</td>`;
    html += `<td class="r">${formatPHP(p.old_capital)}</td>`;
    html += `<td class="r">${renderMAandPO(p.new_capital, p.po_unit_price, true)}</td>`;
    html += `<td class="r" style="font-weight:700;">${arrow} ${changeStr}</td>`;
    html += `<td style="font-size:11px;">${histStr}</td>`;
    html += `</tr>`;
  }
  html += `</tbody></table>`;

  html += `<div class="dm-footer" style="margin-top:16px;">`;
  html += `<div class="dm-disclaimer">Internal price-change summary for the above Purchase Order. Review retail/wholesale prices for any product whose capital cost increased. Generated by AgriBooks.</div>`;
  html += `</div>`;
  return html;
}


// ── Smart Price Scan Review — price list for physical board update ──────────
// Triggered after PIN-confirmed price fix (Below Cost) or price confirmation
// (Capital Change). Gives the operator a dot-matrix sheet to carry around the
// store and update physical price tags/boards.
//
// data shape:
//   report_type: 'below_cost' | 'capital_change'
//   branch_name: string
//   verified_by: string
//   schemes: [{key, name}]
//   items: [{
//     product_name, sku, unit, category,
//     // below_cost only:
//     capital, lp, ma, prices: {scheme_key: number},
//     // capital_change only (in addition):
//     old_capital, new_capital, source_ref, source_type, vendor, changed_at,
//   }]
function smartPriceScanReportDotMatrix(data, biz) {
  const isCapital = data.report_type === 'capital_change';
  const items     = data.items     || [];
  const schemes   = data.schemes   || [];
  const branchName   = data.branch_name  || '';
  const verifierName = data.verified_by  || '';

  const primarySchemes = schemes.filter(s => ['retail','wholesale'].includes(s.key));
  const otherSchemes   = schemes.filter(s => !['retail','wholesale'].includes(s.key));

  // ── Letterhead ─────────────────────────────────────────────────────────────
  const logoHtml = getLogoSrc(biz)
    ? `<img src="${getLogoSrc(biz)}" alt="logo" style="max-width:130px;max-height:90px;object-fit:contain;display:block;" />`
    : '';
  const bizLines = [];
  if (biz.address) bizLines.push(`<div class="dm-biz-detail-lh">${biz.address}</div>`);
  if (biz.phone)   bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">Tel</span>${biz.phone}</div>`);
  if (biz.tin)     bizLines.push(`<div class="dm-biz-detail-lh"><span class="dm-lbl">TIN</span>${biz.tin}</div>`);

  const docTitle = isCapital ? 'CAPITAL CHANGE<br/>PRICE UPDATE' : 'BELOW COST<br/>PRICE UPDATE';

  let html = `
    <table class="dm-letterhead">
      <tr>
        <td style="width:140px;">${logoHtml}</td>
        <td class="dm-lh-biz">
          <div class="dm-biz-name-lh">${biz.business_name || 'AgriBooks'}</div>
          ${bizLines.join('')}
        </td>
        <td class="dm-lh-title-cell" style="width:260px;">
          <div class="dm-lh-title" style="font-size:18px;">${docTitle}</div>
          <div class="dm-lh-invbox">
            <table>
              <tr><td>Branch</td><td class="dm-inv-val">${branchName || '—'}</td></tr>
              <tr><td>Printed</td><td class="dm-inv-val">${formatPrintedAt()}</td></tr>
              ${verifierName ? `<tr><td>Auth by</td><td class="dm-inv-val">${verifierName}</td></tr>` : ''}
            </table>
          </div>
        </td>
      </tr>
    </table>`;

  html += `<div style="font-size:12px;margin:8px 0 4px;font-weight:600;">
    ${items.length} product${items.length !== 1 ? 's' : ''} — ${isCapital ? 'capital changes confirmed, prices updated' : 'below-cost issues corrected'}.
    Update physical price boards / tags accordingly.
  </div>`;

  // ── Column headers ──────────────────────────────────────────────────────────
  // Layout: # | PRODUCT (source sub-line) | CAPITAL (LP/MA sub-line) | RETAIL | WHOLESALE | other schemes
  const otherCount = otherSchemes.length;
  const otherW     = otherCount > 0 ? Math.min(10, Math.floor(16 / otherCount)) : 0;
  html += `<table class="dm-items-table" style="table-layout:fixed;width:100%;"><thead><tr>`;
  html += `<th style="width:4%">#</th>`;
  html += `<th style="width:auto;">PRODUCT</th>`;
  html += `<th class="r" style="width:21%">NEW CAPITAL</th>`;
  for (const s of primarySchemes) {
    html += `<th class="r" style="width:14%;font-size:13px;">${s.name.toUpperCase()}</th>`;
  }
  for (const s of otherSchemes) {
    html += `<th class="r" style="width:${otherW}%">${s.name.toUpperCase()}</th>`;
  }
  html += `</tr></thead><tbody>`;

  // ── Sort: category A→Z, then product name A→Z ──────────────────────────
  const sortedItems = [...items].sort((a, b) => {
    const catA = (a.category || '').toLowerCase();
    const catB = (b.category || '').toLowerCase();
    if (catA < catB) return -1;
    if (catA > catB) return 1;
    const nameA = (a.product_name || '').toLowerCase();
    const nameB = (b.product_name || '').toLowerCase();
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  });

  // ── Rows ────────────────────────────────────────────────────────────────────
  let lastCategory = null;
  for (let i = 0; i < sortedItems.length; i++) {
    const p = sortedItems[i];
    const newPrices = p.prices || {};
    const cap = isCapital ? parseFloat(p.new_capital || 0) : parseFloat(p.capital || 0);

    // Category group header row
    const cat = p.category || 'Uncategorized';
    if (cat !== lastCategory) {
      const colSpan = 2 + primarySchemes.length + otherSchemes.length + 1;
      html += `<tr style="background:#f0f4f8;">
        <td colspan="${colSpan}" style="font-size:10px;font-weight:700;text-transform:uppercase;
          letter-spacing:0.08em;color:#334155;padding:4px 6px;border-top:1px solid #cbd5e1;">
          ${cat}
        </td>
      </tr>`;
      lastCategory = cat;
    }
    let productCell = `<td style="font-size:13px;word-break:break-word;overflow-wrap:break-word;">`;
    productCell += `<strong>${p.product_name}</strong>`;
    if (p.sku)  productCell += ` <span style="font-size:10px;font-weight:400;">(${p.sku})</span>`;
    if (p.unit) productCell += ` <span style="font-size:10px;font-weight:400;">/ ${p.unit}</span>`;

    if (isCapital) {
      const srcLabel  = p.source_type === 'purchase_order' ? 'PO' : 'BTO';
      const rawRef    = p.source_ref || '';
      // Truncate long refs so they never blow up the cell
      const shortRef  = rawRef.length > 28 ? rawRef.substring(0, 26) + '…' : rawRef;
      const dateStr   = p.changed_at
        ? new Date(p.changed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';
      productCell += `<br/><span style="font-size:9px;color:#666;">${srcLabel}${shortRef ? ` · ${shortRef}` : ''}${dateStr ? ` · ${dateStr}` : ''}</span>`;
    }
    productCell += `</td>`;
    html += `<tr style="page-break-inside:avoid;"><td class="c">${i + 1}</td>${productCell}`;

    // Capital cell — old→new on first line, LP / MA as small note
    if (isCapital) {
      const lp = parseFloat(p.lp || 0);
      const ma = parseFloat(p.ma || 0);
      const lpSameAsMa = lp > 0 && ma > 0 && Math.abs(lp - ma) < 0.005;
      let lpMaNote = '';
      if (lpSameAsMa && lp > 0) {
        lpMaNote = `LP = MA = ${formatPHP(lp)}`;
      } else {
        const parts = [];
        if (lp > 0) parts.push(`LP ${formatPHP(lp)}`);
        if (ma > 0) parts.push(`MA ${formatPHP(ma)}`);
        lpMaNote = parts.join(' · ');
      }
      html += `<td class="r">`;
      html += `<span style="font-size:10px;color:#555;">${formatPHP(p.old_capital)} &#8594;</span><br/>`;
      html += `<span style="font-weight:700;font-size:13px;">${formatPHP(p.new_capital)}</span>`;
      if (lpMaNote) {
        html += `<br/><span style="font-size:9px;color:#666;">${lpMaNote}</span>`;
      }
      html += `</td>`;
    } else {
      html += `<td class="r" style="font-weight:700;font-size:13px;">${formatPHP(p.capital)}</td>`;
    }

    // Price columns — retail/wholesale bold & slightly larger; highlight below-capital
    for (const s of [...primarySchemes, ...otherSchemes]) {
      const val      = parseFloat(newPrices[s.key] || 0);
      const isBelow  = val > 0 && cap > 0 && val < cap;
      const hasValue = val > 0;
      const isPrimary = primarySchemes.some(ps => ps.key === s.key);
      const fontSize  = isPrimary ? '14px' : '12px';
      html += `<td class="r" style="font-size:${fontSize};${hasValue ? 'font-weight:700;' : 'color:#aaa;'}${isBelow ? 'color:#c00;' : ''}">`;
      html += hasValue ? formatPHP(val) : '&#8212;';
      if (isBelow) html += `<br/><span style="font-size:9px;font-weight:600;">&#9650;BELOW</span>`;
      html += `</td>`;
    }

    html += `</tr>`;
  }

  html += `</tbody></table>`;

  html += `<div class="dm-footer" style="margin-top:16px;">`;
  html += `<div class="dm-disclaimer">`;
  html += isCapital
    ? 'Capital costs changed via PO, Branch Transfer, or Manual Edit. Prices above have been confirmed and are now active in the POS. Update physical price boards to match.'
    : 'Prices corrected from below-cost to above capital. Now active in POS. Update physical price boards to match.';
  html += ` Generated by AgriBooks.</div>`;
  html += `</div>`;

  return html;
}



// ═══════════════════════════════════════════════════════════════════════════
//  MAIN PRINT ENGINE
// ═══════════════════════════════════════════════════════════════════════════

// ── BIR Sales / Charge Invoice — Dot Matrix (reuses the order-slip page-frame) ──
function salesInvoiceDotMatrix(data, biz, docCode) {
  const isCharge = (data.payment_type === 'credit' || data.payment_type === 'partial');
  const label = data.bir_document_label || (isCharge ? 'CHARGE INVOICE' : 'SALES INVOICE');
  return orderSlipDotMatrix(data, biz, docCode, {
    docTitle: label,
    footerKind: isCharge ? 'charge_invoice' : 'sales_invoice',
    birNumber: data.bir_invoice_number || '',
  });
}

// ── BIR Sales / Charge Invoice — 58mm Thermal ──────────────────────────────
function salesInvoiceThermal(data, biz, docCode) {
  const inv = data;
  const isCharge = (inv.payment_type === 'credit' || inv.payment_type === 'partial');
  const label = inv.bir_document_label || (isCharge ? 'CHARGE INVOICE' : 'SALES INVOICE');
  let html = buildThermalHeader(biz);
  html += `<div class="doc-title">${label}</div>`;
  if (inv.bir_invoice_number) html += `<div class="meta-row"><span class="label">BIR No:</span><span>${inv.bir_invoice_number}</span></div>`;
  html += `<div class="meta-row"><span class="label">Ref No:</span><span>${inv.invoice_number || ''}</span></div>`;
  html += `<div class="meta-row"><span class="label">Date:</span><span>${fmtDateMaybeTime(inv.bir_issued_at || inv.invoice_date || inv.created_at)}</span></div>`;
  if (inv.customer_name && inv.customer_name !== 'Walk-in') html += `<div class="meta-row"><span class="label">Customer:</span><span>${inv.customer_name}</span></div>`;
  if (inv.bir_customer_tin) html += `<div class="meta-row"><span class="label">Cust TIN:</span><span>${inv.bir_customer_tin}</span></div>`;
  html += `<div class="meta-row"><span class="label">Cashier:</span><span>${inv.cashier_name || ''}</span></div>`;
  html += '<div class="sep"></div>';
  html += buildItemsThermal(inv.items || []);
  html += '<div class="sep"></div>';
  html += '<div class="totals">';
  html += `<div class="row"><span>Subtotal</span><span>${formatPHP(inv.subtotal || inv.grand_total)}</span></div>`;
  if (parseFloat(inv.overall_discount) > 0) html += `<div class="row"><span>Discount</span><span>-${formatPHP(inv.overall_discount)}</span></div>`;
  html += `<div class="row grand"><span>TOTAL</span><span>${formatPHP(inv.grand_total)}</span></div>`;
  if (inv.bir_vat_type === 'vat_registered') {
    html += `<div class="row"><span>VATable</span><span>${formatPHP(inv.vatable_sales || 0)}</span></div>`;
    html += `<div class="row"><span>VAT ${parseFloat(inv.vat_rate) || 12}%</span><span>${formatPHP(inv.vat_amount || 0)}</span></div>`;
    if (parseFloat(inv.vat_exempt_sales) > 0) html += `<div class="row"><span>VAT-Exempt</span><span>${formatPHP(inv.vat_exempt_sales)}</span></div>`;
    if (parseFloat(inv.zero_rated_sales) > 0) html += `<div class="row"><span>Zero-Rated</span><span>${formatPHP(inv.zero_rated_sales)}</span></div>`;
  }
  html += '</div>';
  html += '<div class="payment-info">';
  html += `<div class="meta-row"><span class="label">Payment:</span><span>${inv.payment_method || 'Cash'}</span></div>`;
  const _t = parseFloat(inv.tendered_amount) || 0;
  const _ch = inv.change_amount != null ? parseFloat(inv.change_amount) || 0 : Math.max(0, _t - (parseFloat(inv.grand_total) || 0));
  if (_t > 0 && !isCharge) {
    html += `<div class="meta-row"><span class="label">Tendered:</span><span>${formatPHP(_t)}</span></div>`;
    if (_ch > 0) html += `<div class="meta-row"><span class="label">Change:</span><span>${formatPHP(_ch)}</span></div>`;
  }
  for (const ap of addonTenderPairs(inv)) {
    html += `<div class="meta-row"><span class="label">Add-on Tendered:</span><span>${formatPHP(ap.tendered)}</span></div>`;
    if (ap.change > 0) html += `<div class="meta-row"><span class="label">Add-on Change:</span><span>${formatPHP(ap.change)}</span></div>`;
  }
  const _bal = (parseFloat(inv.grand_total) || 0) - (parseFloat(inv.amount_paid) || 0);
  if (_bal > 0 && isCharge) html += `<div class="meta-row"><span class="label">Balance:</span><span>${formatPHP(_bal)}</span></div>`;
  html += '</div>';
  if (docCode) html += qrImgTag(docCode, 120);
  html += buildFooter(isCharge ? 'charge_invoice' : 'sales_invoice', biz, { format: 'thermal' });
  return html;
}

// ── Server-side print history (owner feature 2026-07-29) ────────────────────
// Fire-and-forget: every print of a document that has a server id is logged to
// POST /print-log/register so the invoice History tab can show a complete
// print trail (complements the QR/doc-code register-print counter). Uses a
// lazy import of the auth api to avoid a module cycle, and must NEVER block
// or fail a print.
function registerPrintEvent(type, data, format) {
  try {
    const docId = data?.id;
    if (!docId) return;
    import('../contexts/AuthContext').then(({ api }) => {
      api.post('/print-log/register', {
        doc_id: docId,
        doc_number: data.invoice_number || data.receipt_number || data.po_number || data.transfer_number || '',
        doc_type: type,
        print_type: type,
        format,
        source: (typeof window !== 'undefined' ? window.location.pathname : ''),
      }).catch(() => {});
    }).catch(() => {});
  } catch { /* never break printing */ }
}

const PrintEngine = {
  registerPrintEvent,
  /**
   * @param {object} opts
   * @param {'order_slip'|'packing_slip'|'trust_receipt'|'purchase_order'|'branch_transfer'|'stock_request'|'expense_voucher'|'return_slip'|'statement'} opts.type
   * @param {object} opts.data - Document data
   * @param {'thermal'|'full_page'|'dot_matrix'} opts.format
   * @param {object} opts.businessInfo - From /settings/business-info
   * @param {string} opts.docCode - Unique document QR code (optional)
   */

  /**
   * generateHtml — returns the receipt HTML string WITHOUT opening a window or printing.
   * Used by PrintBridge when running in Capacitor native mode (H10P APK).
   * The HTML is passed to the native H10PPrinterPlugin which renders it to Bitmap.
   */
  generateHtml({ type, data: rawData, format = 'thermal', businessInfo = {}, docCode = '',
                 copyNumber = 1, extras = null }) {
    // The operator's answer to the print prompt is applied ONCE, here: what
    // was switched off is gone from the data before any template sees it, so
    // no template can forget to honour it. No prompt (a reprint, a queued
    // job, an expense voucher) => everything prints.
    const data = applyPrintExtras(rawData, extras);
    // FULL-PAGE -> DOT-MATRIX, here too (2026-09-23). print() has swapped since
    // Jul 2026 but generateHtml() did not, so the SAME button produced
    // dot-matrix paper locally and a true full-page document when the job went
    // to a remote printer. One button must mean one paper. Safe for every type:
    // each one that has a full-page builder also has a dot-matrix builder.
    if (PREFER_DOT_MATRIX_OVER_FULL_PAGE && format === 'full_page') {
      format = 'dot_matrix';
    }
    const css = format === 'thermal' ? thermalCSS
              : format === 'dot_matrix' ? dotMatrixCSS
              : fullPageCSS;
    let body = '';
    switch (type) {
      case 'order_slip':
        body = format === 'thermal'     ? orderSlipThermal(data, businessInfo, docCode)
             : format === 'dot_matrix'  ? orderSlipDotMatrix(data, businessInfo, docCode)
             :                           orderSlipFullPage(data, businessInfo, docCode);
        break;
      case 'packing_slip':
        // Price-free trucker copy — no full_page variant; anything but
        // thermal renders the dot-matrix layout (full_page is deprecated
        // on the print() path anyway via PREFER_DOT_MATRIX_OVER_FULL_PAGE).
        body = format === 'thermal' ? packingSlipThermal(data, businessInfo)
             :                        packingSlipDotMatrix(data, businessInfo);
        break;
      case 'trust_receipt':
        body = format === 'thermal'     ? trustReceiptThermal(data, businessInfo, docCode)
             : format === 'dot_matrix'  ? trustReceiptDotMatrix(data, businessInfo, docCode)
             :                           trustReceiptFullPage(data, businessInfo, docCode);
        break;
      case 'purchase_order':
        body = format === 'thermal'    ? purchaseOrderThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? purchaseOrderDotMatrix(data, businessInfo, docCode)
             : purchaseOrderFullPage(data, businessInfo, docCode);
        break;
      case 'branch_transfer':
        body = format === 'thermal'    ? branchTransferThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? branchTransferDotMatrix(data, businessInfo, docCode)
             :                           branchTransferFullPage(data, businessInfo, docCode);
        break;
      case 'stock_request':
        // Price-free internal doc — thermal or dot-matrix only (like packing_slip).
        body = format === 'thermal' ? stockRequestThermal(data, businessInfo, docCode)
             :                        stockRequestDotMatrix(data, businessInfo, docCode);
        break;
      case 'settlement':
        body = settlementDotMatrix(data, businessInfo, docCode);
        break;
      case 'expense_voucher':
        body = format === 'thermal'    ? expenseVoucherThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? expenseVoucherDotMatrix(data, businessInfo, docCode)
             : expenseVoucherFullPage(data, businessInfo, docCode);
        break;
      case 'return_slip':
        body = format === 'thermal'    ? returnSlipThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? returnSlipDotMatrix(data, businessInfo, docCode)
             : returnSlipFullPage(data, businessInfo, docCode);
        break;
      case 'statement':
        body = format === 'dot_matrix' ? statementDotMatrix(data, businessInfo, docCode)
             : statementFullPage(data, businessInfo, docCode);
        break;
      case 'official_receipt':
        body = format === 'thermal'    ? salesInvoiceThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? salesInvoiceDotMatrix(data, businessInfo, docCode)
             :                           officialReceiptFullPage(data, businessInfo, docCode);
        break;
      case 'proof_of_payment':
        body = format === 'thermal'    ? proofOfPaymentThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? proofOfPaymentDotMatrix(data, businessInfo, docCode)
             :                           proofOfPaymentFullPage(data, businessInfo, docCode);
        break;
      case 'proof_of_payment_terminal':
        body = proofOfPaymentTerminal(data, businessInfo);
        break;
      case 'capital_change_report':
        body = capitalChangeReportDotMatrix(data, businessInfo);
        break;
      case 'capital_price_strip':
        body = capitalPriceStripThermal(data, businessInfo);
        break;
      case 'po_price_change_report':
        body = poPriceChangeReportDotMatrix(data, businessInfo);
        break;
      case 'smart_price_scan_report':
        body = smartPriceScanReportDotMatrix(data, businessInfo);
        break;
      default:
        body = format === 'thermal'    ? orderSlipThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? orderSlipDotMatrix(data, businessInfo, docCode)
             :                          orderSlipFullPage(data, businessInfo, docCode);
    }
    // No window.print() script — the native plugin handles printing
    // Viewport meta: forces CSS viewport = 384px so body width:100% fills the bitmap exactly
    const viewportMeta = format === 'thermal' ? THERMAL_VIEWPORT_META : '';
    const dmExtras = format === 'dot_matrix' ? dotMatrixStampCSS(formatPrintedAt()) : '';
    const reprintBanner = reprintBannerHTML(type, copyNumber, format);
    return `<!DOCTYPE html><html><head><meta charset="utf-8">${viewportMeta}<title>Receipt</title><style>${css}${dmExtras}</style></head><body>${reprintBanner}${body}</body></html>`;
  },

  print({ type, data: rawData, format = 'thermal', businessInfo = {}, docCode = '',
          copyNumber = 1, extras = null }) {
    const data = applyPrintExtras(rawData, extras);
    registerPrintEvent(type, data, format);
    // ── FULL-PAGE → DOT-MATRIX MIGRATION (owner decision, Jul 2026) ─────────
    // The full_page format accumulated too many issues; office/desk printing
    // standardizes on the dot-matrix layout instead. Scope: THIS browser
    // print() path only — generateHtml() (native H10P 576px bitmaps and
    // Send-to-Print jobs) is deliberately untouched. Flip to false to revert
    // every full-page button at once.
    if (PREFER_DOT_MATRIX_OVER_FULL_PAGE && format === 'full_page') {
      format = 'dot_matrix';
    }
    const css = format === 'thermal' ? thermalCSS
              : format === 'dot_matrix' ? dotMatrixCSS
              : fullPageCSS;
    let body = '';

    switch (type) {
      case 'order_slip':
        body = format === 'thermal'     ? orderSlipThermal(data, businessInfo, docCode)
             : format === 'dot_matrix'  ? orderSlipDotMatrix(data, businessInfo, docCode)
             :                           orderSlipFullPage(data, businessInfo, docCode);
        break;
      case 'packing_slip':
        // Price-free trucker copy — thermal or dot-matrix only.
        body = format === 'thermal' ? packingSlipThermal(data, businessInfo)
             :                        packingSlipDotMatrix(data, businessInfo);
        break;
      case 'trust_receipt':
        body = format === 'thermal'     ? trustReceiptThermal(data, businessInfo, docCode)
             : format === 'dot_matrix'  ? trustReceiptDotMatrix(data, businessInfo, docCode)
             :                           trustReceiptFullPage(data, businessInfo, docCode);
        break;
      case 'purchase_order':
        body = format === 'thermal'    ? purchaseOrderThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? purchaseOrderDotMatrix(data, businessInfo, docCode)
             : purchaseOrderFullPage(data, businessInfo, docCode);
        break;
      case 'branch_transfer':
        body = format === 'thermal'    ? branchTransferThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? branchTransferDotMatrix(data, businessInfo, docCode)
             :                           branchTransferFullPage(data, businessInfo, docCode);
        break;
      case 'stock_request':
        // Price-free internal doc — thermal or dot-matrix only (like packing_slip).
        body = format === 'thermal' ? stockRequestThermal(data, businessInfo, docCode)
             :                        stockRequestDotMatrix(data, businessInfo, docCode);
        break;
      case 'expense_voucher':
        body = format === 'thermal'    ? expenseVoucherThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? expenseVoucherDotMatrix(data, businessInfo, docCode)
             : expenseVoucherFullPage(data, businessInfo, docCode);
        break;
      case 'return_slip':
        body = format === 'thermal'    ? returnSlipThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? returnSlipDotMatrix(data, businessInfo, docCode)
             : returnSlipFullPage(data, businessInfo, docCode);
        break;
      case 'statement':
        body = format === 'dot_matrix' ? statementDotMatrix(data, businessInfo, docCode)
             : statementFullPage(data, businessInfo, docCode);
        break;
      case 'official_receipt':
        body = format === 'thermal'    ? salesInvoiceThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? salesInvoiceDotMatrix(data, businessInfo, docCode)
             :                           officialReceiptFullPage(data, businessInfo, docCode);
        break;
      case 'proof_of_payment':
        body = format === 'thermal'    ? proofOfPaymentThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? proofOfPaymentDotMatrix(data, businessInfo, docCode)
             :                           proofOfPaymentFullPage(data, businessInfo, docCode);
        break;
      case 'proof_of_payment_terminal':
        body = proofOfPaymentTerminal(data, businessInfo);
        break;
      case 'capital_change_report':
        body = capitalChangeReportDotMatrix(data, businessInfo);
        break;
      case 'capital_price_strip':
        body = capitalPriceStripThermal(data, businessInfo);
        break;
      case 'po_price_change_report':
        body = poPriceChangeReportDotMatrix(data, businessInfo);
        break;
      case 'smart_price_scan_report':
        body = smartPriceScanReportDotMatrix(data, businessInfo);
        break;
      default:
        body = format === 'thermal'    ? orderSlipThermal(data, businessInfo, docCode)
             : format === 'dot_matrix' ? orderSlipDotMatrix(data, businessInfo, docCode)
             :                          orderSlipFullPage(data, businessInfo, docCode);
    }

    const winWidth = format === 'thermal' ? 400 : 900;

    // Inject a script that waits for all images (QR code) to finish loading
    // before calling window.print() — eliminates the race with api.qrserver.com
    const printScript = `
<script>
(function() {
  var printed = false;
  function doPrint() { if (printed) return; printed = true; window.print(); }
  var imgs = Array.prototype.slice.call(document.images);
  if (!imgs.length) { doPrint(); return; }
  // Wait for every image to be fully DECODED (not merely "loaded") before
  // printing. For large logos / base64 data URLs, img.complete can be true
  // before the bitmap is decoded, so the print rasterizer would capture a
  // partial/garbled image. img.decode() resolves only once it is paintable.
  var promises = imgs.map(function(img) {
    if (img.decode) {
      return img.decode().catch(function() {});
    }
    return new Promise(function(resolve) {
      if (img.complete) { resolve(); }
      else { img.onload = resolve; img.onerror = resolve; }
    });
  });
  Promise.all(promises).then(function() { setTimeout(doPrint, 80); });
  // Safety fallback: print after 6 seconds regardless
  setTimeout(doPrint, 6000);
})();
</script>`;

    // Thermal viewport lock was MISSING on this browser-popup path (only generateHtml had
    // it) — /sales-new direct prints + branch-transfer 58mm prints rendered unlocked on
    // high-DPI screens and came out fuzzy. Same contract as generateHtml now.
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">${format === 'thermal' ? THERMAL_VIEWPORT_META : ''}<title>Print</title><style>${css}${format === 'dot_matrix' ? dotMatrixStampCSS(formatPrintedAt()) : ''}</style></head><body>${reprintBannerHTML(type, copyNumber, format)}${body}${printScript}</body></html>`;
    // ── DESKTOP THERMAL = BITMAP PARITY ──────────────────────────────────
    // The H10P prints "thick/crisp" because its NATIVE path captures the receipt
    // as a 384px bitmap and lays it down dot-for-dot. A desktop browser instead
    // prints the same HTML as VECTOR text, which a 58mm thermal driver
    // anti-aliases thinner/lighter (owner-confirmed 2026-07-15: "desktop looks
    // thinner"). To mirror the device on desktop, rasterize the IDENTICAL thermal
    // HTML to a bitmap and print THAT image — the printer then lays down chunky
    // thermal dots exactly like the terminal. Browser-only (the H10P itself uses
    // the SDK via PrintBridge and never reaches here); any failure falls back to
    // the proven vector popup below so a rasterizer hiccup never blocks a print.
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    if (format === 'thermal' && !isNative && desktopRasterEnabled()) {
      const innerHtml = `${reprintBannerHTML(type, copyNumber, format)}${body}`;
      this._printThermalBitmap({ innerHtml }).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[PrintEngine] thermal bitmap raster failed → vector fallback:', err);
        this._openPrintPopup(html, winWidth);
      });
      return;
    }

    this._openPrintPopup(html, winWidth);
  },

  /** Open the classic auto-printing popup (vector text). Shared by all non-thermal
   *  formats and used as the fallback for the thermal bitmap path. */
  _openPrintPopup(html, winWidth) {
    const win = window.open('', '_blank', `width=${winWidth},height=700`);
    if (!win) { alert('Please allow popups to print'); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
  },

  /**
   * Rasterize a thermal receipt and print the IMAGE, so desktop thermal output is
   * as thick/crisp as the H10P terminal (which prints a native bitmap). Laid out
   * at the configured paper width with fonts at true CSS size (see the desktop
   * raster settings block above). The receipt HTML is rendered in an off-screen,
   * CSS-isolated iframe (thermalCSS uses global body/* selectors — an iframe
   * keeps them off the app), captured via SVG foreignObject (the browser's own
   * renderer — see the capture block for why html2canvas was dropped), then printed as a single
   * <img> pinned to the paper width in mm. Throws on any failure so the caller
   * can fall back to vector.
   */
  async _printThermalBitmap({ innerHtml, fullHtml }) {
    const { stripUrls, paperMm } = await this._captureThermalStrips({ innerHtml, fullHtml });
    this._openThermalBitmapPopup(stripUrls, paperMm);
  },

  /**
   * Capture a thermal receipt as print-ready bitmap STRIPS (data URLs) + the
   * paper width they must print at. Extracted from _printThermalBitmap so the
   * Print Station's headless render surface prints the SAME bitmap the web
   * popup does — vector text on a thermal driver anti-aliases thin, which is
   * the whole reason this raster path exists (owner 2026-07-15, again on real
   * station paper 2026-08-11). Throws on any failure so callers fall back.
   */
  async _captureThermalStrips({ innerHtml, fullHtml }) {
    // GEOMETRY (owner model 2026-07-16): fonts FIXED, layout ADAPTIVE.
    // Lay the receipt out at the paper's real width in CSS px (96dpi:
    // 80mm → 302px) and print the bitmap at exactly that many mm — so
    // 1 CSS px = 1/96in on paper and the terminal's font hierarchy
    // (11px body ≈ 2.9mm) prints at TRUE size regardless of roll width,
    // while line width / wrapping / spacing adapt to the paper. This is
    // the old vector path's geometry with the bitmap path's thickness.
    const PAPER_MM = desktopPaperWidthMm();
    const WIDTH = Math.round((PAPER_MM / 25.4) * 96);  // CSS px at true scale
    const SCALE = 3;     // capture 3× (~288dpi at 80mm) — above the head's 203dpi

    // Localize remote QR images to data URLs BEFORE rasterizing. The browser path
    // normally lets the popup load api.qrserver.com over the network, but the
    // SVG-image capture loads NO external resources (and a cross-origin bitmap
    // would taint the canvas so toDataURL() would throw)
    // → every QR receipt would fall back to vector. Same fix PrintBridge applies for
    // the native path, done here with the bundled `qrcode` package.
    const safeFull = fullHtml ? await this._localizeQrUrls(fullHtml) : null;
    const safeInner = innerHtml ? await this._localizeQrUrls(innerHtml) : null;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, {
      position: 'fixed', left: '-10000px', top: '0',
      width: WIDTH + 'px', height: '10px', border: '0', background: '#fff',
    });
    document.body.appendChild(iframe);

    try {
      // Desktop-raster legibility override (owner 2026-07-16): italic's
      // diagonal strokes anti-alias into grays the desktop thermal driver
      // dithers away — "Less 10%" printed thin. Upright here ONLY; the
      // H10P native path keeps the approved italic.
      const rasterLegibilityCSS = '.items-table .item-discount{font-style:normal;}';
      const doc = iframe.contentDocument;
      doc.open();
      if (safeFull) {
        // Render a complete thermal document verbatim (e.g. statementThermal, which
        // ships its own thermalCSS + viewport), minus any embedded auto-print script
        // so window.print() doesn't fire inside this off-screen iframe.
        doc.write(applyDesktopFontDelta(
          String(safeFull)
            .replace(/<script[\s\S]*?<\/script>/gi, '')
            .replace('</head>', `<style>${rasterLegibilityCSS}</style></head>`)));
      } else {
        doc.write(applyDesktopFontDelta(
          `<!DOCTYPE html><html><head><meta charset="utf-8">${THERMAL_VIEWPORT_META}` +
          `<style>${thermalCSS} html,body{background:#fff !important;} ${rasterLegibilityCSS}</style></head>` +
          `<body>${safeInner}</body></html>`));
      }
      doc.close();

      // Wait for layout, fonts, and the QR image to be fully painted before capture.
      // POLL readyState — don't trust a 'load' event: writing into an about:blank
      // iframe does not reliably fire another load, so a doc still parsing (big
      // inlined QR data URL) would wait forever (station hang, 2026-08-11).
      await new Promise((res) => {
        if (doc.readyState === 'complete') return res();
        let done = false;
        const finish = () => { if (!done) { done = true; clearInterval(poll); res(); } };
        const poll = setInterval(() => { if (doc.readyState === 'complete') finish(); }, 30);
        iframe.addEventListener('load', finish, { once: true });
        setTimeout(finish, 5000);   // never block a print on this wait
      });
      try { if (doc.fonts && doc.fonts.ready) await doc.fonts.ready; } catch { /* no fonts API */ }
      const imgs = Array.from(doc.images || []);
      await Promise.all(imgs.map((img) => (img.decode
        ? img.decode().catch(() => {})
        : new Promise((r) => { if (img.complete) r(); else { img.onload = r; img.onerror = r; } }))));
      await new Promise((r) => setTimeout(r, 30)); // settle one frame

      const target = doc.body;
      const height = Math.max(1, Math.ceil(target.scrollHeight));

      // CAPTURE = the browser's own renderer via SVG <foreignObject>, NOT
      // html2canvas. html2canvas re-implements text drawing and places
      // glyphs LOWER than real layout, so every border following text cut
      // THROUGH it (owner 2026-07-16: dashed product separators pierced
      // "Line Total" / qty-price lines; the footer rule underlined "Scan to
      // view document"). foreignObject rasterizes with the real engine —
      // pixel-identical to what the vector popup shows.
      //
      // SVG-as-image loads NO external resources, so first inline every
      // non-data: <img> (QRs already are; e.g. signature images get fetched
      // to data URLs, and anything unfetchable is dropped rather than
      // silently blanking the whole capture).
      for (const img of Array.from(doc.images || [])) {
        const src = img.getAttribute('src') || '';
        if (!src || src.startsWith('data:')) continue;
        try {
          const resp = await fetch(src, { mode: 'cors' });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          const du = await new Promise((res, rej) => {
            const fr = new FileReader();
            fr.onload = () => res(fr.result);
            fr.onerror = () => rej(new Error('read failed'));
            fr.readAsDataURL(blob);
          });
          img.setAttribute('src', du);
        } catch {
          img.remove();
        }
      }

      // Serialize the DOM to well-formed XHTML (XMLSerializer closes voids,
      // escapes entities) and rasterize at SCALE via canvas. Chrome renders
      // the SVG vector-fresh at the destination resolution — crisp at 3×.
      doc.documentElement.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
      const xhtml = new XMLSerializer().serializeToString(doc.documentElement);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${height}">` +
        `<foreignObject width="100%" height="100%">${xhtml}</foreignObject></svg>`;
      const bmp = new Image();
      await new Promise((res, rej) => {
        bmp.onload = res;
        bmp.onerror = () => rej(new Error('SVG rasterization failed'));
        bmp.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      });
      // BLANK-ROW STRIP SLICING (owner decision 2026-07-18): one giant image
      // hits two walls — browsers cap canvas height (~16k device px ≈ 1.4m of
      // paper at 3×, beyond which capture fails → silent thin-vector fallback)
      // and page-limited drivers cut the image at arbitrary pixel rows (the
      // white line through a QR). So long receipts are split into strips cut
      // ONLY at blank rows (scanned near each target height), each printed
      // break-inside:avoid — driver page breaks can then land only in
      // whitespace. Unlike the H10P's fixed-1024px cuts (a device-memory
      // workaround), cuts here are invisible on paper. Short receipts stay a
      // single image exactly as before. Strip target tunable per device:
      // localStorage agribooks_desktop_strip_css (CSS px, 400-3000, def 1000).
      const stripTargetCss = (() => {
        try {
          const v = parseInt(localStorage.getItem('agribooks_desktop_strip_css'), 10);
          if (Number.isFinite(v)) return Math.max(400, Math.min(3000, v));
        } catch { /* ignore */ }
        return 1000;
      })();
      const cuts = this._computeStripCuts(bmp, WIDTH, height, stripTargetCss);
      const strips = cuts.slice(0, -1).map((y0, i) =>
        this._renderStrip(bmp, WIDTH, SCALE, y0, cuts[i + 1]));

      // Blank-guard: an empty capture means something went wrong — fall back.
      if (!strips.some((c) => this._canvasHasInk(c))) throw new Error('rasterized receipt was blank');

      return { stripUrls: strips.map((c) => c.toDataURL('image/png')), paperMm: PAPER_MM };
    } finally {
      // Remove the off-screen iframe after the capture is done reading from it.
      setTimeout(() => { try { iframe.remove(); } catch { /* already gone */ } }, 100);
    }
  },

  /** Review-first popup for the WEB path: shows the captured bitmap, cashier
   *  presses Print. (The Print Station prints the same strips headlessly.) */
  _openThermalBitmapPopup(stripUrls, PAPER_MM) {
      // Print at exactly PAPER_MM so 1 CSS px = 1/96in (true font scale).
      // No @page size declared — the driver's roll rules the page box; the
      // max-width guard only bites if the real paper is narrower than the
      // configured width (then everything shrinks proportionally but prints).
      const imgWidthCss = `${PAPER_MM}mm`;
      const win = window.open('', '_blank', 'width=420,height=700');
      if (!win) { alert('Please allow popups to print'); return; }
      // REVIEW-FIRST POPUP (owner 2026-07-16): no auto-print. The cashier
      // checks the items on screen, then clicks Print — and the window stays
      // open afterwards so it can be printed again. On screen the receipt
      // shows large and smooth (no pixelated hint — that only made the
      // PREVIEW look blurry; paper always gets the full-resolution bitmap);
      // in print media only the receipt prints, pinned to the paper width.
      // DECODE GUARD (owner discovery 2026-07-18): on a slow PC, clicking
      // Print before the big PNG finishes DECODING makes Chrome snapshot a
      // partially-decoded image → thin/uneven paper output (a fast machine
      // never shows it; waiting ~10s before printing fixed it on the slow
      // one). The Print button stays disabled ("Preparing…") until
      // img.decode() resolves, so the race can't happen on any machine.
      win.document.write(
        `<!DOCTYPE html><html><head><meta charset="utf-8">${THERMAL_VIEWPORT_META}<title>Receipt</title>` +
        `<style>@page{margin:0;} *{margin:0;padding:0;box-sizing:border-box;} ` +
        `html,body{width:100%;background:#e2e8f0;} ` +
        `.bar{position:sticky;top:0;z-index:2;display:flex;align-items:center;gap:10px;` +
        `padding:10px 12px;background:#0f172a;color:#cbd5e1;font:13px system-ui,sans-serif;}` +
        `.bar button{font:bold 14px system-ui,sans-serif;padding:8px 22px;border:0;border-radius:6px;cursor:pointer;}` +
        `.bar .go{background:#16a34a;color:#fff;} .bar .go:disabled{background:#475569;color:#cbd5e1;cursor:wait;}` +
        `.bar .x{background:#334155;color:#e2e8f0;}` +
        `.paper{max-width:430px;margin:10px auto 24px;box-shadow:0 1px 8px rgba(0,0,0,.25);}` +
        `img{display:block;width:100%;background:#fff;}` +
        `@media print{.bar{display:none !important;} html,body{background:#fff;}` +
        `.paper{width:${imgWidthCss};max-width:100%;margin:0 auto;box-shadow:none;}` +
        `img{break-inside:avoid;page-break-inside:avoid;}}</style>` +
        `</head><body>` +
        `<div class="bar"><button class="go" id="pbtn" disabled onclick="window.print()">Preparing&hellip;</button>` +
        `<button class="x" onclick="window.close()">Close</button>` +
        `<span id="pmsg">Loading the receipt&hellip;</span></div>` +
        `<div class="paper">` +
        stripUrls.map((u) => `<img src="${u}" alt="receipt"/>`).join('') +
        `</div>` +
        `<script>(function(){var b=document.getElementById('pbtn'),m=document.getElementById('pmsg');` +
        `var imgs=[].slice.call(document.images);` +
        `function ready(){b.disabled=false;b.textContent='Print';m.textContent='Check the receipt, then press Print.';}` +
        `Promise.all(imgs.map(function(i){return i.decode?i.decode().catch(function(){}):Promise.resolve();}))` +
        `.then(function(){setTimeout(ready,200);});` +
        `setTimeout(ready,4000);})();<\/script>` +
        `</body></html>`
      );
      win.document.close();
      win.focus();
  },

  /**
   * Print a COMPLETE thermal HTML document (its own <style>/viewport) as a bitmap,
   * for 58mm surfaces that assemble their own doc instead of going through print()
   * — e.g. customer-account statements via PrintBridge.printStatement. Same
   * device-parity rationale as the thermal branch in print(); falls back to the
   * plain auto-printing popup (the document's embedded print script) on any failure.
   */
  async printThermalDocument(fullHtml) {
    if (desktopRasterEnabled()) {
      try {
        await this._printThermalBitmap({ fullHtml });
        return;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[PrintEngine] statement bitmap raster failed → vector fallback:', err);
      }
    }
    // Raster disabled (agribooks_desktop_raster='off') or failed → the
    // pre-2026-07-15 vector popup; the document self-prints via its own script.
    const win = window.open('', '_blank', 'width=420,height=700');
    if (!win) { alert('Please allow popups to print'); return; }
    win.document.write(fullHtml);
    win.document.close();
    win.focus();
  },

  /**
   * Replace every api.qrserver.com QR image URL in an HTML string with a locally
   * generated data URL (bundled `qrcode` package). Required before rasterizing:
   * the SVG-image capture loads no external resources, so a remote QR would
   * simply vanish from the bitmap. Mirrors PrintBridge's native-path
   * QR localization. Best-effort: any URL that can't be converted is left as-is.
   */
  async _localizeQrUrls(html) {
    const str = String(html);
    const urls = [...new Set(
      [...str.matchAll(/https:\/\/api\.qrserver\.com\/v1\/create-qr-code\/\?[^"'\s>]+/g)].map((m) => m[0])
    )];
    if (!urls.length) return html;
    let QRCode;
    try { QRCode = (await import('qrcode')).default; } catch { return html; }
    let out = str;
    for (const u of urls) {
      let params;
      try { params = new URL(u).searchParams; } catch { continue; }
      const data = params.get('data');
      if (!data) continue;
      const sizePx = parseInt((params.get('size') || '152').split('x')[0], 10) || 152;
      const marginRaw = parseInt(params.get('margin') || '2', 10);
      try {
        // Generate at 3× the display size: the <img> tags carry explicit
        // width/height attributes so layout is unchanged, but the raster
        // capture (SCALE 3) samples the source 1:1 instead of upscaling a
        // 120px PNG — which printed the QR soft/blurry (owner 2026-07-16).
        const dataUrl = await QRCode.toDataURL(data, {
          width: Math.max(120, sizePx) * 3,
          margin: Number.isNaN(marginRaw) ? 2 : marginRaw,
          errorCorrectionLevel: 'M',
          color: { dark: '#000000', light: '#ffffff' },
        });
        out = out.split(u).join(dataUrl);
      } catch { /* leave this URL as-is — it alone may force a vector fallback */ }
    }
    return out;
  },

  /** True if a rendered canvas contains any non-white pixels (sampled band).
   *  Used to detect a blank/failed raster so print() can fall back to vector. */
  /**
   * Strip cut positions [0, …, heightCss]. Short receipts return a single
   * span (byte-identical output to the pre-slicing path). Longer receipts
   * get cuts at BLANK pixel rows nearest each target height so the seams —
   * and any driver page break — land in whitespace between line items,
   * never through text or a QR.
   */
  _computeStripCuts(bmp, widthCss, heightCss, targetCss) {
    const LIMIT = Math.round(targetCss * 1.2);
    if (heightCss <= LIMIT) return [0, heightCss];
    const BAND = 220;    // search ± this many rows around each proposed cut
    const MIN_RUN = 4;   // a safe cut needs ≥ this many consecutive blank rows
    const cuts = [0];
    let y = 0;
    while (heightCss - y > LIMIT) {
      const proposed = y + targetCss;
      let cut = this._findBlankRow(bmp, widthCss, heightCss, proposed, BAND, MIN_RUN);
      if (!cut || cut <= y + 200) cut = proposed;  // no blank row found — cut anyway
      cuts.push(cut);
      y = cut;
    }
    cuts.push(heightCss);
    return cuts;
  },

  /** Middle of the ≥minRun-row blank band nearest `proposed`, or null. Scans a
   *  small 1× window of the SVG bitmap — cheap even on slow machines. */
  _findBlankRow(bmp, widthCss, heightCss, proposed, band, minRun) {
    const y0 = Math.max(0, proposed - band);
    const y1 = Math.min(heightCss, proposed + band);
    const h = y1 - y0;
    if (h <= 0) return null;
    const c = document.createElement('canvas');
    c.width = widthCss; c.height = h;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.fillStyle = '#ffffff';
    x.fillRect(0, 0, widthCss, h);
    x.drawImage(bmp, 0, -y0);
    let px;
    try { px = x.getImageData(0, 0, widthCss, h).data; } catch { return null; }
    const rowBlank = new Array(h);
    for (let r = 0; r < h; r++) {
      let blank = true;
      const base = r * widthCss * 4;
      for (let i = 0; i < widthCss; i++) {
        const o = base + i * 4;
        if (px[o] < 250 || px[o + 1] < 250 || px[o + 2] < 250) { blank = false; break; }
      }
      rowBlank[r] = blank;
    }
    let best = null, bestDist = Infinity, runStart = -1;
    for (let r = 0; r <= h; r++) {
      const b = r < h && rowBlank[r];
      if (b && runStart < 0) runStart = r;
      if (!b && runStart >= 0) {
        if (r - runStart >= minRun) {
          const mid = y0 + runStart + Math.floor((r - runStart) / 2);
          const d = Math.abs(mid - proposed);
          if (d < bestDist) { bestDist = d; best = mid; }
        }
        runStart = -1;
      }
    }
    return best;
  },

  /** Rasterize one strip of the SVG bitmap at SCALE (only this strip's canvas
   *  is ever materialized — the full receipt never needs one giant canvas). */
  _renderStrip(bmp, widthCss, scale, yTop, yBottom) {
    const c = document.createElement('canvas');
    c.width = widthCss * scale;
    c.height = Math.max(1, yBottom - yTop) * scale;
    const x = c.getContext('2d');
    x.fillStyle = '#ffffff';
    x.fillRect(0, 0, c.width, c.height);
    x.scale(scale, scale);
    x.drawImage(bmp, 0, -yTop);
    return c;
  },

  _canvasHasInk(canvas) {
    try {
      const ctx = canvas.getContext('2d');
      const w = canvas.width;
      const y0 = Math.floor(canvas.height / 3);
      const rows = Math.max(1, Math.floor(canvas.height / 3));
      const { data } = ctx.getImageData(0, y0, w, rows);
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] < 240 || data[i + 1] < 240 || data[i + 2] < 240) return true;
      }
      return false;
    } catch {
      return true; // can't inspect (e.g. tainted) → assume ok, don't block printing
    }
  },

  // Helper to determine doc type from invoice data
  getDocType(invoice) {
    if (!invoice) return 'order_slip';
    // An officially-issued sale (Direct / Auto-Print BIR mode) prints the BIR
    // Sales Invoice / Charge Invoice. Internal mode and the Staged / Smart-Role
    // workflows have no BIR number yet → Order Slip (cash) / Trust Agreement
    // (credit). Identical logic for /terminal and /sales-new — only the print
    // FORMAT differs (thermal vs full-page/dot-matrix).
    if (invoice.bir_invoice_number) return 'official_receipt';
    const pt = invoice.payment_type || '';
    const balance = parseFloat(invoice.balance) || 0;
    if (pt === 'credit' || pt === 'partial' || (balance > 0 && invoice.status !== 'paid')) {
      return 'trust_receipt';
    }
    return 'order_slip';
  },
};

export default PrintEngine;
